// INDEX_SPLIT_B2_v1 (07.09.2026) -- Mitarbeiter, Schichten, Zeiterfassung: BADGE_CONFIG, BadgeSVG, EmployeeBadges, AvatarUploadField, SickDayNotifyModal, SickApptRow, ShiftTemplateModal, ShiftOverrideModal, EmployeeAvatar, EmployeesPage, ShiftsPage, TimeCorrectionModal, TimeTrackingPage (Schicht-/Krank-Modals hier statt calendar, sonst Zyklus calendar<->employees)
// 1:1 aus app.jsx verschoben (Reihenfolge unverändert), nur `export` ergänzt und Imports erzeugt (scripts/split_frontend.mjs).
import { Award, Check, CheckCircle, ChevronLeft, ChevronRight, CreditCard, DollarSign, Download, Edit3, Euro, Phone, Plus, Scissors, Shield, Star, Trash2, TrendingUp, UserCheck, Users, X, Zap, useEffect, useState } from "../core/vendor.js";
import { api } from "../core/api.js";
import { COLORS, formatDate, formatDateShort, getISOWeek, isKosmetikSalon, shiftAppliesOn, today, weekParityOf } from "../core/config.jsx";
import { Badge, Btn, Card, Input, Modal, Select, StatCard } from "../core/ui.jsx";
import { Header } from "../layout/layout.jsx";
import { OwnerPinPrompt, ownerPinErrorText } from "../core/ownerpin.jsx"; // OWNER_PIN_v1
import { TierPricingTab } from "../services/services.jsx";


// ============================================================
// BADGE SVG COMPONENTS — US National Park Style
// ============================================================
export const BADGE_CONFIG = {
  revenue: { label: "Umsatz-König", color: "#B45309", bg: "#FEF3C7", icon: "€", desc: "Höchster Umsatz" },
  hours: { label: "Stunden-Champion", color: "#1D4ED8", bg: "#DBEAFE", icon: "⏱", desc: "Meiste Stunden" },
  beards: { label: "Bart-Meister", color: "#065F46", bg: "#D1FAE5", icon: "✂", desc: "Meiste Bärte" },
  color: { label: "Farb-Künstler", color: "#7C3AED", bg: "#EDE9FE", icon: "🎨", desc: "Meiste Farbe" },
};

export const BadgeSVG = ({ type, size = 32, showTooltip = true }) => {
  const conf = BADGE_CONFIG[type];
  if (!conf) return null;

  const colors = {
    revenue: { outer: "#92400E", inner: "#F59E0B", peak: "#FBBF24", tree: "#B45309", text: "#78350F" },
    hours:   { outer: "#1E3A5F", inner: "#3B82F6", peak: "#93C5FD", tree: "#1E40AF", text: "#1E3A5F" },
    beards:  { outer: "#064E3B", inner: "#10B981", peak: "#6EE7B7", tree: "#047857", text: "#064E3B" },
    color:   { outer: "#4C1D95", inner: "#8B5CF6", peak: "#C4B5FD", tree: "#6D28D9", text: "#4C1D95" },
  }[type];

  const iconPaths = {
    revenue: React.createElement("g", { transform: "translate(14,11) scale(0.45)" },
      React.createElement("text", { x: 0, y: 14, fontSize: 20, fontWeight: 900, fill: "white", textAnchor: "middle", fontFamily: "serif" }, "€")
    ),
    hours: React.createElement("g", { transform: "translate(14,14) scale(0.32)" },
      React.createElement("circle", { cx: 0, cy: 0, r: 11, fill: "none", stroke: "white", strokeWidth: 2.5 }),
      React.createElement("line", { x1: 0, y1: 0, x2: 0, y2: -7, stroke: "white", strokeWidth: 2.2, strokeLinecap: "round" }),
      React.createElement("line", { x1: 0, y1: 0, x2: 5, y2: 2, stroke: "white", strokeWidth: 2.2, strokeLinecap: "round" })
    ),
    beards: React.createElement("g", { transform: "translate(14,14) scale(0.38)" },
      React.createElement("line", { x1: -6, y1: -8, x2: 6, y2: 8, stroke: "white", strokeWidth: 2.5, strokeLinecap: "round" }),
      React.createElement("line", { x1: 6, y1: -8, x2: -6, y2: 8, stroke: "white", strokeWidth: 2.5, strokeLinecap: "round" }),
      React.createElement("circle", { cx: -6, cy: -8, r: 3, fill: "none", stroke: "white", strokeWidth: 2 }),
      React.createElement("circle", { cx: 6, cy: -8, r: 3, fill: "none", stroke: "white", strokeWidth: 2 })
    ),
    color: React.createElement("g", { transform: "translate(14,13) scale(0.35)" },
      React.createElement("ellipse", { cx: 0, cy: 0, rx: 10, ry: 7, fill: "none", stroke: "white", strokeWidth: 2.2 }),
      React.createElement("circle", { cx: -4, cy: -2, r: 2, fill: "#FBBF24" }),
      React.createElement("circle", { cx: 2, cy: -3, r: 2, fill: "#EF4444" }),
      React.createElement("circle", { cx: 5, cy: 1, r: 2, fill: "#3B82F6" }),
      React.createElement("circle", { cx: -1, cy: 2, r: 2, fill: "#10B981" })
    ),
  };

  return React.createElement("div", {
    style: { position: "relative", display: "inline-flex", cursor: "pointer" },
    title: showTooltip ? `${conf.label} — ${conf.desc}` : undefined,
  },
    React.createElement("svg", { width: size, height: size, viewBox: "0 0 28 28" },
      // Shield/arrowhead shape (National Park style)
      React.createElement("path", {
        d: "M14 1 L25 6 L25 16 L14 27 L3 16 L3 6 Z",
        fill: colors.outer, stroke: colors.outer, strokeWidth: 0.5
      }),
      React.createElement("path", {
        d: "M14 3 L23 7.5 L23 15.5 L14 25 L5 15.5 L5 7.5 Z",
        fill: colors.inner
      }),
      // Mountain silhouette
      React.createElement("path", {
        d: "M5 18 L9 12 L12 15 L14 10 L18 14 L23 18 Z",
        fill: colors.outer, opacity: 0.3
      }),
      // Trees
      React.createElement("path", { d: "M7 18 L8.5 14 L10 18 Z", fill: colors.tree, opacity: 0.4 }),
      React.createElement("path", { d: "M19 18 L20.5 14.5 L22 18 Z", fill: colors.tree, opacity: 0.4 }),
      // Icon
      iconPaths[type],
      // Stars at top
      React.createElement("circle", { cx: 10, cy: 5.5, r: 0.8, fill: colors.peak }),
      React.createElement("circle", { cx: 14, cy: 4.5, r: 1, fill: "white" }),
      React.createElement("circle", { cx: 18, cy: 5.5, r: 0.8, fill: colors.peak }),
    )
  );
};

// Badge row: show all badges for an employee
export const EmployeeBadges = ({ employeeId, badgeAwards = [], size = 26 }) => {
  const empBadges = badgeAwards.filter(b => b.employee_id === employeeId);
  if (empBadges.length === 0) return null;
  return React.createElement("div", { style: { display: "inline-flex", gap: 3, marginLeft: 6, verticalAlign: "middle" } },
    empBadges.map(b => React.createElement(BadgeSVG, { key: b.badge_type, type: b.badge_type, size }))
  );
};

// EMPLOYEE_AVATAR_UPLOAD_v1 -- Drag&Drop + File-Picker fuer Profilbild
export const AvatarUploadField = ({ employeeId, currentUrl, onUploaded, fallbackInitials, fallbackColor }) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = React.useRef(null);

  const handleFile = async (file) => {
    setError("");
    if (!file) return;
    if (!employeeId) {
      setError("Bitte speichere den Mitarbeiter zuerst, dann kannst du ein Bild hochladen.");
      return;
    }
    if (!file.type.startsWith("image/") && !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)) {
      setError("Nur Bilder erlaubt (JPEG, PNG, WebP, HEIC).");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("Datei zu groß (max 10 MB).");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file, file.name);
      const res = await fetch(`/api/employees/${employeeId}/avatar`, {
        method: "POST",
        headers: { "Authorization": "Bearer " + sessionStorage.getItem("sp_token") },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.avatar_image_url) {
        onUploaded(data.avatar_image_url);
      } else {
        setError(data.error || `Upload fehlgeschlagen (${res.status})`);
      }
    } catch (e) {
      setError("Fehler: " + (e.message || "unbekannt"));
    }
    setUploading(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 6, display: "block" }}>
        Profil-Bild <span style={{ fontWeight: 400, textTransform: "none" }}>(optional, für Online-Buchung — JPEG, PNG, HEIC, max 10 MB)</span>
      </label>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current && fileRef.current.click()}
        style={{
          display: "flex", alignItems: "center", gap: 14, padding: 12, borderRadius: 10,
          border: `2px dashed ${dragging ? COLORS.primary : COLORS.border}`,
          background: dragging ? `${COLORS.primary}10` : "var(--cardHover)",
          cursor: "pointer", transition: "all 0.15s",
        }}>
        {currentUrl
          ? <img src={currentUrl} alt="" style={{ width: 56, height: 56, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: `2px solid ${COLORS.border}` }} onError={e => { e.target.onerror = null; e.target.style.display = "none"; }} />
          : <div style={{ width: 56, height: 56, borderRadius: "50%", background: fallbackColor || COLORS.primary, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, flexShrink: 0 }}>{fallbackInitials}</div>
        }
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
            {uploading ? "Lädt hoch…" : currentUrl ? "Bild ersetzen" : "Bild hierher ziehen oder klicken"}
          </div>
          <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 2 }}>
            Wird automatisch quadratisch zugeschnitten und auf 512 px verkleinert.
          </div>
          {error && <div style={{ fontSize: 11, color: COLORS.danger, marginTop: 4 }}>{error}</div>}
        </div>
        {currentUrl && (
          <button onClick={e => { e.stopPropagation(); onUploaded(""); }}
            title="Bild entfernen"
            style={{ background: "transparent", border: "none", cursor: "pointer", color: COLORS.textLight, fontSize: 18, padding: 4 }}>×</button>
        )}
        <input ref={fileRef} type="file" accept="image/*,.heic,.heif" style={{ display: "none" }}
          onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) handleFile(f); e.target.value = ""; }} />
      </div>
    </div>
  );
};

// SICK_DAY_NOTIFY_v1 (5.3) -- Krankmelde-Verwaltung: betroffene Termine durchgehen, verschieben/absagen, SMS+Mail
export const SickDayNotifyModal = ({ employeeId, employeeName, date, employees, mode, onClose, onChanged }) => {
  // mode: "manager" (owner/receptionist) | "self" (employee, eigene Termine)
  const [appts, setAppts] = useState(null);
  const [selected, setSelected] = useState({}); // { aptId: true } fuer Bulk-Absage
  const [busyMs, setBusyMs] = useState({}); // status meldungen pro apt
  const [bulkRunning, setBulkRunning] = useState(false);

  const load = async () => {
    const r = await api.request(`/appointments?employee_id=${employeeId}&date=${date}`);
    let list = (r && r.ok) ? r.data : [];
    // Falls Endpoint kein Filter unterstuetzt: clientseitig
    // SICK_DAY_DEDUP_v1 -- nur "echte" Termine zeigen, keine Phase-Children, keine Assistant-Blocks, keine Pausen.
    // Phase-Master und Standalone-Termine zählen. Vorher: 1 Strähnen-Termin = 3 Zeilen (Master + Children).
    list = (Array.isArray(list) ? list : []).filter(a => {
      if (String(a.employee_id ?? a.employeeId) !== String(employeeId)) return false;
      const aDate = a.date || "";
      if (!(aDate === date || aDate === date + "T00:00:00")) return false;
      if (a.status === "cancelled") return false;
      // Phase-Children rausfiltern (haben phase_parent_id)
      if (a.phase_parent_id || a.phaseParentId) return false;
      // Assistant-Blocks rausfiltern (source='assistant')
      if (a.source === "assistant") return false;
      // Pausen rausfiltern
      if (a.source === "blocker") return false;
      return true;
    });
    setAppts(list);
  };
  useEffect(() => { load(); }, [employeeId, date]);

  const setMsg = (id, m) => setBusyMs(s => ({ ...s, [id]: m }));

  const cancelOne = async (apt) => {
    setMsg(apt.id, "Storniere…");
    const upd = await api.updateAppointment(apt.id, {
      status: "cancelled",
      cancellation_reason: "salon_verspaetung",
      cancellation_note: `Mitarbeiter ${employeeName} krank`,
    });
    if (!upd || !upd.ok) { setMsg(apt.id, "Fehler"); return false; }
    const nf = await api.request(`/appointments/${apt.id}/notify-customer`, {
      method: "POST",
      body: JSON.stringify({ action: "cancel" }),
    });
    const data = nf && nf.data || {};
    setMsg(apt.id, data.sms_sent ? "✓ SMS gesendet" : data.email_sent ? "✓ E-Mail gesendet" : data.no_contact ? "✓ storniert (kein Kontakt)" : "✓ storniert");
    return true;
  };

  const moveTo = async (apt, { newEmpId, newDate, newTime }) => {
    setMsg(apt.id, "Verschiebe…");
    const payload = {};
    if (newEmpId) payload.employee_id = Number(newEmpId);
    if (newDate) payload.date = newDate;
    if (newTime) payload.start_time = newTime;
    const upd = await api.updateAppointment(apt.id, payload);
    if (!upd || !upd.ok) {
      if (upd && upd.status === 409) setMsg(apt.id, "⚠ Konflikt — Slot belegt");
      else setMsg(apt.id, "Fehler");
      return false;
    }
    const nf = await api.request(`/appointments/${apt.id}/notify-customer`, {
      method: "POST",
      body: JSON.stringify({
        action: "moved",
        new_date: newDate || apt.date,
        new_time: newTime || (apt.start_time || apt.startTime),
        new_employee_id: newEmpId ? Number(newEmpId) : null,
      }),
    });
    const data = nf && nf.data || {};
    setMsg(apt.id, data.sms_sent ? "✓ verschoben + SMS" : data.email_sent ? "✓ verschoben + E-Mail" : "✓ verschoben (kein Kontakt)");
    return true;
  };

  const bulkCancel = async () => {
    const ids = Object.keys(selected).filter(k => selected[k]).map(Number);
    if (ids.length === 0) { alert("Keine Termine ausgewählt."); return; }
    if (!confirm(`${ids.length} Termin(e) absagen + Kunden per SMS/Mail informieren?`)) return;
    setBulkRunning(true);
    for (const id of ids) {
      const apt = appts.find(a => a.id === id);
      if (apt) await cancelOne(apt);
    }
    setBulkRunning(false);
    await load();
    onChanged && onChanged();
  };

  const visibleAppts = appts || [];
  const _otherEmps = (employees || []).filter(e => Number(e.id) !== Number(employeeId));

  return (
    <Modal open={true} onClose={onClose} title={`Krankmeldung — ${employeeName} am ${date}`} width={720}>
      <div style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 12 }}>
        {mode === "self"
          ? <>Du bist krank gemeldet. Hier sind <strong>deine Termine</strong>. Du kannst <strong>im eigenen Kalender</strong> verschieben oder absagen — Vertretung muss <strong>Empfang/Inhaber</strong> machen.</>
          : <>Empfang/Inhaber-Sicht: pro Termin verschieben (anderer Mitarbeiter, neues Datum/Zeit) oder absagen. SMS+Mail werden automatisch versendet.</>}
      </div>
      {!appts && <div style={{ padding: 20, textAlign: "center" }}>Lade Termine…</div>}
      {appts && appts.length === 0 && <div style={{ padding: 20, textAlign: "center", color: COLORS.textLight }}>Keine offenen Termine an diesem Tag — alles gut!</div>}
      {visibleAppts.map(apt => (
        <SickApptRow key={apt.id}
          apt={apt}
          mode={mode}
          otherEmployees={_otherEmps}
          ownerCanReassign={mode === "manager"}
          msg={busyMs[apt.id]}
          checked={!!selected[apt.id]}
          onCheck={(v) => setSelected(s => ({ ...s, [apt.id]: v }))}
          onCancel={() => cancelOne(apt).then(() => onChanged && onChanged())}
          onMove={(opts) => moveTo(apt, opts).then(() => onChanged && onChanged())}
        />
      ))}
      {appts && appts.length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 12, color: COLORS.textLight }}>
            {Object.values(selected).filter(Boolean).length} / {appts.length} ausgewählt
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn size="sm" variant="secondary" onClick={onClose}>Schließen</Btn>
            <Btn size="sm" variant="danger" onClick={bulkCancel} disabled={bulkRunning}>
              {bulkRunning ? "Sage ab…" : "Ausgewählte absagen + benachrichtigen"}
            </Btn>
          </div>
        </div>
      )}
    </Modal>
  );
};

export const SickApptRow = ({ apt, mode, otherEmployees, ownerCanReassign, msg, checked, onCheck, onCancel, onMove }) => {
  const [newDate, setNewDate] = useState(apt.date || "");
  const [newTime, setNewTime] = useState(apt.start_time || apt.startTime || "");
  const [newEmpId, setNewEmpId] = useState("");
  const isModified = (newDate !== (apt.date || "")) || (newTime !== (apt.start_time || apt.startTime || "")) || newEmpId;
  const custName = apt.customer_name || apt.customerName || `Kunde #${apt.customer_id || apt.customerId}`;
  const svcs = apt.services_text || apt.services || apt.services_names || "";
  return (
    <div style={{ padding: 10, marginBottom: 8, border: `1px solid ${COLORS.border}`, borderRadius: 8, background: "var(--cardHover)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <input type="checkbox" checked={checked} onChange={e => onCheck(e.target.checked)} style={{ accentColor: COLORS.danger }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>{custName} · {apt.start_time || apt.startTime} · {apt.total_duration || apt.totalDuration} Min</div>
          {svcs && <div style={{ fontSize: 11, color: COLORS.textLight }}>{Array.isArray(svcs) ? svcs.join(", ") : svcs}</div>}
        </div>
        {msg && <div style={{ fontSize: 11, color: msg.includes("⚠") ? COLORS.danger : msg.includes("✓") ? COLORS.success : COLORS.textLight }}>{msg}</div>}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
          style={{ padding: "6px 8px", border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 12 }} />
        <input type="time" value={newTime} onChange={e => setNewTime(e.target.value)}
          style={{ padding: "6px 8px", border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 12 }} />
        {ownerCanReassign && (
          <select value={newEmpId} onChange={e => setNewEmpId(e.target.value)}
            style={{ padding: "6px 8px", border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 12, background: "var(--card)", color: COLORS.text }}>
            <option value="">— gleicher Mitarbeiter —</option>
            {otherEmployees.map(e => <option key={e.id} value={e.id}>auf {e.name} verschieben</option>)}
          </select>
        )}
        <Btn size="sm" disabled={!isModified} onClick={() => onMove({ newDate, newTime, newEmpId })}>Verschieben</Btn>
        <Btn size="sm" variant="danger" onClick={() => { if (confirm("Termin absagen + Kunde informieren?")) onCancel(); }}>Absagen</Btn>
      </div>
    </div>
  );
};

// SHIFT_TEMPLATE_v1 (3.2) -- Wochen-Vorlage auf mehrere Tage anwenden
export const ShiftTemplateModal = ({ employees, onClose, onSaved }) => {
  const [empId, setEmpId] = useState(employees && employees[0] ? employees[0].id : "");
  const [days, setDays] = useState({ mo: true, di: true, mi: true, do: true, fr: true, sa: false, so: false });
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("18:00");
  const [breakStart, setBreakStart] = useState("12:00");
  const [breakEnd, setBreakEnd] = useState("13:00");
  const [saving, setSaving] = useState(false);
  const dayLabels = { mo: "Mo", di: "Di", mi: "Mi", do: "Do", fr: "Fr", sa: "Sa", so: "So" };
  const apply = async () => {
    if (!empId) return;
    setSaving(true);
    let ok = 0, fail = 0;
    for (const d of Object.keys(days)) {
      if (!days[d]) continue;
      const payload = {
        employee_id: Number(empId), day_of_week: d,
        start_time: startTime, end_time: endTime, is_off: 0,
        break_start: breakStart || null, break_end: breakEnd || null,
      };
      try {
        // Existierende Schicht? -> update; sonst create
        // BIWEEKLY_SHIFT_v1 -- Vorlage schreibt nur Wochen-Zeilen (week_parity NULL), 14-Tage-Zeilen bleiben unangetastet
        const ex = await api.request(`/shifts`).then(r => (r && r.ok && Array.isArray(r.data)) ? r.data.find(s => s.employee_id === Number(empId) && s.day_of_week === d && s.week_parity == null) : null);
        const res = ex
          ? await api.updateShift(ex.id, payload)
          : await api.createShift(payload);
        if (res && res.ok) ok++; else fail++;
      } catch { fail++; }
    }
    setSaving(false);
    if (fail === 0) {
      onSaved && (await onSaved());
      onClose();
    } else {
      alert(`${ok} gespeichert, ${fail} fehlgeschlagen.`);
    }
  };
  return (
    <Modal open={true} onClose={onClose} title="Schicht-Vorlage anwenden" width={460}>
      <div style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 12 }}>
        Setzt für die ausgewählten Tage gleiche Zeiten + optional Pause. Bestehende Schichten werden überschrieben.
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Mitarbeiter</label>
        <select value={empId} onChange={e => setEmpId(Number(e.target.value))}
          style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`, background: "var(--card)", color: COLORS.text }}>
          {(employees || []).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Tage</label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {Object.keys(days).map(d => (
            <button key={d} onClick={() => setDays(s => ({ ...s, [d]: !s[d] }))}
              style={{ padding: "6px 12px", borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: "pointer",
                background: days[d] ? `${COLORS.primary}15` : "var(--cardHover)",
                color: days[d] ? COLORS.primary : COLORS.textLight,
                border: `1px solid ${days[d] ? COLORS.primary : COLORS.border}` }}>{dayLabels[d]}</button>
          ))}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Von</label>
          <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} style={{ width: "100%", padding: 8, borderRadius: 8, border: `1px solid ${COLORS.border}` }} /></div>
        <div><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Bis</label>
          <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} style={{ width: "100%", padding: 8, borderRadius: 8, border: `1px solid ${COLORS.border}` }} /></div>
      </div>
      <div style={{ padding: 10, background: "var(--cardHover)", borderRadius: 8, marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textLight, textTransform: "uppercase", marginBottom: 6 }}>Pause (optional)</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <input type="time" value={breakStart} onChange={e => setBreakStart(e.target.value)} style={{ padding: 6, borderRadius: 6, border: `1px solid ${COLORS.border}` }} />
          <input type="time" value={breakEnd} onChange={e => setBreakEnd(e.target.value)} style={{ padding: 6, borderRadius: 6, border: `1px solid ${COLORS.border}` }} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn variant="secondary" onClick={onClose} disabled={saving}>Abbrechen</Btn>
        <Btn onClick={apply} disabled={saving || !empId}>{saving ? "Wendet an…" : "Anwenden"}</Btn>
      </div>
    </Modal>
  );
};

// SHIFT_OVERRIDE_UI_v1 (3.3) -- Krank / Verspätung / früher Schluss
// SICK_DAY_NOTIFY_v1 (5.3) -- nach Speichern bietet Modal an, Termine durchzugehen
export const ShiftOverrideModal = ({ employees, onClose, onSaved }) => {
  const [empId, setEmpId] = useState(employees && employees[0] ? employees[0].id : "");
  const [date, setDate] = useState(formatDate(today));
  const [otype, setOtype] = useState("sick");
  const [startTime, setStartTime] = useState("10:00");
  const [endTime, setEndTime] = useState("17:00");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [followUpModal, setFollowUpModal] = useState(null); // SICK_DAY_NOTIFY_v1
  const save = async () => {
    if (!empId || !date || !otype) return;
    setSaving(true);
    const body = { employee_id: Number(empId), date, override_type: otype, notes };
    if (otype === "late") body.start_time = startTime;
    else if (otype === "early_leave") body.end_time = endTime;
    else if (otype === "custom") { body.start_time = startTime; body.end_time = endTime; }
    /* EXTRA_SHIFT_v1: Mitarbeiter kommt an einem Tag extra rein → mit Start/Ende */
    else if (otype === "extra") { body.start_time = startTime; body.end_time = endTime; }
    const res = await api.request("/shift-overrides", { method: "POST", body: JSON.stringify(body) });
    setSaving(false);
    if (res && res.ok) {
      onSaved && (await onSaved());
      // SICK_DAY_NOTIFY_v1: bei sick-override gleich Termin-Cleanup anbieten
      if (otype === "sick") {
        const empName = (employees || []).find(e => Number(e.id) === Number(empId))?.name || "Mitarbeiter";
        setFollowUpModal({ employeeId: Number(empId), employeeName: empName, date });
        return;
      }
      onClose();
    } else {
      alert((res && res.data && res.data.error) || "Speichern fehlgeschlagen.");
    }
  };
  if (followUpModal) {
    return <SickDayNotifyModal
      employeeId={followUpModal.employeeId}
      employeeName={followUpModal.employeeName}
      date={followUpModal.date}
      employees={employees}
      mode="manager"
      onClose={() => { setFollowUpModal(null); onClose(); }}
      onChanged={onSaved}
    />;
  }
  return (
    <Modal open={true} onClose={onClose} title="Schichtänderung (krank, verspätet, Extra-Schicht, ...)" width={460}>
      <div style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 12 }}>
        Sofort-Wirkung: blockt Online-Buchung & KI-Tele für den ausgewählten Tag. Audit-Log wird geschrieben.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Mitarbeiter</label>
          <select value={empId} onChange={e => setEmpId(Number(e.target.value))} style={{ width: "100%", padding: 8, borderRadius: 8, border: `1px solid ${COLORS.border}`, background: "var(--card)", color: COLORS.text }}>
            {(employees || []).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <div><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Datum</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ width: "100%", padding: 8, borderRadius: 8, border: `1px solid ${COLORS.border}` }} />
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Art</label>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {[
            { v: "sick", l: "Krank (gesamter Tag blockiert)" },
            { v: "late", l: "Verspätung (späterer Start)" },
            { v: "early_leave", l: "Früher Schluss" },
            { v: "custom", l: "Custom (eigene Zeiten)" },
            /* EXTRA_SHIFT_v1 -- MA kommt extra an einem Tag, sonst nicht eingeteilt */
            { v: "extra", l: "Extra-Schicht (kommt heute zusätzlich rein)" },
          ].map(opt => (
            <label key={opt.v} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, padding: "4px 8px", borderRadius: 6, cursor: "pointer", background: otype === opt.v ? `${COLORS.primary}10` : "transparent" }}>
              <input type="radio" name="ovType" value={opt.v} checked={otype === opt.v} onChange={() => setOtype(opt.v)} /> {opt.l}
            </label>
          ))}
        </div>
      </div>
      {(otype === "late" || otype === "custom" || otype === "extra") && (
        <div style={{ marginBottom: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>{otype === "extra" ? "Start" : "Neuer Start"}</label>
          <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} style={{ padding: 8, borderRadius: 8, border: `1px solid ${COLORS.border}` }} />
        </div>
      )}
      {(otype === "early_leave" || otype === "custom" || otype === "extra") && (
        <div style={{ marginBottom: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Ende</label>
          <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} style={{ padding: 8, borderRadius: 8, border: `1px solid ${COLORS.border}` }} />
        </div>
      )}
      <Input label="Notiz (optional)" value={notes} onChange={e => setNotes(e.target.value)} />
      <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
        <Btn variant="secondary" onClick={onClose} disabled={saving}>Abbrechen</Btn>
        <Btn onClick={save} disabled={saving}>{saving ? "Speichert…" : "Speichern"}</Btn>
      </div>
    </Modal>
  );
};

// ============================================================
// EMPLOYEE_AVATAR_UPLOAD_v1
// Profilbild-Avatar mit Initial-Bubble-Fallback bei Lade-Fehler.
// - Reset imgError-State wenn URL sich aendert (sonst bleibt Bubble nach Re-Upload kleben)
// - Schaut auf BEIDE Feld-Schreibweisen (avatar_image_url + avatarImageUrl).
// - Cache-Buster ans Ende anhaengen falls keiner da ist (z.B. URL ohne ?v=).
// - DEBUG-Log einmal pro Mount, damit man im Console-Tab sehen kann warum Bubble erscheint.
// ============================================================
// CAL_AVATAR_BIG_v1 (Toby 31.07.) -- shape="square" = Rounded Square (zeigt mehr vom Gesicht
// als der Kreis, Ecken ~22% gerundet passend zur App-Optik); Default bleibt Kreis.
export const EmployeeAvatar = ({ emp, size = 50, fontSize = 18, borderWidth = 3, shape = "circle" }) => {
  const _radius = shape === "square" ? Math.round(size * 0.22) : "50%";
  const url = (emp && (emp.avatar_image_url || emp.avatarImageUrl)) || null;
  const [imgError, setImgError] = useState(false);

  // Reset error-state wenn URL sich aendert (z.B. nach neu-Upload)
  useEffect(() => { setImgError(false); }, [url]);

  // Diagnostic log -- damit Toby im Console sehen kann was vorliegt
  useEffect(() => {
    if (emp && (emp.name || emp.id)) {
      try {
        console.log("[Avatar]", emp.name || emp.id, "url=", url, "imgError=", imgError);
      } catch (e) {}
    }
  }, [emp && emp.id, url, imgError]);

  const showImage = !!url && !imgError;
  if (showImage) {
    // Cache-Buster appenden wenn keiner da ist (sicheres Browser-Reload)
    const finalUrl = url.indexOf("?") >= 0 ? url : (url + "?v=" + Date.now());
    return (
      <img
        src={finalUrl}
        alt={emp.name || ""}
        onError={(e) => {
          try { console.warn("[Avatar] LOAD FAILED:", finalUrl, "for", emp.name); } catch(_){}
          setImgError(true);
        }}
        style={{
          width: size,
          height: size,
          borderRadius: _radius,  /* CAL_AVATAR_BIG_v1 */
          objectFit: "cover",
          border: `${borderWidth}px solid ${emp.color || "#ccc"}`,
          flexShrink: 0,
          background: "#fff",
        }}
      />
    );
  }
  // Fallback-Bubble — Tooltip verraet warum kein Bild
  const tooltip = !url ? "Kein Foto hinterlegt" : "Foto konnte nicht geladen werden (404?)";
  // KUERZEL_CONTRAST_v1 (#15) -- Initialen immer sichtbar: Textfarbe nach Helligkeit der MA-Farbe
  // (helle Farbe -> dunkler Text, dunkle Farbe -> weiss), unabhaengig vom Hell/Dunkel-Modus.
  const _bubbleBg = (emp && emp.color) || "#999";
  const _iniColor = (() => {
    try {
      const m = String(_bubbleBg).replace("#", "").trim();
      if (m.length < 6) return "white";
      const r = parseInt(m.slice(0, 2), 16), g = parseInt(m.slice(2, 4), 16), b = parseInt(m.slice(4, 6), 16);
      if ([r, g, b].some(Number.isNaN)) return "white";
      const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return lum > 0.6 ? "#1F2937" : "white";
    } catch (e) { return "white"; }
  })();
  return (
    <div title={tooltip} style={{
      width: size,
      height: size,
      borderRadius: _radius,  /* CAL_AVATAR_BIG_v1 -- Fallback-Bubble in derselben Form */
      background: _bubbleBg,
      color: _iniColor,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize,
      fontWeight: 700,
      flexShrink: 0,
    }}>
      {(emp && emp.avatar) || ((emp && emp.name) ? emp.name.charAt(0).toUpperCase() : "?")}
    </div>
  );
};

// ============================================================
// VACATION_ENTITLEMENT_v1 (23.09.2026) -- Urlaubskonto + Urlaubsanspruch (Auftrag Torsten/H-Galerie).
// Gerechnet wird NUR auf dem Server (services/vacation_entitlement.py: Arbeitstage laut Schichtplan,
// ohne Feiertage, nur Art „Urlaub"); hier wird nur angezeigt. Beide Seiten (Mitarbeiter, Schichtplan)
// nutzen dieselben Bausteine.
const fmtDays = (v) => {
  if (v === null || v === undefined || v === "") return "–";
  const n = Number(v);
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(".", ",");
};

export const VacationAccountLine = ({ row, onEdit }) => {
  if (!row) return null;
  const linkBtn = { background: "none", border: "none", padding: 0, color: COLORS.primary, cursor: "pointer", fontSize: 12, fontWeight: 600, textDecoration: "underline" };
  if (!row.has_plan) {
    return (
      <div data-feature="VACATION_ENTITLEMENT_v1" style={{ fontSize: 12, color: COLORS.textLight, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <span>🌴 Urlaub {row.year}: kein Schichtplan – Tage werden nicht gezählt</span>
        {onEdit && <button onClick={onEdit} style={linkBtn}>Anspruch</button>}
      </div>
    );
  }
  const over = row.remaining !== null && row.remaining < 0;
  return (
    <div data-feature="VACATION_ENTITLEMENT_v1" style={{ fontSize: 12, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
      <span>🌴 Urlaub {row.year}: <strong>{fmtDays(row.taken)}</strong> von {fmtDays(row.entitlement)} Tagen · <strong style={{ color: over ? COLORS.danger : COLORS.success }}>{fmtDays(row.remaining)} übrig</strong></span>
      <span style={{ color: COLORS.textLight, fontSize: 11 }}>({row.manual === null ? `gesetzl. Minimum, ${fmtDays(row.days_per_week)} Arbeitstage/Woche` : "laut Vertrag"}{Number(row.carryover || 0) > 0 ? ` · ${fmtDays(row.base_entitlement)} + ${fmtDays(row.carryover)} Übertrag aus ${row.carryover_from}` : ""})</span>
      {onEdit && <button onClick={onEdit} style={linkBtn}>Anspruch / Übertrag</button>}
    </div>
  );
};

// MY_VACATION_v1 (23.09.2026, Toby „a und b bauen") -- einzelne Urlaube einer Person im Jahr:
// Datum + gezaehlte Tage, geliefert vom Server (dieselbe Liste, aus der „genommen" summiert wird).
export const VacationEntries = ({ entries }) => {
  const list = entries || [];
  if (!list.length) {
    return <div data-feature="MY_VACATION_v1" style={{ fontSize: 12, color: COLORS.textLight, padding: "4px 0" }}>Noch keine Urlaubstage in diesem Jahr eingetragen.</div>;
  }
  return (
    <div data-feature="MY_VACATION_v1" style={{ display: "flex", flexDirection: "column", gap: 3, padding: "6px 0 2px" }}>
      {list.map(e => (
        <div key={e.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12 }}>
          <span>{e.from_date === e.to_date ? formatDateShort(e.from_date) : `${formatDateShort(e.from_date)} – ${formatDateShort(e.to_date)}`}</span>
          <span style={{ color: COLORS.textLight, whiteSpace: "nowrap" }}>
            {e.days === null || e.days === undefined ? "–" : `${fmtDays(e.days)} ${Number(e.days) === 1 ? "Tag" : "Tage"}`}
          </span>
        </div>
      ))}
    </div>
  );
};

// VACATION_CARRYOVER_v1 (26.09.2026, Toby „resturlaub machen“) -- im selben Fenster: Übertrag aus dem Vorjahr
// für das angezeigte Jahr. Shearly schlägt den Rest des Vorjahres vor (Server: suggested_carryover), eingetragen
// wird nur, was der Salon bestätigt; nichts wird automatisch übertragen oder gestrichen.
export const EntitlementEditor = ({ row, onClose, onSaved }) => {
  const [mode, setMode] = useState(row && row.manual !== null && row.manual !== undefined ? "manual" : "auto");
  const [val, setVal] = useState(row && row.manual !== null && row.manual !== undefined ? fmtDays(row.manual) : "");
  const [coVal, setCoVal] = useState(row && Number(row.carryover || 0) > 0 ? fmtDays(row.carryover) : "");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinErr, setPinErr] = useState("");
  if (!row) return null;
  const hasCarry = row.carryover_from !== undefined && row.carryover_from !== null;
  const save = async (ownerPin) => {
    setErr("");
    let v = null;
    if (mode === "manual") {
      v = Number(String(val).replace(",", "."));
      if (!String(val).trim() || isNaN(v) || v < 0 || v > 60) { setErr("Bitte eine Zahl zwischen 0 und 60 eingeben (z. B. 25 oder 24,5)."); return; }
    }
    const curManual = row.manual === null || row.manual === undefined ? null : Number(row.manual);
    const entChanged = v !== curManual;
    if (entChanged && mode === "auto" && !row.has_plan) {
      setErr("Ohne Schichtplan gibt es kein automatisches Minimum – bitte eine Zahl laut Vertrag eintragen."); return;
    }
    let c = 0;
    if (hasCarry && String(coVal).trim()) {
      c = Number(String(coVal).replace(",", "."));
      if (isNaN(c) || c < 0 || c > 60) { setErr("Übertrag: bitte eine Zahl zwischen 0 und 60 eingeben (z. B. 3 oder 2,5)."); return; }
    }
    const coChanged = hasCarry && Math.round(c * 2) / 2 !== Number(row.carryover || 0);
    if (!entChanged && !coChanged) { onClose(); return; }
    const pinPart = typeof ownerPin === "string" ? { owner_pin: ownerPin } : {};
    const handleFail = (r) => {
      const code = r && r.data && r.data.code;
      if (code === "pin_required") { setPinErr(""); setPinOpen(true); return; }
      if (code && String(code).startsWith("pin_")) { setPinErr(ownerPinErrorText(r)); setPinOpen(true); return; }
      setErr((r && r.data && r.data.error) || "Speichern fehlgeschlagen.");
    };
    setBusy(true);
    if (entChanged) {
      const r = await api.updateEmployee(row.employee_id, { vacation_days_per_year: v, ...pinPart });
      if (!(r && r.ok)) { setBusy(false); handleFail(r); return; }
    }
    if (coChanged) {
      const r2 = await api.setVacationCarryover({ employee_id: row.employee_id, year: row.year, days: c, ...pinPart });
      if (!(r2 && r2.ok)) { setBusy(false); handleFail(r2); return; }
    }
    setBusy(false);
    setPinOpen(false);
    onSaved();
  };
  const radio = { display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, cursor: "pointer", padding: "8px 0" };
  return (
    <>
      <div data-feature="VACATION_ENTITLEMENT_v1" onClick={e => { if (e.target === e.currentTarget && !busy) onClose(); }}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10040, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
        <div style={{ background: "var(--card)", borderRadius: 16, padding: 22, width: "100%", maxWidth: 400, boxShadow: "0 25px 50px rgba(0,0,0,0.25)" }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>Urlaubsanspruch – {row.name}</h3>
          <p style={{ margin: "0 0 10px", fontSize: 12.5, color: COLORS.textLight, lineHeight: 1.5 }}>Tage pro Kalenderjahr. Resturlaub aus dem Vorjahr unten als Übertrag eintragen.</p>
          <label style={radio}>
            <input type="radio" checked={mode === "auto"} onChange={() => setMode("auto")} style={{ marginTop: 3 }} />
            <span>Automatisch: gesetzliches Minimum{row.has_plan ? <> – <strong>{fmtDays(row.legal_minimum)} Tage</strong> bei {fmtDays(row.days_per_week)} Arbeitstagen pro Woche</> : " (kein Schichtplan hinterlegt)"}</span>
          </label>
          <label style={radio}>
            <input type="radio" checked={mode === "manual"} onChange={() => setMode("manual")} style={{ marginTop: 3 }} />
            <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>Laut Vertrag:
              <input type="text" inputMode="decimal" value={val} onFocus={() => setMode("manual")} onChange={e => setVal(e.target.value.replace(/[^0-9,\.]/g, "").slice(0, 5))}
                style={{ width: 64, padding: "6px 8px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, textAlign: "center" }} /> Tage</span>
          </label>
          {mode === "manual" && row.has_plan && val && Number(String(val).replace(",", ".")) < Number(row.legal_minimum) && (
            <div style={{ fontSize: 12, color: COLORS.warning, marginTop: 4 }}>Achtung: weniger als das gesetzliche Minimum von {fmtDays(row.legal_minimum)} Tagen.</div>
          )}
          {hasCarry && (
            <div data-feature="VACATION_CARRYOVER_v1" style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${COLORS.border}` }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Übertrag aus {row.carryover_from} in {row.year}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 13 }}>
                <input type="text" inputMode="decimal" value={coVal} placeholder="0" onChange={e => setCoVal(e.target.value.replace(/[^0-9,\.]/g, "").slice(0, 5))}
                  style={{ width: 64, padding: "6px 8px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, textAlign: "center", background: "var(--card)", color: COLORS.text }} /> Tage
                {row.suggested_carryover !== null && row.suggested_carryover !== undefined && Number(row.suggested_carryover) > 0
                  && Number(String(coVal || "0").replace(",", ".")) !== Number(row.suggested_carryover) && (
                  <button type="button" onClick={() => setCoVal(fmtDays(row.suggested_carryover))}
                    style={{ background: "none", border: `1px solid ${COLORS.primary}`, color: COLORS.primary, borderRadius: 8, padding: "5px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    Rest {row.carryover_from} übernehmen: {fmtDays(row.suggested_carryover)} {Number(row.suggested_carryover) === 1 ? "Tag" : "Tage"}
                  </button>
                )}
              </div>
              <div style={{ fontSize: 11.5, color: COLORS.textLight, marginTop: 6, lineHeight: 1.5 }}>
                {row.suggested_carryover !== null && row.suggested_carryover !== undefined
                  ? `Rest ${row.carryover_from} laut den Einträgen in Shearly: ${fmtDays(row.suggested_carryover)} Tage. `
                  : row.suggest_basis === "no_data"
                    ? `Für ${row.carryover_from} sind in Shearly keine Urlaube eingetragen – den Rest kann Shearly nicht ausrechnen. `
                    : "Ohne Schichtplan kann Shearly keinen Rest ausrechnen. "}
                Nur eintragen, was vereinbart ist – Shearly überträgt und streicht nichts automatisch.
              </div>
            </div>
          )}
          {err && <div style={{ fontSize: 12.5, color: COLORS.danger, marginTop: 8 }}>{err}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
            <Btn variant="secondary" onClick={onClose} disabled={busy}>Abbrechen</Btn>
            <Btn onClick={() => save()} disabled={busy}>{busy ? "Speichere …" : "Speichern"}</Btn>
          </div>
        </div>
      </div>
      <OwnerPinPrompt open={pinOpen} title="Inhaber-PIN" text="Anspruch und Übertrag ändern geht nur mit der Inhaber-PIN."
        error={pinErr} busy={busy} onSubmit={p => save(p)} onCancel={() => setPinOpen(false)} />
    </>
  );
};

// PAGE: Employees (mit Provisionen & Trinkgeld)
// ============================================================
// EMP_MODAL_STABLE_v1 (25.09.2026) -- Fenster „Mitarbeiter bearbeiten“ / „Neuer Mitarbeiter“ als eigene Komponente
// auf Modul-Ebene. Vorher war es INLINE in EmployeesPage definiert (`const EmployeeModal = () => …` im Render) →
// bei jedem Neuzeichnen der Seite (Termin-Refresh alle 45 s in app.jsx + bei jedem Zurückwechseln in den Tab)
// eine NEUE Komponente → React baute das Fenster neu auf, alle Eingaben (u. a. „🔑 Persönlicher Login“ mit
// E-Mail + Passwort) fielen auf die Startwerte zurück, „Speichern“ speicherte dann ohne Login (Michi, Salon 25,
// 24.09.2026). Gleiche Fehlerklasse wie SHIFT_MODAL_REMOUNT_FIX_v1 — hier mit eigenen Hooks, daher kein
// Funktionsaufruf möglich, sondern stabile Komponente + Props. NICHT zurück in den Render von EmployeesPage ziehen!
export const EmployeeEditModal = ({ editEmp, saving, enabledFeatures = {}, roleOptions, colorOptions, onClose, onSave }) => {
  const [name, setName] = useState(editEmp?.name || "");
  const [role, setRole] = useState(editEmp?.role || (isKosmetikSalon() ? "Kosmetikerin" : "Stylist")); // BUSINESS_TYPE_v1
  const [phone, setPhone] = useState(editEmp?.phone || "");
  const [email, setEmail] = useState(editEmp?.email || "");
  const [color, setColor] = useState(editEmp?.color || "#7C3AED");
  const [hourlyRate, setHourlyRate] = useState(editEmp?.hourlyRate || 0);
  const [commissionRate, setCommissionRate] = useState(editEmp?.commissionRate || 0);
  const [skills, setSkills] = useState(editEmp?.skills || []);
  // STYLIST_PROFILE_v1 (4.4) -- Profil-Bild URL fuer Online-Buchung
  const [avatarImageUrl, setAvatarImageUrl] = useState(editEmp?.avatar_image_url || editEmp?.avatarImageUrl || "");
  // EMP_ONLINE_BLOCK_v1 -- Schalter "Online buchbar" (Default an; sperrt nur Formular/Zoey/KI, nicht den Kalender)
  const [onlineBookable, setOnlineBookable] = useState(editEmp ? editEmp.onlineBookable !== false : true);
  // KASSE_EMP_ACCESS_v1 -- Schalter "Zugriff auf die Kasse" (Default an; nur sichtbar wenn Kassen-Modul frei)
  const [kasseAccess, setKasseAccess] = useState(editEmp ? editEmp.kasseAccess !== false : true);
  // EMPLOYEE_LOGIN_v1 (5.1) -- Mitarbeiter-Login-Account direkt anlegen/aktualisieren
  const [loginEnabled, setLoginEnabled] = useState(!!(editEmp?.has_login));
  const [loginEmail, setLoginEmail] = useState(editEmp?.login_email || "");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginActive, setLoginActive] = useState(editEmp?.login_active !== false);
      const [categories, setCategories] = useState([]);
      // EMP_SVC_CATS_AUTH_FIX_v1 -- vorher roher fetch mit credentials:"include" (Cookie) = IMMER 401,
      // weil der Server nur den Bearer-Header liest -> Modal zeigte NIE Chips, Zuordnen unmöglich.
      // Jetzt über den api-Wrapper (Token + Auto-Refresh wie überall sonst).
      React.useEffect(() => {
        api.request("/service-categories")
        .then(res => { if (res.ok && Array.isArray(res.data)) setCategories(res.data); })
        .catch(() => {});
      }, []);
      // EMP_SVC_SEARCH_v1 -- Suchfeld filtert die Dienstleistungs-Chips (Import-Salons haben 70+ Services)
      const [skillSearch, setSkillSearch] = useState("");
      const _skillSearchNorm = skillSearch.trim().toLowerCase();
      const visibleCategories = _skillSearchNorm
        ? categories.filter(cat => String(cat).toLowerCase().includes(_skillSearchNorm))
        : categories;


  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "var(--card)", borderRadius: 16, padding: 28, width: "100%", maxWidth: 520, maxHeight: "90vh", overflow: "auto", boxShadow: "0 25px 50px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{editEmp ? "Mitarbeiter bearbeiten" : "Neuer Mitarbeiter"}</h2>
          <button onClick={() => onClose()} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}><X size={20} /></button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4, display: "block" }}>Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Vor- und Nachname"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, boxSizing: "border-box" }} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4, display: "block" }}>Rolle</label>
              <select value={role} onChange={e => setRole(e.target.value)}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, boxSizing: "border-box" }}>
                {roleOptions.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4, display: "block" }}>Telefon</label>
              <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+49 170 ..."
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, boxSizing: "border-box" }} />
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4, display: "block" }}>E-Mail</label>
            <input value={email} onChange={e => setEmail(e.target.value)} placeholder="email@beispiel.de"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, boxSizing: "border-box" }} />
          </div>

          {/* EMPLOYEE_AVATAR_UPLOAD_v1 (4.4 erweitert) -- Drag&Drop + File-Picker */}
          <AvatarUploadField
            employeeId={editEmp && editEmp.id}
            currentUrl={avatarImageUrl}
            onUploaded={setAvatarImageUrl}
            fallbackInitials={(name || "?").split(" ").map(w=>w[0]).join("").substring(0,2).toUpperCase()}
            fallbackColor={color}
          />

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4, display: "block" }}>Farbe</label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {colorOptions.map(c => (
                <div key={c} onClick={() => setColor(c)} style={{
                  width: 28, height: 28, borderRadius: "50%", background: c, cursor: "pointer",
                  border: color === c ? "3px solid #1F2937" : "3px solid transparent", transition: "all 0.15s"
                }} />
              ))}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4, display: "block" }}>Stundenlohn (€)</label>
              <input type="number" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4, display: "block" }}>Provision (%)</label>
              <input type="number" value={commissionRate} onChange={e => setCommissionRate(e.target.value)}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, boxSizing: "border-box" }} />
            </div>
          </div>

          <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4, display: "block" }}>Dienstleistungen</label>
        <div style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 8 }}>Welche Dienstleistungen bietet dieser Mitarbeiter an? (Mehrfachauswahl — {skills.length} ausgewählt)</div>
        {/* EMP_SVC_SEARCH_v1 -- Suchfeld nur zeigen wenn die Liste lang genug ist, dass Suchen hilft */}
        {categories.length > 8 && (
          <input type="text" value={skillSearch} onChange={e => setSkillSearch(e.target.value)}
            placeholder="Dienstleistung suchen… (z.B. Augenbrauen)"
            style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, marginBottom: 8, boxSizing: "border-box", background: "var(--card)", color: COLORS.text }} />
        )}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {visibleCategories.map(cat => (
            <span key={cat} onClick={() => setSkills(prev => prev.includes(cat) ? prev.filter(s => s !== cat) : [...prev, cat])} style={{
              padding: "8px 16px", borderRadius: 20, fontSize: 13, cursor: "pointer",
              background: skills.includes(cat) ? color + "20" : COLORS.bg,
              color: skills.includes(cat) ? color : COLORS.textLight,
              border: "2px solid " + (skills.includes(cat) ? color : COLORS.border),
              fontWeight: skills.includes(cat) ? 600 : 400, userSelect: "none"
            }}>{skills.includes(cat) ? "✓ " : ""}{cat}</span>
          ))}
          {_skillSearchNorm && visibleCategories.length === 0 && categories.length > 0 && (
            <span style={{ fontSize: 12, color: COLORS.textLight, fontStyle: "italic" }}>Keine Dienstleistung passt zu „{skillSearch.trim()}"</span>
          )}
          {/* EMP_SVC_CATS_AUTH_FIX_v1 -- leere Liste nicht mehr stumm: erklären statt rätseln lassen */}
          {categories.length === 0 && (
            <span style={{ fontSize: 12, color: COLORS.textLight, fontStyle: "italic" }}>Keine Dienstleistungen gefunden — bitte zuerst unter „Dienstleistungen" anlegen oder Seite neu laden.</span>
          )}
        </div>
          </div>
        </div>

          {/* EMP_ONLINE_BLOCK_v1 -- Mitarbeiter fuer Online-Buchungen sperren/freigeben */}
          <div style={{ marginTop: 14, padding: 14, background: "var(--cardHover)", borderRadius: 10, border: `1px solid ${onlineBookable ? COLORS.border : "#DC2626"}` }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
              <input type="checkbox" checked={onlineBookable} onChange={e => setOnlineBookable(e.target.checked)} />
              🌐 Online buchbar
            </label>
            <p style={{ fontSize: 11, color: COLORS.textLight, margin: 0 }}>
              {onlineBookable
                ? "Kunden können diesen Mitarbeiter über die Online-Buchung, den Chat und das KI-Telefon buchen."
                : "Gesperrt: erscheint nicht mehr in der Online-Buchung, im Chat und am KI-Telefon. Termine im Kalender können weiterhin selbst eingetragen werden; bestehende Termine bleiben unverändert."}
            </p>
          </div>

          {/* KASSE_EMP_ACCESS_v1 -- Kassen-Zugriff pro Mitarbeiter (nur wenn Kassen-Modul für den Salon frei) */}
          {enabledFeatures.pos_shearly === true && (
            <div style={{ marginTop: 14, padding: 14, background: "var(--cardHover)", borderRadius: 10, border: `1px solid ${kasseAccess ? COLORS.border : "#DC2626"}` }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
                <input type="checkbox" checked={kasseAccess} onChange={e => setKasseAccess(e.target.checked)} />
                🧾 Zugriff auf die Kasse
              </label>
              <p style={{ fontSize: 11, color: COLORS.textLight, margin: 0 }}>
                {kasseAccess
                  ? "Dieser Mitarbeiter sieht den Menüpunkt „Kasse“ und kann die Shearly Kasse mit seinem eigenen Login öffnen."
                  : "Gesperrt: Der Menüpunkt „Kasse“ ist für diesen Mitarbeiter ausgeblendet und die Kasse lässt sich mit seinem Login nicht öffnen. Der Inhaber-Zugriff bleibt unberührt."}
              </p>
            </div>
          )}

          {/* EMPLOYEE_LOGIN_v1 (5.1) -- Mitarbeiter-Login direkt anlegen/aendern */}
          {editEmp && editEmp.id && (
            <div style={{ marginTop: 14, padding: 14, background: "var(--cardHover)", borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
                <input type="checkbox" checked={loginEnabled} onChange={e => setLoginEnabled(e.target.checked)} />
                🔑 Persönlicher Login für {name || "diesen Mitarbeiter"}
              </label>
              <p style={{ fontSize: 11, color: COLORS.textLight, margin: "0 0 10px" }}>
                Damit kann sich der Mitarbeiter selbst auf seinem Handy einloggen — eigener Kalender, Schichten, Self-Krankmeldung.
                Nicht zu verwechseln mit dem geteilten Empfangs-Account (siehe „Einstellungen → Team-Account").
              </p>
              {loginEnabled && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 3, display: "block" }}>Login-E-Mail</label>
                    <input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)}
                      placeholder="vorname.nachname@salon-mail.de"
                      style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13, background: "var(--card)", color: COLORS.text, boxSizing: "border-box" }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 3, display: "block" }}>
                      {editEmp.has_login ? "Neues Passwort (optional, nur ausfüllen wenn ändern)" : "Passwort (mind. 8 Zeichen + 1 Zahl/Sonderzeichen) *"}
                    </label>
                    <input type="text" value={loginPassword} onChange={e => setLoginPassword(e.target.value)}
                      placeholder={editEmp.has_login ? "Leer = Passwort behalten" : "min. 8 Zeichen + 1 Zahl/Sonderzeichen"}
                      style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13, background: "var(--card)", color: COLORS.text, boxSizing: "border-box", fontFamily: "monospace" }} />
                  </div>
                  {editEmp.has_login && (
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: COLORS.textLight, cursor: "pointer", marginTop: 4 }}>
                      <input type="checkbox" checked={loginActive} onChange={e => setLoginActive(e.target.checked)} />
                      Login aktiv (deaktivieren statt löschen)
                    </label>
                  )}
                  {editEmp.has_login && editEmp.login_email && (
                    <div style={{ fontSize: 11, color: COLORS.textLight, fontStyle: "italic" }}>
                      Aktueller Login: <strong>{editEmp.login_email}</strong> {editEmp.login_active ? "✓ aktiv" : "(deaktiviert)"}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {!editEmp && (
            <div style={{ marginTop: 12, padding: "8px 12px", background: "var(--cardHover)", borderRadius: 8, fontSize: 11, color: COLORS.textLight }}>
              💡 Login-Account kannst du anlegen nachdem der Mitarbeiter erstellt ist (in der Bearbeiten-Ansicht).
            </div>
          )}

        <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
          <Btn variant="secondary" onClick={() => onClose()}>Abbrechen</Btn>
          <Btn onClick={() => onSave({
            name, role, phone, email, color, hourlyRate, commissionRate, skills, avatarImageUrl,
            // EMPLOYEE_LOGIN_v1
            loginEnabled, loginEmail: loginEmail.trim(), loginPassword, loginActive,
            onlineBookable, // EMP_ONLINE_BLOCK_v1
            kasseAccess, // KASSE_EMP_ACCESS_v1
          })} disabled={!name.trim() || saving}>
            {saving ? "Speichern..." : (editEmp ? "Speichern" : "Erstellen")}
          </Btn>
        </div>
      </div>
    </div>
  );
};

export const EmployeesPage = ({ employees = [], setEmployees, appointments = [], services = [], badgeAwards = [], onGoToSubscription, enabledFeatures = {} }) => { /* KASSE_EMP_ACCESS_v1: +enabledFeatures */
  // VACATION_ENTITLEMENT_v1 -- Urlaubskonto des laufenden Jahres (Endpunkt nur fuer Inhaber/Admin)
  const [vacBal, setVacBal] = useState(null);
  const [entEdit, setEntEdit] = useState(null);
  const loadVacBal = () => api.getVacationBalance(new Date().getFullYear()).then(r => { if (r && r.ok) setVacBal(r.data); });
  useEffect(() => { loadVacBal(); }, []);
  const vacBalRows = {};
  ((vacBal && vacBal.employees) || []).forEach(r => { vacBalRows[r.employee_id] = r; });
  // EMP_SKILLS_COLLAPSE_v1 -- Skills/Dienstleistungen-Pills hinter Click verbergen (zu viele Pills wirken überladen)
  const [expandedSkillsEmpId, setExpandedSkillsEmpId] = useState(null);
  // EMP_SVC_REMOVE_X_v1 -- verhindert Doppelklick waehrend ein Entfernen-PUT laeuft
  const [skillRemoveBusy, setSkillRemoveBusy] = useState(false);
  const [activeTab, setActiveTab] = useState("team");
  const [selectedMonth, setSelectedMonth] = useState("2026-03");
  const [showModal, setShowModal] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [saving, setSaving] = useState(false);
  // EMPLOYEE_LIMIT_v1 -- Plan-Limit-Status (Bestandsschutz-Banner + Anlege-Bremse mit Upgrade-Weg)
  const [empLimit, setEmpLimit] = useState(null);
  const [limitModal, setLimitModal] = useState(null);
  const loadEmpLimit = async () => {
    try {
      const r = await api.request("/employees/limit");
      if (r.ok && r.data && typeof r.data.active === "number") setEmpLimit(r.data);
    } catch (e) {}
  };
  useEffect(() => { loadEmpLimit(); }, []);

  /* BUSINESS_TYPE_v2 -- Rollen-Vorschläge je Branche */
  const ROLE_OPTIONS = isKosmetikSalon()
    ? ["Inhaberin", "Inhaber", "Kosmetikerin", "Kosmetiker", "Senior Kosmetikerin", "Junior Kosmetikerin", "Fußpflegerin", "Nageldesignerin", "Wimpernstylistin", "Auszubildende", "Auszubildender", "Rezeptionistin", "Rezeptionist"]
    : ["Inhaberin", "Inhaber", "Senior Stylistin", "Senior Stylist", "Stylistin", "Stylist", "Junior Stylistin", "Junior Stylist", "Colorist", "Auszubildende", "Auszubildender", "Rezeptionist", "Rezeptionistin"];
  const COLOR_OPTIONS = ["#DC2626","#EA580C","#D97706","#65A30D","#16A34A","#0D9488","#0891B2","#2563EB","#7C3AED","#9333EA","#DB2777","#E11D48"];

  const reloadEmployees = async () => {
    loadEmpLimit(); // EMPLOYEE_LIMIT_v1 -- Zaehlstand parallel mit aktualisieren (nach Anlegen/Entfernen)
    const res = await api.getEmployees();
    // NULLSAFE_AUDIT_v1 -- Array.isArray-Check verhindert Crash wenn API leeren/falschen Body returnt
    if (res.ok && Array.isArray(res.data)) setEmployees(res.data.map(e => ({
      id: e.id, name: e.name, role: e.role_title, color: e.color, phone: e.phone, email: e.email,
      skills: e.skills || [], hourlyRate: e.hourly_rate, avatar: e.avatar,
      // STYLIST_PROFILE_v1 (4.4)
      avatar_image_url: e.avatar_image_url || null, avatarImageUrl: e.avatar_image_url || null,
      // EMPLOYEE_LOGIN_v1 (5.1)
      has_login: !!e.has_login, login_email: e.login_email || null, login_active: !!e.login_active,
      isOwner: !!e.is_owner, isManager: !!e.is_manager, managerPages: e.manager_pages || [], commissionRate: e.commission_rate,
      onlineBookable: e.online_bookable !== 0 && e.online_bookable !== false, // EMP_ONLINE_BLOCK_v1
      kasseAccess: e.kasse_access !== 0 && e.kasse_access !== false, // KASSE_EMP_ACCESS_v1
    })));
  };

  // EMP_DEACTIVATE_v1 (Toby 21.07.) -- Mitarbeiter pausieren/reaktivieren (z.B. Schwangerschaft).
  // Deaktiviert = raus aus Kalender, Schichtplan-Pflicht, Online-Buchung, Zoey + KI-Telefonie
  // (Server filtert überall is_active=1); Termine + Statistiken bleiben erhalten.
  const [inactiveEmps, setInactiveEmps] = useState([]);
  const reloadInactive = async () => {
    const r = await api.request("/employees?include_inactive=1");
    if (r && r.ok && Array.isArray(r.data)) setInactiveEmps(r.data.filter(e => !e.is_active));
  };
  useEffect(() => { reloadInactive(); }, []);

  const handleDeactivate = async (emp) => {
    if (emp.isOwner) { alert("Salon-Inhaber kann nicht deaktiviert werden."); return; }
    if (!confirm(`${emp.name} deaktivieren?\n\n${emp.name} verschwindet aus dem Kalender, braucht keinen Schichtplan mehr und ist online sowie über die KI-Telefonie nicht mehr buchbar. Termine und Statistiken bleiben erhalten.\n\nUnten unter „Deaktivierte Mitarbeiter" jederzeit wieder aktivierbar.`)) return;
    const res = await api.updateEmployee(emp.id, { is_active: 0 });
    if (res && res.ok) { await reloadEmployees(); await reloadInactive(); }
    else alert("Deaktivieren fehlgeschlagen: " + ((res && res.data && res.data.error) || "Server-Fehler"));
  };

  const handleReactivate = async (emp) => {
    const res = await api.updateEmployee(emp.id, { is_active: 1 });
    if (res && res.ok) { await reloadEmployees(); await reloadInactive(); }
    else alert((res && res.data && res.data.error) || "Aktivieren fehlgeschlagen.");
  };

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        role_title: formData.role,
        color: formData.color,
        phone: formData.phone || "",
        email: formData.email || "",
        // NULLSAFE_AUDIT_v1 -- defensive falls formData.name null/undefined
        avatar: String(formData.name || "").split(" ").map(w => (w && w[0]) || "").join("").substring(0, 2).toUpperCase(),
        // STYLIST_PROFILE_v1 (4.4)
        avatar_image_url: formData.avatarImageUrl || null,
        hourly_rate: parseFloat(formData.hourlyRate) || 0,
        commission_rate: parseFloat(formData.commissionRate) || 0,
        skills: formData.skills,
        online_bookable: formData.onlineBookable === false ? 0 : 1, // EMP_ONLINE_BLOCK_v1
        kasse_access: formData.kasseAccess === false ? 0 : 1, // KASSE_EMP_ACCESS_v1
      };
      // EMPLOYEE_LOGIN_v1 (5.1) -- nur bei Edit-Modus + nur wenn user etwas geaendert hat
      if (editEmp && formData.loginEnabled) {
        if (formData.loginEmail) payload.login_email = formData.loginEmail;
        if (formData.loginPassword) payload.login_password = formData.loginPassword;
        if (typeof formData.loginActive === "boolean") payload.login_active = formData.loginActive;
      } else if (editEmp && !formData.loginEnabled && editEmp.has_login) {
        // Login deaktivieren
        payload.login_active = false;
      }
      let res;
      if (editEmp) {
        res = await api.updateEmployee(editEmp.id, payload);
      } else {
        res = await api.createEmployee(payload);
      }
      if (res.ok) {
        await reloadEmployees();
        setShowModal(false);
        setEditEmp(null);
      } else if (res.status === 409 && res.data && res.data.code === "EMPLOYEE_LIMIT_REACHED") {
        // EMPLOYEE_LIMIT_v1 -- Limit erreicht: Hinweisfenster mit direktem Upgrade-Weg statt alert
        setLimitModal(res.data.error);
      } else {
        // EMP_SAVE_ERRMSG_v1 -- vorher: Fehler = Modal bleibt stumm offen (Muster APPT_SAVE_ERRMSG_FIX_v1)
        alert((res.data && res.data.error) || "Speichern fehlgeschlagen — bitte erneut versuchen.");
      }
    } catch (e) { console.error(e); alert("Speichern fehlgeschlagen — bitte Internetverbindung prüfen."); }
    setSaving(false);
  };

  // EMP_DELETE_GUARD_v1 -- 2-stufige Bestaetigung (Misclick-Schutz)
  const handleDelete = async (emp) => {
    if (emp.isOwner) {
      alert("Salon-Inhaber kann nicht entfernt werden.");
      return;
    }
    const step1 = confirm(
      `Mitarbeiter "${emp.name}" wirklich entfernen?\n\n` +
      `Bestehende Termine, Provisionen und Statistiken bleiben erhalten — ` +
      `aber der Mitarbeiter ist nicht mehr buchbar und nicht mehr im Schichtplan.\n\n` +
      `Im nächsten Schritt musst du den Namen zur Bestaetigung eingeben.`
    );
    if (!step1) return;
    const typed = prompt(`Tippe zum Bestaetigen den Namen ein:\n\n${emp.name}`);
    if (typed === null) return; // Cancel
    if (String(typed).trim() !== String(emp.name).trim()) {
      alert("Name stimmt nicht ueberein. Vorgang abgebrochen — nichts geloescht.");
      return;
    }
    const res = await api.deleteEmployee(emp.id);
    if (res && res.ok) {
      await reloadEmployees();
      await reloadInactive(); // EMP_DEACTIVATE_v1 -- Soft-Delete landet in der Deaktiviert-Liste
    } else {
      alert("Löschen fehlgeschlagen. Bitte erneut versuchen.");
    }
  };

  // Commission config per employee (percentage of service revenue)
  const [commissions] = useState({
    0: { rate: 0, bonusThreshold: 0, bonusRate: 0 },
    1: { rate: 35, bonusThreshold: 5000, bonusRate: 5 },
    2: { rate: 30, bonusThreshold: 4000, bonusRate: 5 },
    3: { rate: 25, bonusThreshold: 3000, bonusRate: 3 },
    4: { rate: 35, bonusThreshold: 5000, bonusRate: 5 },
  });

  // Mock monthly revenue per employee
  const [monthlyData] = useState({
    0: { revenue: 7800, services: 64, tips: 420, tipStripe: 280, tipCash: 140, hours: 140 },
    1: { revenue: 6420, services: 82, tips: 345, tipStripe: 215, tipCash: 130, hours: 168 },
    2: { revenue: 4850, services: 95, tips: 180, tipStripe: 120, tipCash: 60, hours: 172 },
    3: { revenue: 3200, services: 68, tips: 95, tipStripe: 55, tipCash: 40, hours: 160 },
    4: { revenue: 5900, services: 74, tips: 290, tipStripe: 190, tipCash: 100, hours: 170 },
  });

  const tabs = [
    { id: "team", label: "Team-Übersicht", icon: Users },
    /* TIER_PRICING_UI_v1 -- neuer Tab fuer Tier-basierte Preise pro Mitarbeiter-Klasse */
    { id: "tiers", label: isKosmetikSalon() ? "Mitarbeiter-Preise" : "Stylist-Preise", icon: DollarSign }, // BUSINESS_TYPE_v1
    { id: "badges", label: "Abzeichen", icon: Award },
    { id: "provision", label: "Provisionen", icon: TrendingUp },
    { id: "tips", label: "Trinkgeld", icon: Euro },
    { id: "settlement", label: "Monatsabrechnung", icon: CreditCard },
  ];

  const defaultEmpData = { revenue: 0, services: 0, tips: 0, tipStripe: 0, tipCash: 0, hours: 0 };
  const safeMonthlyData = new Proxy(monthlyData, { get: (t, k) => t[k] || defaultEmpData });
  const calcCommission = (empId) => {
    const conf = commissions[empId];
    const data = safeMonthlyData[empId];
    if (!conf || !data) return { base: 0, bonus: 0, total: 0 };
    const base = data.revenue * ((conf?.rate || 0) / 100);
    const bonus = (data?.revenue || 0) >= (conf?.bonusThreshold || 0) ? data.revenue * ((conf?.bonusRate || 0) / 100) : 0;
    return { base: Math.round(base), bonus: Math.round(bonus), total: Math.round(base + bonus) };
  };

  return (
    <div>
      {/* EMPLOYEE_LIMIT_v1 -- Bestandsschutz-Banner: ueber dem Limit arbeiten alle normal weiter, nur Neuanlage ist gesperrt */}
      {empLimit && empLimit.over_limit && (
        <div style={{ background: "rgba(245,158,11,0.14)", border: "1px solid #f59e0b", borderRadius: 10, padding: "10px 14px", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", fontSize: 13, color: "var(--text)" }}>
          <span>⚠️ Dein Team hat <b>{empLimit.active} aktive Mitarbeiter</b> — dein Plan „{empLimit.plan_name}“ umfasst {empLimit.limit}. Alle arbeiten normal weiter, neue Mitarbeiter kannst du nach einem Upgrade anlegen.</span>
          {onGoToSubscription && <button onClick={() => onGoToSubscription()} style={{ background: "#92400E", color: "#fff", border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Plan upgraden → Mein Abo</button>}
        </div>
      )}
      {/* EMPLOYEE_LIMIT_v1 -- Hinweisfenster wenn das Anlegen am Plan-Limit scheitert (409).
          zIndex 10500: muss UEBER dem Mitarbeiter-Formular (9999) liegen, sonst unsichtbar. */}
      {limitModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "var(--card)", borderRadius: 14, padding: 24, maxWidth: 460, width: "100%", border: "1px solid var(--border)", boxShadow: "0 12px 40px rgba(0,0,0,0.25)" }}>
            <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 10 }}>Mitarbeiter-Limit erreicht</div>
            <div style={{ fontSize: 14, lineHeight: 1.55, color: "var(--text)", marginBottom: 18 }}>{limitModal}</div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button onClick={() => setLimitModal(null)} style={{ padding: "9px 16px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--card)", color: "var(--text)", cursor: "pointer" }}>Schließen</button>
              {onGoToSubscription && <button onClick={() => { setLimitModal(null); setShowModal(false); setEditEmp(null); onGoToSubscription(); }} style={{ padding: "9px 16px", borderRadius: 8, border: "none", background: "#7C3AED", color: "#fff", fontWeight: 700, cursor: "pointer" }}>Jetzt upgraden → Mein Abo</button>}
            </div>
          </div>
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
        <Header title="Mitarbeiter" subtitle={`${employees.length} Mitarbeiter aktiv — Provisionen, Trinkgeld & Abrechnung`} />
        {/* VACATION_ENTITLEMENT_v1 -- Anspruch-Editor (festes Overlay, Position im Baum egal) */}
        {entEdit && <EntitlementEditor row={entEdit} onClose={() => setEntEdit(null)} onSaved={() => { setEntEdit(null); loadVacBal(); }} />}
        <div style={{ display: "flex", gap: 8 }}>
        <Btn onClick={() => { setEditEmp(null); setShowModal(true); }}>
          <Plus size={14} /> Neuer Mitarbeiter
        </Btn>
        {/* BUG006_HIDE_FAKE_REPORT_v1 -- Umsatzbericht-Export druckt Mock-Daten (monthlyData)
            + hartcodiert "Salon Schönheit" im Kopf -> bis echte API-Daten da sind ausgeblendet
            (Audit BUG-006). Reaktivieren: false-Guard entfernen + Umsatz/Salonname aus API. */}
        {false && <Btn size="sm" variant="secondary" onClick={() => {
          const w = window.open("", "_blank");
          const rows = employees.map(emp => {
            const data = safeMonthlyData[emp.id];
            const comm = calcCommission(emp.id);
            return `<tr>
              <td>${emp.name}</td><td>${emp.role}</td>
              <td style="text-align:right">${(data?.revenue || 0).toLocaleString("de-DE")} €</td>
              <td style="text-align:right">${data?.services || 0}</td>
              <td style="text-align:right">${comm.base} €</td>
              <td style="text-align:right">${comm.bonus} €</td>
              <td style="text-align:right"><strong>${comm.total} €</strong></td>
              <td style="text-align:right">${data?.tips || 0} €</td>
              <td style="text-align:right">${data?.hours || 0}h</td>
            </tr>`;
          }).join("");
          const totalRev = employees.reduce((s, e) => s + (safeMonthlyData[e.id]?.revenue || 0), 0);
          const totalComm = employees.reduce((s, e) => s + calcCommission(e.id).total, 0);
          const totalTips = employees.reduce((s, e) => s + (safeMonthlyData[e.id]?.tips || 0), 0);
          w.document.write(`<!DOCTYPE html><html><head><title>Umsatzbericht ${selectedMonth}</title>
            <style>body{font-family:-apple-system,sans-serif;padding:40px;color:#1F2937}
            h1{font-size:20px;margin-bottom:4px}h2{font-size:14px;color:#6B7280;margin-bottom:24px;font-weight:400}
            table{width:100%;border-collapse:collapse;font-size:13px}
            th{text-align:left;padding:8px;border-bottom:2px solid #E5E7EB;font-size:11px;text-transform:uppercase;color:#6B7280}
            td{padding:8px;border-bottom:1px solid #E5E7EB}
            tfoot td{border-top:2px solid #1F2937;font-weight:700}
            .footer{margin-top:40px;font-size:11px;color:#9CA3AF;text-align:center}
            @media print{body{padding:20px}}</style></head><body>
            <h1>Umsatzbericht — ${selectedMonth}</h1>
            <h2>Salon Schönheit — Erstellt am ${new Date().toLocaleDateString("de-DE")}</h2>
            <table><thead><tr><th>Mitarbeiter</th><th>Rolle</th><th style="text-align:right">Umsatz</th>
            <th style="text-align:right">Services</th><th style="text-align:right">Provision</th>
            <th style="text-align:right">Bonus</th><th style="text-align:right">Gesamt</th>
            <th style="text-align:right">Trinkgeld</th><th style="text-align:right">Stunden</th></tr></thead>
            <tbody>${rows}</tbody>
            <tfoot><tr><td colspan="2">Gesamt</td><td style="text-align:right">${totalRev.toLocaleString("de-DE")} €</td>
            <td></td><td></td><td></td><td style="text-align:right">${totalComm.toLocaleString("de-DE")} €</td>
            <td style="text-align:right">${totalTips} €</td><td></td></tr></tfoot></table>
            <div class="footer">Shearly — Automatisch generierter Bericht</div></body></html>`);
          w.document.close();
          setTimeout(() => w.print(), 300);
        }}>
          <Download size={12} /> PDF-Export
        </Btn>}
        </div>
      </div>

      {/* Employee Add/Edit Modal */}
      {/* EMP_MODAL_STABLE_v1 -- stabile Komponente (s. EmployeeEditModal oben); key = neuer Zustand je Mitarbeiter */}
      {showModal && (
        <EmployeeEditModal key={(editEmp && editEmp.id) || "new"}
          editEmp={editEmp} saving={saving} enabledFeatures={enabledFeatures}
          roleOptions={ROLE_OPTIONS} colorOptions={COLOR_OPTIONS}
          onClose={() => { setShowModal(false); setEditEmp(null); }}
          onSave={handleSave} />
      )}

      <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: `2px solid ${COLORS.border}` }}>
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              display: "flex", alignItems: "center", gap: 6, padding: "10px 16px", fontSize: 13, fontWeight: isActive ? 700 : 500,
              background: "none", border: "none", cursor: "pointer", color: isActive ? COLORS.primary : COLORS.textLight,
              borderBottom: isActive ? `2px solid ${COLORS.primary}` : "2px solid transparent", marginBottom: -2, transition: "all 0.15s"
            }}>
              <Icon size={15} /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* TAB: Team Overview */}
      {activeTab === "team" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
          {employees.map((emp, idx) => {
            const data = safeMonthlyData[emp.id];
            const comm = calcCommission(emp.id);
            return (
              <Card key={emp.id} style={{ position: "relative" }}>
                {/* EMPLOYEE_SORT_v2 -- Reihenfolge wird im Kalender per Long-Press + Drag geändert, nicht hier. */}
                {/* EMP_DELETE_GUARD_v1 -- delete button raeumlich abgesetzt vom Edit (Misclick-Schutz) */}
                <div style={{ position: "absolute", top: 12, right: 12, display: "flex", gap: 4 }}>
                  <button onClick={() => { setEditEmp(emp); setShowModal(true); }} title="Bearbeiten"
                    style={{ background: `${COLORS.primary}10`, border: "none", borderRadius: 6, padding: "5px 7px", cursor: "pointer", color: COLORS.primary }}>
                    <Edit3 size={13} />
                  </button>
                  {/* EMP_DEACTIVATE_v2 -- ⊘-Knopf entfernt (Toby: „total doof"), stattdessen Aktiv-Switch über „Salonleitung" */}
                  {!emp.isOwner && (
                    <button onClick={() => handleDelete(emp)} title="Entfernen (mit doppelter Bestaetigung)"
                      style={{ background: `${COLORS.danger}10`, border: `1px solid ${COLORS.danger}40`, borderRadius: 6, padding: "5px 7px", cursor: "pointer", color: COLORS.danger, marginLeft: 16 }}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
                  {/* EMPLOYEE_AVATAR_UPLOAD_v1 -- Profilbild in Mitarbeiter-Cards + Fallback-Bubble bei Lade-Fehler */}
                  {/* CAL_AVATAR_BIG_v2 -- gleiche Rounded-Square-Form wie im Kalenderkopf (durchgehende Optik) */}
                  <EmployeeAvatar emp={emp} size={50} fontSize={18} borderWidth={3} shape="square" />
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{emp.name}</h3>
                      <EmployeeBadges employeeId={emp.id} badgeAwards={badgeAwards} size={26} />
                    </div>
                    <p style={{ margin: "2px 0 0", fontSize: 13, color: COLORS.textLight }}>{emp.role}</p>
                  </div>
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.8 }}>
                  <div><Phone size={12} style={{ verticalAlign: -2 }} /> {emp.phone}</div>
                  <div><Euro size={12} style={{ verticalAlign: -2 }} /> {(emp?.hourlyRate || 0)} €/Std. | Provision: {(commissions[emp.id]?.rate || 0)}%</div>
                  <div title="Stand: Termin-Wert × abgeschlossene Termine. Mit verbundener Kasse aus echten Zahlungen." ><TrendingUp size={12} style={{ verticalAlign: -2 }} /> Umsatz Monat: <strong>{(data?.revenue || 0).toLocaleString("de-DE")} €</strong>{" "}<span style={{ fontSize: 9, color: COLORS.textLight }}>(geschätzt)</span></div>
                  <div title="Stand: nur Trinkgelder aus Stripe-Online-Vorkasse. Vollständig mit Kassensystem-Anbindung."><Star size={12} style={{ verticalAlign: -2 }} /> Trinkgeld: <strong>{(data?.tips || 0)} €</strong>{" "}<span style={{ fontSize: 9, color: COLORS.textLight }}>(nur online)</span></div>
                  {vacBalRows[emp.id] && <VacationAccountLine row={vacBalRows[emp.id]} onEdit={() => setEntEdit(vacBalRows[emp.id])} />}
                </div>
                {/* EMP_SKILLS_COLLAPSE_v1 -- Skills nicht mehr dauerhaft sichtbar.
                    Default: nur ein dezenter "X Dienstleistungen ▾" Toggle.
                    Klick öffnet die Pills-Liste mit sanftem Slide. */}
                {emp.skills && emp.skills.length > 0 && (() => {
                  const isOpen = expandedSkillsEmpId === emp.id;
                  return (
                    <div style={{ marginTop: 12 }}>
                      <button
                        onClick={() => setExpandedSkillsEmpId(isOpen ? null : emp.id)}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 6,
                          background: isOpen ? `${emp.color}12` : "transparent",
                          border: `1px solid ${emp.color}40`,
                          color: emp.color, fontWeight: 600, fontSize: 12,
                          padding: "4px 10px", borderRadius: 14, cursor: "pointer",
                          transition: "background 0.15s",
                        }}
                        aria-expanded={isOpen}>
                        <Scissors size={11} />
                        {emp.skills.length} Dienstleistung{emp.skills.length === 1 ? "" : "en"}
                        <span style={{ fontSize: 10, marginLeft: 2, transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▾</span>
                      </button>
                      {isOpen && (
                        <div style={{
                          display: "flex", flexWrap: "wrap", gap: 4, marginTop: 10,
                          paddingTop: 10, borderTop: `1px dashed ${COLORS.border}`,
                          animation: "shearlySkillsSlide 0.18s ease-out",
                        }}>
                          {/* EMP_SVC_REMOVE_X_v1 -- × auf jedem Chip: Dienstleistung direkt von der Karte entfernen (mit Rückfrage) */}
                          {emp.skills.map(skill => (
                            <span key={skill} style={{
                              padding: "2px 4px 2px 10px", borderRadius: 20, fontSize: 11,
                              background: `${emp.color}15`, color: emp.color, fontWeight: 500,
                              display: "inline-flex", alignItems: "center", gap: 3
                            }}>
                              {skill}
                              <span
                                title={`„${skill}" entfernen`}
                                onClick={async () => {
                                  if (skillRemoveBusy) return;
                                  if (!confirm(`„${skill}" bei ${emp.name} entfernen?\n\nBestehende Termine bleiben unverändert — ${emp.name} ist für diese Dienstleistung nur nicht mehr online buchbar.`)) return;
                                  setSkillRemoveBusy(true);
                                  try {
                                    const res = await api.updateEmployee(emp.id, { skills: emp.skills.filter(s => s !== skill) });
                                    if (res.ok) { await reloadEmployees(); }
                                    else { alert((res.data && res.data.error) || "Entfernen fehlgeschlagen — bitte Seite neu laden und erneut versuchen."); }
                                  } catch (err) { alert("Entfernen fehlgeschlagen — bitte Internetverbindung prüfen."); }
                                  setSkillRemoveBusy(false);
                                }}
                                style={{ cursor: "pointer", fontWeight: 700, fontSize: 13, lineHeight: 1, padding: "2px 5px", borderRadius: "50%", opacity: 0.65 }}
                              >×</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
                {/* EMP_SVC_EMPTY_ENTRY_v1 -- bei 0 zugeordneten Dienstleistungen sichtbarer Einstieg zum Zuordnen */}
                {(!emp.skills || emp.skills.length === 0) && (
                  <div style={{ marginTop: 12 }}>
                    <button onClick={() => { setEditEmp(emp); setShowModal(true); }}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: `1px dashed ${emp.color}66`, color: emp.color, fontWeight: 600, fontSize: 12, padding: "4px 10px", borderRadius: 14, cursor: "pointer" }}>
                      <Scissors size={11} /> + Dienstleistungen zuordnen
                    </button>
                  </div>
                )}
                {!emp.isOwner && (() => {
                  const MANAGER_PAGE_OPTIONS = [
                    { id: "dashboard", label: "Dashboard" },
                    { id: "calendar", label: "Kalender" },
                    { id: "bookings", label: "Termine" },
                    { id: "customers", label: "Kunden (CRM)" },
                    { id: "services", label: "Dienstleistungen" },
                    { id: "employees", label: "Mitarbeiter" },
                    { id: "ai-calls", label: "KI-Anrufe" },
                    { id: "messages", label: "SMS & Nachrichten" },
                    { id: "online-booking", label: "Online-Buchung" },
                    { id: "shifts", label: "Schichtplan" },
                    { id: "payments", label: "Zahlungen" },
                    { id: "vouchers", label: "Gutscheine" },
                    { id: "accounting", label: "Buchhaltung" },
                    { id: "google", label: "Google Business" },
                  ];
                  const togglePage = async (pageId) => {
                    const current = emp.managerPages || [];
                    const updated = current.includes(pageId) ? current.filter(p => p !== pageId) : [...current, pageId];
                    await api.updateEmployee(emp.id, { manager_pages: updated });
                    await reloadEmployees();
                  };
                  return (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${COLORS.border}` }}>
                      {/* EMP_DEACTIVATE_v2 (Toby 21.07.) -- Aktiv-Switch im Salonleitung-Stil, direkt über „Salonleitung".
                          Ausschalten = deaktivieren (Karte wandert in die Liste „Deaktivierte Mitarbeiter" unten). */}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                        <span style={{ fontSize: 12, color: COLORS.success, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                          <UserCheck size={13} /> Aktiv
                        </span>
                        <button onClick={() => handleDeactivate(emp)}
                          title="Ausschalten = deaktivieren (z.B. Schwangerschaft/Auszeit) — unten jederzeit wieder aktivierbar"
                          style={{
                            width: 40, height: 22, borderRadius: 11, border: "none", cursor: "pointer",
                            background: COLORS.success, position: "relative", transition: "background 0.2s"
                          }}>
                          <div style={{
                            width: 18, height: 18, borderRadius: "50%", background: "var(--card)",
                            position: "absolute", top: 2, left: 20,
                            transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
                          }} />
                        </button>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 12, color: emp.isManager ? COLORS.primary : COLORS.textLight, fontWeight: emp.isManager ? 600 : 400, display: "flex", alignItems: "center", gap: 4 }}>
                          <Shield size={13} /> Salonleitung
                        </span>
                        <button onClick={async () => {
                          await api.updateEmployee(emp.id, { is_manager: !emp.isManager, manager_pages: !emp.isManager ? ["dashboard","calendar","bookings","customers","shifts"] : [] });
                          await reloadEmployees();
                        }} style={{
                          width: 40, height: 22, borderRadius: 11, border: "none", cursor: "pointer",
                          background: emp.isManager ? COLORS.primary : "#D1D5DB",
                          position: "relative", transition: "background 0.2s"
                        }}>
                          <div style={{
                            width: 18, height: 18, borderRadius: "50%", background: "var(--card)",
                            position: "absolute", top: 2,
                            left: emp.isManager ? 20 : 2,
                            transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
                          }} />
                        </button>
                      </div>
                      <div style={{ marginTop: 10 }}>
                        <span style={{ fontSize: 11, color: COLORS.textLight, fontWeight: 500, marginBottom: 6, display: "block" }}>Zugänge</span>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px" }}>
                          {MANAGER_PAGE_OPTIONS.map(opt => {
                            const checked = (emp.managerPages || []).includes(opt.id);
                            return (
                              <label key={opt.id} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: checked ? COLORS.text : COLORS.textLight, cursor: "pointer", padding: "3px 0" }}>
                                <input type="checkbox" checked={checked} onChange={() => togglePage(opt.id)}
                                  style={{ accentColor: COLORS.primary, width: 13, height: 13, cursor: "pointer" }} />
                                {opt.label}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </Card>
            );
          })}
        </div>
      )}

      {/* EMP_DEACTIVATE_v1 -- Liste deaktivierter Mitarbeiter + Wieder-Aktivieren */}
      {activeTab === "team" && inactiveEmps.length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700 }}>Deaktivierte Mitarbeiter</h3>
          <p style={{ margin: "0 0 10px", fontSize: 12, color: COLORS.textLight }}>
            Nicht im Kalender, kein Schichtplan nötig, online und über KI-Telefonie nicht buchbar — Termine und Statistiken bleiben erhalten.
          </p>
          {inactiveEmps.map(e => (
            <div key={e.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${COLORS.border}`, fontSize: 13 }}>
              <span><strong>{e.name}</strong>{e.role_title ? <span style={{ color: COLORS.textLight }}> — {e.role_title}</span> : null}</span>
              {/* EMP_DEACTIVATE_v2 -- gleicher Switch-Stil wie auf der Karte, hier im Aus-Zustand; Einschalten = wieder aktivieren */}
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, color: COLORS.textLight }}>Wieder aktivieren</span>
                <button onClick={() => handleReactivate({ id: e.id, name: e.name })} title="Einschalten = wieder aktivieren"
                  style={{
                    width: 40, height: 22, borderRadius: 11, border: "none", cursor: "pointer",
                    background: "#D1D5DB", position: "relative", transition: "background 0.2s"
                  }}>
                  <div style={{
                    width: 18, height: 18, borderRadius: "50%", background: "var(--card)",
                    position: "absolute", top: 2, left: 2,
                    transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
                  }} />
                </button>
              </span>
            </div>
          ))}
        </Card>
      )}

      {/* TIER_PRICING_UI_v1 -- TAB: Stylist-Preise (Tier-Pricing pro Mitarbeiter-Klasse) */}
      {activeTab === "tiers" && <TierPricingTab employees={employees} services={services} />}

      {/* TAB: Abzeichen */}
      {activeTab === "badges" && (() => {
        const BadgesTab = () => {
          const [badgeSettings, setBadgeSettings] = useState(null);
          const [loading, setLoading] = useState(true);
          const [saving, setSaving] = useState(false);
          const [calculating, setCalculating] = useState(false);
          const [calcResult, setCalcResult] = useState(null);

          useEffect(() => {
            (async () => {
              const res = await api.getBadgeSettings();
              if (res.ok) setBadgeSettings(res.data);
              else setBadgeSettings({ revenue: { enabled: true, service_ids: [] }, hours: { enabled: true, service_ids: [] }, beards: { enabled: true, service_ids: [] }, color: { enabled: true, service_ids: [] } });
              setLoading(false);
            })();
          }, []);

          // BADGE_TOGGLE_AUTOSAVE_v1 -- Toggle macht jetzt direkt API-Call statt nur lokales State.
          // Vorher: Inline-Component BadgesTab mit lokalem State + manuellem "Speichern"-Button →
          // bei Parent-Re-Render (Auto-Refresh alle 45s) wurde BadgesTab als "neue Component" gesehen,
          // unmount/remount, useEffect lädt Server-State neu, Toggle springt zurück auf alten Wert.
          // Lösung: optimistic update + sofortiger PUT. Server ist Single Source of Truth.
          const persistBadgeSettings = async (next) => {
            setBadgeSettings(next);  // optimistic
            const res = await api.updateBadgeSettings(next);
            if (!res || !res.ok) {
              // Rollback bei Fehler — Server-State erneut holen
              const reload = await api.getBadgeSettings();
              if (reload && reload.ok) setBadgeSettings(reload.data);
              alert("Abzeichen-Einstellung konnte nicht gespeichert werden. Wurde zurückgesetzt.");
            }
          };

          const toggleBadge = (type) => {
            const next = { ...badgeSettings, [type]: { ...badgeSettings[type], enabled: !badgeSettings[type].enabled } };
            persistBadgeSettings(next);
          };

          const toggleService = (badgeType, serviceId) => {
            const current = (badgeSettings[badgeType] && badgeSettings[badgeType].service_ids) || [];
            const updated = current.includes(serviceId) ? current.filter(id => id !== serviceId) : [...current, serviceId];
            const next = { ...badgeSettings, [badgeType]: { ...badgeSettings[badgeType], service_ids: updated } };
            persistBadgeSettings(next);
          };

          const handleSave = async () => {
            // BADGE_TOGGLE_AUTOSAVE_v1: Speichern-Button überflüssig geworden — bleibt für Notfälle
            // (z.B. wenn jemand explizit nochmal speichern will). Effekt: gleicher PUT.
            setSaving(true);
            await api.updateBadgeSettings(badgeSettings);
            setSaving(false);
          };

          const handleCalculate = async () => {
            setCalculating(true);
            const res = await api.calculateBadges();
            if (res.ok) setCalcResult(res.data);
            setCalculating(false);
          };

          if (loading) return React.createElement("div", { style: { textAlign: "center", padding: 40, color: COLORS.textLight } }, "Lade Badge-Einstellungen...");

          const badgeTypes = [
            { type: "revenue", title: "Umsatz-König", desc: "Wer den höchsten Umsatz im Monat macht", needsServices: false },
            { type: "hours", title: "Stunden-Champion", desc: "Wer die meisten Stunden aus Terminen hat", needsServices: false },
            { type: "beards", title: "Bart-Meister", desc: "Wer die meisten Bart-Services durchführt", needsServices: true },
            { type: "color", title: "Farb-Künstler", desc: "Wer die meisten Farb-Services durchführt", needsServices: true },
          ];

          return React.createElement("div", null,
            React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 } },
              React.createElement("div", null,
                React.createElement("p", { style: { margin: 0, fontSize: 13, color: COLORS.textLight } }, "Badges werden automatisch am Monatsanfang für den Vormonat vergeben. Der Mitarbeiter mit dem höchsten Wert in jeder Kategorie gewinnt.")
              ),
              React.createElement("div", { style: { display: "flex", gap: 8 } },
                React.createElement(Btn, { variant: "secondary", onClick: handleCalculate, disabled: calculating },
                  calculating ? "Berechne..." : "Jetzt berechnen"
                ),
                React.createElement(Btn, { onClick: handleSave, disabled: saving },
                  saving ? "Speichere..." : "Einstellungen speichern"
                )
              )
            ),

            calcResult && React.createElement(Card, { style: { marginBottom: 16, background: `${COLORS.success}08`, border: `1px solid ${COLORS.success}30` } },
              React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 8 } },
                React.createElement(CheckCircle, { size: 16, color: COLORS.success }),
                React.createElement("strong", { style: { fontSize: 14 } }, calcResult.message)
              ),
              calcResult.awards && calcResult.awards.length > 0
                ? React.createElement("div", { style: { display: "flex", gap: 12, flexWrap: "wrap" } },
                    calcResult.awards.map(a => {
                      const emp = employees.find(e => e.id === a.employee_id);
                      const conf = BADGE_CONFIG[a.badge_type];
                      return React.createElement("div", { key: a.badge_type, style: { display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 8, background: "var(--card)" } },
                        React.createElement(BadgeSVG, { type: a.badge_type, size: 28 }),
                        React.createElement("span", { style: { fontSize: 13 } },
                          React.createElement("strong", null, conf?.label), " → ", emp?.name || "?", " (", typeof a.value === "number" && a.value % 1 !== 0 ? a.value.toFixed(1) : a.value, a.badge_type === "revenue" ? " €" : a.badge_type === "hours" ? " Std." : "x", ")"
                        )
                      );
                    })
                  )
                : React.createElement("p", { style: { margin: 0, fontSize: 13, color: COLORS.textLight } }, "Keine Daten für den Vormonat gefunden.")
            ),

            React.createElement("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 16 } },
              badgeTypes.map(bt => {
                const conf = badgeSettings[bt.type] || { enabled: true, service_ids: [] };
                return React.createElement(Card, { key: bt.type, style: { border: conf.enabled ? `2px solid ${BADGE_CONFIG[bt.type].color}30` : `1px solid ${COLORS.border}`, opacity: conf.enabled ? 1 : 0.6 } },
                  React.createElement("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 } },
                    React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 10 } },
                      React.createElement(BadgeSVG, { type: bt.type, size: 40, showTooltip: false }),
                      React.createElement("div", null,
                        React.createElement("h3", { style: { margin: 0, fontSize: 15, fontWeight: 700 } }, bt.title),
                        React.createElement("p", { style: { margin: "2px 0 0", fontSize: 12, color: COLORS.textLight } }, bt.desc)
                      )
                    ),
                    React.createElement("button", {
                      onClick: () => toggleBadge(bt.type),
                      style: {
                        width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer",
                        background: conf.enabled ? BADGE_CONFIG[bt.type].color : "#D1D5DB",
                        position: "relative", transition: "background 0.2s", flexShrink: 0
                      }
                    },
                      React.createElement("div", { style: {
                        width: 20, height: 20, borderRadius: "50%", background: "var(--card)",
                        position: "absolute", top: 2, left: conf.enabled ? 22 : 2,
                        transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
                      } })
                    )
                  ),

                  bt.needsServices && conf.enabled && React.createElement("div", { style: { marginTop: 8, paddingTop: 12, borderTop: `1px solid ${COLORS.border}` } },
                    React.createElement("span", { style: { fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 8, display: "block" } },
                      bt.type === "beards" ? "Welche Services zählen als \"Bart\"?" : "Welche Services zählen als \"Farbe\"?"
                    ),
                    React.createElement("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px" } },
                      services.map(svc =>
                        React.createElement("label", { key: svc.id, style: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer", padding: "3px 0", color: (conf.service_ids || []).includes(svc.id) ? COLORS.text : COLORS.textLight } },
                          React.createElement("input", {
                            type: "checkbox",
                            checked: (conf.service_ids || []).includes(svc.id),
                            onChange: () => toggleService(bt.type, svc.id),
                            style: { accentColor: BADGE_CONFIG[bt.type].color, width: 14, height: 14, cursor: "pointer" }
                          }),
                          svc.name, " (", svc.category, ")"
                        )
                      )
                    )
                  )
                );
              })
            ),

            React.createElement(Card, { style: { marginTop: 16, background: `${COLORS.primary}05` } },
              React.createElement("h3", { style: { margin: "0 0 8px", fontSize: 14, fontWeight: 700 } }, "So funktionieren die Abzeichen"),
              React.createElement("div", { style: { fontSize: 13, color: COLORS.textLight, lineHeight: 1.7 } },
                React.createElement("p", { style: { margin: "0 0 4px" } }, "Am Monatsanfang werden automatisch die Abzeichen des Vormonats vergeben."),
                React.createElement("p", { style: { margin: "0 0 4px" } }, "Der Mitarbeiter mit dem höchsten Wert in jeder Kategorie bekommt das Abzeichen."),
                React.createElement("p", { style: { margin: "0 0 4px" } }, "Abzeichen sind für einen Monat sichtbar — im Kalender und bei der Mitarbeiter-Übersicht."),
                React.createElement("p", { style: { margin: 0 } }, "Ein Mitarbeiter kann mehrere Abzeichen gleichzeitig haben!")
              )
            )
          );
        };
        return React.createElement(BadgesTab);
      })()}

      {/* TAB: Provisionen */}
      {activeTab === "provision" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: window.innerWidth < 768 ? 8 : 16, marginBottom: window.innerWidth < 768 ? 12 : 20 }}>
            <StatCard icon={Euro} label="Gesamtumsatz Team" value={`${Object.values(safeMonthlyData).reduce((s, d) => s + (d?.revenue || 0), 0).toLocaleString("de-DE")} €`} color={COLORS.primary} />
            <StatCard icon={TrendingUp} label="Provisionen gesamt" value={`${employees.reduce((s, e) => s + calcCommission(e.id).total, 0).toLocaleString("de-DE")} €`} color={COLORS.success} />
            <StatCard icon={Zap} label="Boni gesamt" value={`${employees.reduce((s, e) => s + calcCommission(e.id).bonus, 0).toLocaleString("de-DE")} €`} color={COLORS.accent} />
            <StatCard icon={Users} label="Ø pro Mitarbeiter" value={`${Math.round(employees.reduce((s, e) => s + calcCommission(e.id).total, 0) / employees.length).toLocaleString("de-DE")} €`} color={COLORS.info} />
          </div>

          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Provisionsübersicht — {selectedMonth}</h3>
              <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13 }} />
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                  {["Mitarbeiter", "Umsatz", "Provisionssatz", "Grundprovision", "Bonus-Schwelle", "Bonus", "Gesamt"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: COLORS.textLight, fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.map(emp => {
                  const data = safeMonthlyData[emp.id];
                  const conf = commissions[emp.id];
                  const comm = calcCommission(emp.id);
                  return (
                    <tr key={emp.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: "12px 10px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 8, height: 8, borderRadius: "50%", background: emp.color }} />
                          <strong>{emp.name}</strong>
                        </div>
                      </td>
                      <td style={{ padding: "12px 10px", fontWeight: 700 }}>{(data?.revenue || 0).toLocaleString("de-DE")} €</td>
                      <td style={{ padding: "12px 10px" }}>{(conf?.rate || 0)}%</td>
                      <td style={{ padding: "12px 10px" }}>{comm.base.toLocaleString("de-DE")} €</td>
                      <td style={{ padding: "12px 10px" }}>
                        {(data?.revenue || 0) >= (conf?.bonusThreshold || 0) ? (
                          <Badge text={`${(conf?.bonusThreshold || 0).toLocaleString("de-DE")} € erreicht`} color={COLORS.success} />
                        ) : (
                          <span style={{ color: COLORS.textLight }}>noch {((conf?.bonusThreshold || 0) - (data?.revenue || 0)).toLocaleString("de-DE")} €</span>
                        )}
                      </td>
                      <td style={{ padding: "12px 10px", fontWeight: 600, color: comm.bonus > 0 ? COLORS.success : COLORS.textLight }}>
                        {comm.bonus > 0 ? `+${comm.bonus.toLocaleString("de-DE")} €` : "—"}
                      </td>
                      <td style={{ padding: "12px 10px", fontWeight: 700, color: COLORS.primary, fontSize: 14 }}>
                        {comm.total.toLocaleString("de-DE")} €
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: `${COLORS.primary}08`, fontWeight: 700 }}>
                  <td style={{ padding: "12px 10px" }}>Gesamt</td>
                  <td style={{ padding: "12px 10px" }}>{Object.values(safeMonthlyData).reduce((s, d) => s + (d?.revenue || 0), 0).toLocaleString("de-DE")} €</td>
                  <td></td>
                  <td style={{ padding: "12px 10px" }}>{employees.reduce((s, e) => s + calcCommission(e.id).base, 0).toLocaleString("de-DE")} €</td>
                  <td></td>
                  <td style={{ padding: "12px 10px", color: COLORS.success }}>{employees.reduce((s, e) => s + calcCommission(e.id).bonus, 0).toLocaleString("de-DE")} €</td>
                  <td style={{ padding: "12px 10px", color: COLORS.primary, fontSize: 14 }}>{employees.reduce((s, e) => s + calcCommission(e.id).total, 0).toLocaleString("de-DE")} €</td>
                </tr>
              </tfoot>
            </table>
          </Card>

          <Card style={{ marginTop: 16 }}>
            <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Provisionsregeln bearbeiten</h3>
            {/* OWNER_SAVE_AUDIT_v1 -- vorher defaultValue-Felder + Button ohne Speichern (Mock).
                Der Provisionssatz wird pro Mitarbeiter gespeichert (commission_rate) -> dort bearbeiten. */}
            <p style={{ fontSize: 13, color: COLORS.textLight, lineHeight: 1.7, margin: 0 }}>
              Den <strong>Provisionssatz (%)</strong> stellst du direkt beim Mitarbeiter ein:
              <strong> Mitarbeiter → Name antippen → Bearbeiten</strong>. Dort wird er gespeichert und
              fließt in die Berechnung oben ein.
            </p>
          </Card>
        </div>
      )}

      {/* TAB: Trinkgeld */}
      {activeTab === "tips" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: window.innerWidth < 768 ? 8 : 16, marginBottom: window.innerWidth < 768 ? 12 : 20 }}>
            <StatCard icon={Star} label="Trinkgeld gesamt" value={`${Object.values(safeMonthlyData).reduce((s, d) => s + (d?.tips || 0), 0)} €`} color={COLORS.accent} />
            <StatCard icon={CreditCard} label="Davon Stripe" value={`${Object.values(safeMonthlyData).reduce((s, d) => s + d.tipStripe, 0)} €`} color={COLORS.info} />
            <StatCard icon={Euro} label="Davon Bar" value={`${Object.values(safeMonthlyData).reduce((s, d) => s + d.tipCash, 0)} €`} color={COLORS.success} />
            <StatCard icon={Users} label="Ø pro Mitarbeiter" value={`${Math.round(Object.values(safeMonthlyData).reduce((s, d) => s + (d?.tips || 0), 0) / employees.length)} €`} color={COLORS.primary} />
          </div>

          <Card>
            <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Trinkgeld pro Mitarbeiter — {selectedMonth}</h3>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                  {["Mitarbeiter", "Stripe (Karte)", "Bar", "Gesamt", "Ø pro Service", "Anteil am Team"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: COLORS.textLight, fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.map(emp => {
                  const data = safeMonthlyData[emp.id];
                  const totalTips = Object.values(safeMonthlyData).reduce((s, d) => s + (d?.tips || 0), 0);
                  const pct = Math.round(((data?.tips || 0) / totalTips) * 100);
                  return (
                    <tr key={emp.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: "12px 10px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 8, height: 8, borderRadius: "50%", background: emp.color }} />
                          <strong>{emp.name}</strong>
                        </div>
                      </td>
                      <td style={{ padding: "12px 10px" }}>{(data?.tipStripe || 0)} € <span style={{ color: COLORS.textLight }}>(Stripe)</span></td>
                      <td style={{ padding: "12px 10px" }}>{(data?.tipCash || 0)} € <span style={{ color: COLORS.textLight }}>(Bar)</span></td>
                      <td style={{ padding: "12px 10px", fontWeight: 700, color: COLORS.accent }}>{(data?.tips || 0)} €</td>
                      <td style={{ padding: "12px 10px" }}>{((data?.tips || 0) / (data?.services || 0)).toFixed(2)} €</td>
                      <td style={{ padding: "12px 10px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 80, height: 6, borderRadius: 3, background: COLORS.border }}>
                            <div style={{ width: `${pct}%`, height: "100%", borderRadius: 3, background: emp.color }} />
                          </div>
                          <span style={{ fontSize: 11 }}>{pct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>

          <Card style={{ marginTop: 16 }}>
            <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Trinkgeld-Einstellungen</h3>
            <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: 16 }}>
              <div>
                <h4 style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 600 }}>Stripe-Trinkgeld</h4>
                <div style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 10, padding: 10, background: `${COLORS.info}08`, borderRadius: 8 }}>
                  Trinkgeld per Kartenzahlung wird automatisch über die Stripe Terminal API erfasst. Der Kunde kann beim Bezahlen einen Betrag hinzufügen. Dieser wird dem jeweiligen Mitarbeiter zugeordnet.
                </div>
                <Select label="Trinkgeld-Vorschläge" options={[
                  { value: "percent", label: "Prozentsätze: 10%, 15%, 20%" },
                  { value: "fixed", label: "Festbeträge: 2€, 5€, 10€" },
                  { value: "both", label: "Beides anzeigen" },
                ]} />
              </div>
              <div>
                <h4 style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 600 }}>Bar-Trinkgeld</h4>
                <div style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 10, padding: 10, background: `${COLORS.accent}08`, borderRadius: 8 }}>
                  Bar-Trinkgeld wird manuell pro Termin beim Kassieren erfasst. Der Mitarbeiter oder die Rezeption trägt den Betrag ein.
                </div>
                <Select label="Erfassung" options={[
                  { value: "checkout", label: "Beim Checkout eingeben" },
                  { value: "end-of-day", label: "Am Tagesende gesammelt eintragen" },
                ]} />
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <Btn><Check size={14} /> Einstellungen speichern</Btn>
            </div>
          </Card>
        </div>
      )}

      {/* TAB: Monatsabrechnung */}
      {activeTab === "settlement" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13 }} />
              <Badge text="Entwurf" color={COLORS.warning} />
            </div>
            {/* SETTLEMENT_DUMMY_BUTTONS_OFF_v1 (08.09.2026, Toby: "export-knöpfe ausblenden") -- "PDF Export",
                "DATEV Export" und "Abrechnung freigeben" hatten nie eine Funktion (kein onClick). Ausgeblendet,
                bis es die Funktion gibt; Wiederherstellen = Block unten wieder einblenden.
            <div style={{ display: "flex", gap: 8 }}>
              <Btn variant="secondary"><Download size={14} /> PDF Export</Btn>
              <Btn variant="secondary"><Download size={14} /> DATEV Export</Btn>
              <Btn variant="success"><Check size={14} /> Abrechnung freigeben</Btn>
            </div> */}
          </div>

          <Card>
            <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Monatsabrechnung Mitarbeiter — {selectedMonth}</h3>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                  {["Mitarbeiter", "Stunden", "Stundenlohn", "Grundgehalt", "Provision", "Bonus", "Trinkgeld", "Gesamtvergütung"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: COLORS.textLight, fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.map(emp => {
                  const data = safeMonthlyData[emp.id];
                  const comm = calcCommission(emp.id);
                  const baseSalary = (data?.hours || 0) * (emp?.hourlyRate || 0);
                  const total = baseSalary + comm.total + (data?.tips || 0);
                  return (
                    <tr key={emp.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: "12px 10px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 8, height: 8, borderRadius: "50%", background: emp.color }} />
                          <strong>{emp.name}</strong>
                        </div>
                      </td>
                      <td style={{ padding: "12px 10px" }}>{(data?.hours || 0)} Std.</td>
                      <td style={{ padding: "12px 10px" }}>{(emp?.hourlyRate || 0)} €</td>
                      <td style={{ padding: "12px 10px" }}>{baseSalary.toLocaleString("de-DE")} €</td>
                      <td style={{ padding: "12px 10px" }}>{comm.base.toLocaleString("de-DE")} €</td>
                      <td style={{ padding: "12px 10px", color: comm.bonus > 0 ? COLORS.success : COLORS.textLight }}>
                        {comm.bonus > 0 ? `+${comm.bonus.toLocaleString("de-DE")} €` : "—"}
                      </td>
                      <td style={{ padding: "12px 10px" }}>{(data?.tips || 0)} €</td>
                      <td style={{ padding: "12px 10px", fontWeight: 700, color: COLORS.primary, fontSize: 14 }}>
                        {total.toLocaleString("de-DE")} €
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: `${COLORS.primary}08`, fontWeight: 700 }}>
                  <td style={{ padding: "12px 10px" }}>Gesamt</td>
                  <td style={{ padding: "12px 10px" }}>{Object.values(safeMonthlyData).reduce((s, d) => s + (d?.hours || 0), 0)} Std.</td>
                  <td></td>
                  <td style={{ padding: "12px 10px" }}>{employees.reduce((s, e) => s + safeMonthlyData[e.id].hours * e.hourlyRate, 0).toLocaleString("de-DE")} €</td>
                  <td style={{ padding: "12px 10px" }}>{employees.reduce((s, e) => s + calcCommission(e.id).base, 0).toLocaleString("de-DE")} €</td>
                  <td style={{ padding: "12px 10px", color: COLORS.success }}>{employees.reduce((s, e) => s + calcCommission(e.id).bonus, 0).toLocaleString("de-DE")} €</td>
                  <td style={{ padding: "12px 10px" }}>{Object.values(safeMonthlyData).reduce((s, d) => s + (d?.tips || 0), 0)} €</td>
                  <td style={{ padding: "12px 10px", color: COLORS.primary, fontSize: 14 }}>
                    {employees.reduce((s, e) => {
                      const data = safeMonthlyData[e.id]; const comm = calcCommission(e.id);
                      return s + ((data?.hours || 0) * e.hourlyRate) + comm.total + (data?.tips || 0);
                    }, 0).toLocaleString("de-DE")} €
                  </td>
                </tr>
              </tfoot>
            </table>
          </Card>
        </div>
      )}
    </div>
  );
};

// ============================================================
// PAGE: Schichtplanung
// ============================================================
export const ShiftsPage = ({ employees = [], currentUser }) => {
  const [viewMode, setViewMode] = useState("week"); // "week" | "month"
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  const [shifts, setShifts] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [loading, setLoading] = useState(true);
  // SALON_CLOSURE_v1 (Toby 14.09.) -- Betriebsurlaub: ganze Tage, salonweit
  const [closures, setClosures] = useState([]);
  const [closureModal, setClosureModal] = useState(false);
  const [clLabel, setClLabel] = useState("Betriebsurlaub");
  const [clFrom, setClFrom] = useState("");
  const [clTo, setClTo] = useState("");
  const [clBusy, setClBusy] = useState(false);
  const [clMsg, setClMsg] = useState(null); // {type:"ok"|"err", text, appointments?}
  const canEditClosures = !currentUser || currentUser.role === "owner" || currentUser.role === "admin";
  const [shiftModal, setShiftModal] = useState(null); // { empId, day, existing? }
  const [vacModal, setVacModal] = useState(false);
  // SHIFT_TEMPLATE_v1 (3.2) -- Wochen-Vorlage anwenden
  const [tplModal, setTplModal] = useState(false);
  // SHIFT_OVERRIDE_UI_v1 (3.3) -- ad-hoc Krank/Verspaetung
  const [ovModal, setOvModal] = useState(false);
  const [overrides, setOverrides] = useState([]);
  // VACATION_MODAL_STATE_FIX_v1 -- State auf ShiftsPage-Level damit Auto-Refresh
  // (CALENDAR_AUTOREFRESH_v1 alle 45s) nicht den VacationModal-State zurücksetzt.
  // Vorher: bei jedem AppRoot-Re-Render wurde VacationModal als neue Component
  // gesehen → unmount/remount → empId sprang auf Julia, fromDate auf heute zurück.
  const [vacEmpId, setVacEmpId] = useState(0);
  const [vacType, setVacType] = useState("Urlaub");
  const [vacFromDate, setVacFromDate] = useState("");
  const [vacToDate, setVacToDate] = useState("");
  const [vacSaving, setVacSaving] = useState(false);
  const [vacEditId, setVacEditId] = useState(null); // VACATION_EDIT_v1 -- gesetzt = Modal bearbeitet bestehenden Urlaub
  // VACATION_ENTITLEMENT_v1 + OWNER_PIN_v1 -- Urlaubskonto, Vorschau im Urlaubs-Fenster, PIN bei Ueberschreitung
  const [vacBal, setVacBal] = useState(null);
  const [vacPreview, setVacPreview] = useState(null);
  const [vacPinOpen, setVacPinOpen] = useState(false);
  const [vacPinErr, setVacPinErr] = useState("");
  const [entEdit, setEntEdit] = useState(null);
  const vacOwnerish = !!(currentUser && (currentUser.role === "owner" || currentUser.role === "admin"));
  // MY_VACATION_v1 -- Mitarbeiter-Login bekommt vom Server nur die eigene Zeile („Mein Urlaub")
  const vacCanSeeBal = vacOwnerish || !!(currentUser && currentUser.role === "employee");
  const [vacOpenEmp, setVacOpenEmp] = useState(null); // Urlaubskonto: aufgeklappte Person (Inhaber-Sicht)
  // SHIFT_MODAL_STATE_FIX_v1 -- gleicher Pattern wie VACATION_MODAL_STATE_FIX_v1.
  // Vorher: ShiftModal war als Inline-Component in ShiftsPage definiert mit eigenem
  // useState. Bei jedem Auto-Refresh (CALENDAR_AUTOREFRESH_v1 alle 45s) wurde ShiftsPage
  // re-rendered → ShiftModal als „neue Komponente" gesehen → unmount/remount → State-Reset.
  // Konkret: Julia tippt 09:30 als Startzeit, nimmt 30 Sek zum Nachdenken zur Pause,
  // Auto-Refresh feuert → Startzeit springt zurück auf existing oder 09:00. Sehr nervig.
  // Fix: State hier auf ShiftsPage-Level. useEffect (s.u.) synct die Felder beim Öffnen
  // des Modals aus dem `existing`-Eintrag.
  const [shiftFormStart, setShiftFormStart] = useState("09:00");
  const [shiftFormEnd, setShiftFormEnd] = useState("17:00");
  const [shiftFormIsOff, setShiftFormIsOff] = useState(false);
  const [shiftFormBreakStart, setShiftFormBreakStart] = useState("");
  const [shiftFormBreakEnd, setShiftFormBreakEnd] = useState("");
  const [shiftFormBiweekly, setShiftFormBiweekly] = useState(false); // BIWEEKLY_SHIFT_v1
  // SHIFT_ONCE_v1 (Toby 22.09.2026) -- "Nur am TT.MM." = einmalige Aenderung, Wochenregel bleibt; vorausgewaehlt
  const [shiftFormOnce, setShiftFormOnce] = useState(true);
  const [shiftFormSaving, setShiftFormSaving] = useState(false);

  const loadData = async () => {
    // SHIFT_OVERRIDE_UI_v1 -- overrides parallel mitladen
    const [sRes, vRes, oRes, cRes] = await Promise.all([
      api.getShifts(),
      api.getVacations(),
      api.request("/shift-overrides"),
      api.request("/salon-closures"),  // SALON_CLOSURE_v1
    ]);
    if (cRes && cRes.ok && Array.isArray(cRes.data)) setClosures(cRes.data);
    if (sRes.ok) setShifts(sRes.data);
    if (vRes.ok) setVacations(vRes.data);
    if (oRes && oRes.ok) setOverrides(Array.isArray(oRes.data) ? oRes.data : []);
    setLoading(false);
    // VACATION_ENTITLEMENT_v1 -- Urlaubskonto nicht-blockierend nachladen (Endpunkt nur Inhaber/Admin)
    if (vacCanSeeBal) api.getVacationBalance(new Date().getFullYear()).then(r => { if (r && r.ok) setVacBal(r.data); });
  };
  useEffect(() => { loadData(); }, []);
  // VACATION_ENTITLEMENT_v1 -- Vorschau im Urlaubs-Fenster: dieselbe Rechnung wie beim Speichern (Server)
  useEffect(() => {
    if (!vacModal || !vacEmpId || !vacFromDate || !vacOwnerish) { setVacPreview(null); return; }
    let alive = true;
    const t = setTimeout(() => {
      api.previewVacation({ employee_id: vacEmpId, from: vacFromDate, to: vacToDate || vacFromDate, type: vacType, exclude_id: vacEditId || "" })
        .then(r => { if (alive) setVacPreview(r && r.ok ? r.data : null); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [vacModal, vacEmpId, vacFromDate, vacToDate, vacType, vacEditId]);

  const getWeekDates = (offset) => {
    const d = new Date(today); d.setDate(d.getDate() - d.getDay() + 1 + offset * 7);
    return Array.from({ length: 7 }, (_, i) => { const dd = new Date(d); dd.setDate(d.getDate() + i); return dd; });
  };
  const weekDates = getWeekDates(weekOffset);
  const dayNames = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
  const dayKeys = ["mo", "di", "mi", "do", "fr", "sa", "so"];

  // Feiertage Bayern - rollend 24 Monate ab heute (dynamisch)
  const baseHolidays = (() => {
    const out = []; const today = new Date(); today.setHours(0,0,0,0);
    const endD = new Date(today); endD.setMonth(endD.getMonth() + 24);
    const fmt = d => formatDate(d); // HOLIDAY_TZ_FIX_v1 -- lokale Zeit statt UTC: toISOString() schob bewegliche Feiertage (z.B. Fronleichnam) in DE einen Tag zu früh
    const addDays = (d,n) => { const r = new Date(d); r.setDate(r.getDate()+n); return r; };
    const easter = y => { const a=y%19,b=Math.floor(y/100),c=y%100,d=Math.floor(b/4),e=b%4,ff=Math.floor((b+8)/25),g=Math.floor((b-ff+1)/3); const h=(19*a+b-d-g+15)%30,i=Math.floor(c/4),k=c%4,l=(32+2*e+2*i-h-k)%7; const m=Math.floor((a+11*h+22*l)/451),mo=Math.floor((h+l-7*m+114)/31),da=((h+l-7*m+114)%31)+1; return new Date(y,mo-1,da); };
    for (let y = today.getFullYear(); y <= endD.getFullYear(); y++) {
      const e = easter(y);
      const items = [
        { date: `${y}-01-01`, name: "Neujahr" },
        { date: `${y}-01-06`, name: "Heilige Drei Könige" },
        { date: fmt(addDays(e,-2)), name: "Karfreitag" },
        { date: fmt(addDays(e,1)), name: "Ostermontag" },
        { date: `${y}-05-01`, name: "Tag der Arbeit" },
        { date: fmt(addDays(e,39)), name: "Christi Himmelfahrt" },
        { date: fmt(addDays(e,50)), name: "Pfingstmontag" },
        { date: fmt(addDays(e,60)), name: "Fronleichnam" },
        { date: `${y}-08-15`, name: "Mariä Himmelfahrt" },
        { date: `${y}-10-03`, name: "Tag der Dt. Einheit" },
        { date: `${y}-11-01`, name: "Allerheiligen" },
        { date: `${y}-12-25`, name: "1. Weihnachtstag" },
        { date: `${y}-12-26`, name: "2. Weihnachtstag" }
      ];
      const ts = fmt(today), es = fmt(endD);
      items.forEach(h => { if (h.date >= ts && h.date <= es) out.push(h); });
    }
    return out.sort((a,b) => a.date.localeCompare(b.date));
  })();
  // SALON_CLOSURE_v1 -- Betriebsurlaubs-Tage laufen ueber dieselbe Liste wie Feiertage: Kopfzeile zeigt die
  // Bezeichnung, Zellen sind gesperrt, Stunden werden nicht gezaehlt. Feiertag hat bei Doppelung Vorrang.
  const holidays = (() => {
    const out = [...baseHolidays];
    const seen = new Set(out.map(h => h.date));
    (closures || []).forEach(c => {
      const d = new Date(c.from_date + "T00:00:00"), end = new Date(c.to_date + "T00:00:00");
      let guard = 0;
      while (d <= end && guard++ < 400) {
        const ds = formatDate(d);
        if (!seen.has(ds)) { out.push({ date: ds, name: "🏖 " + (c.label || "Betriebsurlaub"), closure: true }); seen.add(ds); }
        d.setDate(d.getDate() + 1);
      }
    });
    return out.sort((a,b) => a.date.localeCompare(b.date));
  })();
  const fmtDe = (iso) => { const p = String(iso || "").split("-"); return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : iso; };
  const saveClosure = async () => {
    if (!clFrom || !clTo) { setClMsg({ type: "err", text: "Bitte Von- und Bis-Datum wählen." }); return; }
    setClBusy(true); setClMsg(null);
    const r = await api.request("/salon-closures", { method: "POST", body: JSON.stringify({ from_date: clFrom, to_date: clTo, label: clLabel }) });
    setClBusy(false);
    if (r && r.ok) {
      const n = (r.data && r.data.appointments_in_range) || 0;
      setClMsg({ type: "ok", text: n ? `Betriebsurlaub gespeichert. Achtung: In diesem Zeitraum liegen schon ${n} Termin${n === 1 ? "" : "e"} — die bleiben bestehen, bitte selbst mit den Kunden klären.` : "Betriebsurlaub gespeichert. In diesem Zeitraum sind keine Termine eingetragen.", appointments: (r.data && r.data.appointments) || [] });
      setClFrom(""); setClTo(""); setClLabel("Betriebsurlaub");
      loadData();
    } else {
      setClMsg({ type: "err", text: (r && r.data && r.data.error) || "Speichern fehlgeschlagen." });
    }
  };
  const deleteClosure = async (c) => {
    if (!window.confirm(`Betriebsurlaub „${c.label}" (${fmtDe(c.from_date)}–${fmtDe(c.to_date)}) löschen?\nDanach sind diese Tage wieder online buchbar.`)) return;
    const r = await api.request(`/salon-closures/${c.id}`, { method: "DELETE" });
    if (r && r.ok) { setClMsg(null); loadData(); } else alert((r && r.data && r.data.error) || "Löschen fehlgeschlagen.");
  };
  // SALON_CLOSURE_v1 -- als Funktionsaufruf rendern (SHIFT_MODAL_REMOUNT_FIX_v1: keine eigene Komponenten-Identitaet)
  const ClosureModal = () => {
    const todayIso = formatDate(today);
    const upcoming = (closures || []).filter(c => c.to_date >= todayIso);
    const iS = { width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, boxSizing: "border-box", background: "var(--card)", color: COLORS.text };
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={() => !clBusy && setClosureModal(false)}>
        <div style={{ background: "var(--card)", borderRadius: 16, padding: 22, width: Math.min(480, window.innerWidth - 32), maxHeight: "90vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
          <h2 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>🏖 Betriebsurlaub</h2>
          <p style={{ margin: "0 0 14px", fontSize: 12, color: COLORS.textLight, lineHeight: 1.5 }}>
            Der ganze Salon ist geschlossen: An diesen Tagen sind Online-Buchung, Zoey und KI-Telefon gesperrt, im Kalender und Schichtplan sind die Tage für alle Mitarbeiter grau. Kunden sehen auf der Buchungsseite 4 Wochen vorher einen Hinweis.
          </p>
          {upcoming.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Eingetragen</div>
              {upcoming.map(c => (
                <div key={c.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 10px", border: `1px solid ${COLORS.border}`, borderRadius: 8, marginBottom: 6, fontSize: 13 }}>
                  <div><strong>{c.label}</strong><div style={{ fontSize: 12, color: COLORS.textLight }}>{fmtDe(c.from_date)} – {fmtDe(c.to_date)}</div></div>
                  {canEditClosures && <Btn size="sm" variant="ghost" onClick={() => deleteClosure(c)}>Löschen</Btn>}
                </div>
              ))}
            </div>
          )}
          {canEditClosures ? (<>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Neu eintragen</div>
            <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Bezeichnung</label>
            <input type="text" style={{ ...iS, marginBottom: 10 }} value={clLabel} maxLength={60} placeholder="z. B. Weihnachten" onChange={e => setClLabel(e.target.value)} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div><label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Von (erster freier Tag)</label><input type="date" style={iS} value={clFrom} onChange={e => { setClFrom(e.target.value); if (!clTo || clTo < e.target.value) setClTo(e.target.value); }} /></div>
              <div><label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Bis (letzter freier Tag)</label><input type="date" style={iS} value={clTo} min={clFrom || undefined} onChange={e => setClTo(e.target.value)} /></div>
            </div>
          </>) : (
            <p style={{ fontSize: 12, color: COLORS.textLight }}>Betriebsurlaub kann nur die Inhaberin / der Inhaber eintragen.</p>
          )}
          {clMsg && (
            <div style={{ padding: "8px 10px", borderRadius: 8, marginBottom: 10, fontSize: 12, lineHeight: 1.5, background: clMsg.type === "ok" ? `${COLORS.success}15` : `${COLORS.danger}12`, color: clMsg.type === "ok" ? COLORS.text : COLORS.danger }}>
              {clMsg.text}
              {clMsg.appointments && clMsg.appointments.length > 0 && (
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {clMsg.appointments.slice(0, 15).map(a => <li key={a.id}>{fmtDe(a.date)} {a.start_time} · {a.employee || "?"}{a.customer ? " · " + a.customer : ""}</li>)}
                  {clMsg.appointments.length > 15 && <li>… und {clMsg.appointments.length - 15} weitere</li>}
                </ul>
              )}
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="secondary" style={{ flex: 1 }} onClick={() => setClosureModal(false)} disabled={clBusy}>Schließen</Btn>
            {canEditClosures && <Btn style={{ flex: 1 }} onClick={saveClosure} disabled={clBusy}>{clBusy ? "Speichere…" : "Betriebsurlaub speichern"}</Btn>}
          </div>
        </div>
      </div>
    );
  };

  // Helper: get shift for employee + day
  // BIWEEKLY_SHIFT_v1 -- ds (YYYY-MM-DD) optional: 14-Tage-Zeilen (week_parity 0/1) gelten
  // nur in ihrer Woche; existiert zusätzlich eine Wochen-Zeile, gewinnt die paritäts-spezifische.
  const getShift = (empId, dayKey, ds) => {
    const rows = shifts.filter(s => s.employee_id === empId && s.day_of_week === dayKey && (!ds || shiftAppliesOn(s, ds)));
    return rows.find(s => s.week_parity != null) || rows[0];
  };

  // Calculate weekly hours per employee (BIWEEKLY_SHIFT_v1: bezogen auf die ANGEZEIGTE Woche)
  const calcWeekHours = (empId) => {
    let mins = 0;
    dayKeys.forEach((day, i) => {
      const s = getShift(empId, day, weekDates[i] ? formatDate(weekDates[i]) : null);
      if (s && !s.is_off && s.start_time && s.end_time) {
        const [sh, sm] = s.start_time.split(":").map(Number);
        const [eh, em] = s.end_time.split(":").map(Number);
        mins += (eh * 60 + em) - (sh * 60 + sm);
      }
    });
    return Math.round(mins / 60 * 10) / 10;
  };

  // SHIFT_MODAL_STATE_FIX_v1 -- ShiftModal nutzt jetzt State auf ShiftsPage-Level.
  // useEffect synct beim Öffnen die Felder aus dem existierenden Shift-Eintrag.
  // Sync only re-runs wenn sich shiftModal selbst ändert (Öffnen/Schließen/anderes Feld) —
  // NICHT bei jedem Re-Render von ShiftsPage. So bleibt die Eingabe stabil.
  useEffect(() => {
    if (!shiftModal) return;
    // BIWEEKLY_SHIFT_v1 -- ds = konkretes Datum der angeklickten Zelle (für Paritäts-Zuordnung)
    const ex = getShift(shiftModal.empId, shiftModal.dayKey, shiftModal.ds);
    setShiftFormStart(ex && ex.start_time ? ex.start_time : "09:00");
    setShiftFormEnd(ex && ex.end_time ? ex.end_time : "17:00");
    setShiftFormIsOff(ex ? !!ex.is_off : false);
    setShiftFormBreakStart(ex && ex.break_start ? ex.break_start : "");
    setShiftFormBreakEnd(ex && ex.break_end ? ex.break_end : "");
    setShiftFormBiweekly(!!(ex && ex.week_parity != null));
    setShiftFormOnce(!!shiftModal.ds && !(ex && ex.week_parity != null)); // SHIFT_ONCE_v1: bei 2-Wochen-Rhythmus bleibt der Rhythmus gewaehlt
  }, [shiftModal && shiftModal.empId, shiftModal && shiftModal.dayKey, shiftModal && shiftModal.ds]);

  const handleShiftSave = async () => {
    if (!shiftModal) return;
    const { empId, dayKey, ds } = shiftModal;
    const existing = getShift(empId, dayKey, ds);
    setShiftFormSaving(true);
    // SHIFT_ONCE_v1 -- "Nur am …": vorhandene Einzeltag-Wege nutzen, Wochenregel (shifts) bleibt unangetastet.
    //   Frei -> Abwesenheit "Frei" fuer genau diesen Tag; Zeiten -> Schichtaenderung custom (Tag hat Schicht) bzw. extra (sonst).
    if (shiftFormOnce && ds) {
      try {
        let res;
        if (shiftFormIsOff) {
          res = await api.createVacation({ employee_id: empId, type: "Frei", from_date: ds, to_date: ds });
        } else {
          if (!shiftFormStart || !shiftFormEnd || shiftFormEnd <= shiftFormStart) { alert("Bitte gültige Von-/Bis-Zeiten eingeben."); return; }
          const hasRegular = existing && !existing.is_off && existing.start_time;
          res = await api.request("/shift-overrides", { method: "POST", body: JSON.stringify({
            employee_id: Number(empId), date: ds, override_type: hasRegular ? "custom" : "extra",
            start_time: shiftFormStart, end_time: shiftFormEnd, notes: "" }) });
        }
        if (!(res && res.ok)) { alert("Speichern fehlgeschlagen: " + ((res && res.data && res.data.error) || "Server-Fehler")); return; }
        await loadData();
        setShiftModal(null);
      } finally {
        setShiftFormSaving(false);
      }
      return;
    }
    // BIWEEKLY_SHIFT_v1 -- "Alle 2 Wochen": Parität aus dem angeklickten Datum
    // (= dieser Tag ist ein Arbeitstag, dann alle 14 Tage wieder). NULL = jede Woche.
    const weekParity = (!shiftFormIsOff && shiftFormBiweekly && ds) ? weekParityOf(ds) : null;
    const payload = {
      start_time: shiftFormIsOff ? null : shiftFormStart,
      end_time: shiftFormIsOff ? null : shiftFormEnd,
      is_off: shiftFormIsOff ? 1 : 0,
      break_start: !shiftFormIsOff && shiftFormBreakStart ? shiftFormBreakStart : null,
      break_end: !shiftFormIsOff && shiftFormBreakEnd ? shiftFormBreakEnd : null,
      week_parity: weekParity,
    };
    if (existing) {
      await api.updateShift(existing.id, payload);
    } else {
      await api.createShift({ employee_id: empId, day_of_week: dayKey, ...payload });
    }
    await loadData();
    setShiftModal(null);
    setShiftFormSaving(false);
  };

  const handleShiftDelete = async () => {
    if (!shiftModal) return;
    const existing = getShift(shiftModal.empId, shiftModal.dayKey, shiftModal.ds);
    if (!existing) return;
    setShiftFormSaving(true);
    await api.deleteShift(existing.id);
    await loadData();
    setShiftModal(null);
    setShiftFormSaving(false);
  };

  // SHIFT_TIME_ARROWS_v1 -- Pfeiltasten ↑/↓ verstellen die Uhrzeit (±5 Min), geräteunabhängig
  const stepTimeStr = (hhmm, deltaMin) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm || "");
    let total = m ? (parseInt(m[1], 10) * 60 + parseInt(m[2], 10)) : 0;
    total = ((total + deltaMin) % 1440 + 1440) % 1440;
    return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
  };
  const timeArrowKey = (e, val, setter) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      setter(stepTimeStr(val, e.key === "ArrowUp" ? 5 : -5));
    }
  };

  // VAC_CELL_EDIT_v1 (Toby 22.09.2026) -- Klick auf eine Zelle mit Abwesenheit (Frei/Urlaub/Krank …) oeffnet
  // dasselbe Bearbeiten-Fenster wie der Knopf in der Abwesenheiten-Liste. Feiertage/Betriebsurlaub bleiben gesperrt.
  const openVacEdit = (v) => {
    setVacEditId(v.id); setVacEmpId(v.employee_id); setVacType(v.type || "Urlaub");
    setVacFromDate(String(v.from_date).slice(0, 10)); setVacToDate(String(v.to_date).slice(0, 10)); setVacModal(true);
  };

  // ── Shift Modal (jetzt mit State von Parent, daher resistent gegen Auto-Refresh-Remounts) ──
  const ShiftModal = () => {
    if (!shiftModal) return null;
    const { empId, dayKey, ds } = shiftModal;
    const existing = getShift(empId, dayKey, ds);
    const emp = employees.find(e => e.id === empId);
    const dayLabel = { mo: "Montag", di: "Dienstag", mi: "Mittwoch", do: "Donnerstag", fr: "Freitag", sa: "Samstag", so: "Sonntag" }[dayKey];
    const onceLabel = ds ? `${ds.slice(8, 10)}.${ds.slice(5, 7)}.` : ""; // SHIFT_ONCE_v1
    // BIWEEKLY_SHIFT_v1 -- Beispiel-Termine für den 2-Wochen-Rhythmus ab dem angeklickten Datum
    const biweeklyExample = (() => {
      if (!ds) return "";
      try {
        const [y, m, d] = ds.split("-").map(Number);
        const dates = [0, 14, 28].map(off => { const dt = new Date(y, m - 1, d + off); return `${String(dt.getDate()).padStart(2, "0")}.${String(dt.getMonth() + 1).padStart(2, "0")}.`; });
        return dates.join(", ") + " …";
      } catch (_) { return ""; }
    })();

    return (
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
        onClick={e => { if (e.target === e.currentTarget) setShiftModal(null); }}>
        {/* SHIFT_ENTER_SAVE_v1 -- Enter in den Feldern speichert (Nutzer-Erwartung, Toby 13.07.).
            Buttons ausgenommen, damit Enter auf Abbrechen/Löschen nicht fälschlich speichert. */}
        <div style={{ background: "var(--card)", borderRadius: 16, padding: 24, width: "100%", maxWidth: 380, boxShadow: "0 25px 50px rgba(0,0,0,0.25)" }}
          onKeyDown={e => { if (e.key === "Enter" && e.target.tagName !== "BUTTON" && !shiftFormSaving) { e.preventDefault(); handleShiftSave(); } }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
              {shiftFormOnce && ds ? "Schicht nur für diesen Tag" : existing ? "Schicht bearbeiten" : "Schicht anlegen"}
            </h2>
            <button onClick={() => setShiftModal(null)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={18} /></button>
          </div>
          <div style={{ fontSize: 13, color: COLORS.textLight, marginBottom: 16 }}>
            <strong style={{ color: emp?.color }}>{emp?.name}</strong> — {dayLabel}{ds ? ", " + onceLabel : ""}
          </div>

          {/* BIWEEKLY_SHIFT_v1 + SHIFT_ONCE_v1 -- Rhythmus: nur dieser Tag, jede Woche oder alle 2 Wochen.
              Steht jetzt VOR "Frei", damit auch "nur an diesem Tag frei" geht. */}
          <div style={{ padding: 10, background: "var(--cardHover)", borderRadius: 8, marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textLight, textTransform: "uppercase", marginBottom: 6 }}>Rhythmus</div>
            <div style={{ display: "flex", gap: 6 }}>
              {[
                { k: "once", l: ds ? "Nur am " + onceLabel : "Nur dieser Tag", on: shiftFormOnce, click: () => setShiftFormOnce(true), ok: !!ds },
                { k: "week", l: "Jede Woche", on: !shiftFormOnce && !shiftFormBiweekly, click: () => { setShiftFormOnce(false); setShiftFormBiweekly(false); }, ok: true },
                { k: "bi", l: "Alle 2 Wochen", on: !shiftFormOnce && shiftFormBiweekly, click: () => { setShiftFormOnce(false); setShiftFormBiweekly(true); }, ok: !!ds },
              ].map(o => (
                <button key={o.k} onClick={o.click} disabled={!o.ok}
                  style={{ flex: 1, padding: "7px 6px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: o.ok ? "pointer" : "not-allowed",
                    border: `1px solid ${o.on ? COLORS.primary : COLORS.border}`,
                    background: o.on ? `${COLORS.primary}12` : "transparent",
                    color: o.on ? COLORS.primary : COLORS.textLight }}>
                  {o.l}
                </button>
              ))}
            </div>
            {shiftFormOnce && ds && (
              <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 6 }}>
                Gilt nur am {onceLabel} — die normale {dayLabel}-Schicht bleibt unverändert.
              </div>
            )}
            {!shiftFormOnce && shiftFormBiweekly && biweeklyExample && (
              <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 6 }}>
                Arbeitet {dayLabel && dayLabel.toLowerCase() === "samstag" ? "samstags" : dayLabel + "s"} am {biweeklyExample} (alle 14 Tage ab dem angeklickten Tag)
              </div>
            )}
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 16, cursor: "pointer" }}>
            <input type="checkbox" checked={shiftFormIsOff} onChange={e => setShiftFormIsOff(e.target.checked)} style={{ accentColor: COLORS.primary, width: 16, height: 16 }} />
            Frei / Kein Dienst
          </label>

          {!shiftFormIsOff && (
            <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4, display: "block" }}>Von</label>
                <input type="time" value={shiftFormStart} onChange={e => setShiftFormStart(e.target.value)} onKeyDown={e => timeArrowKey(e, shiftFormStart, setShiftFormStart)}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4, display: "block" }}>Bis</label>
                <input type="time" value={shiftFormEnd} onChange={e => setShiftFormEnd(e.target.value)} onKeyDown={e => timeArrowKey(e, shiftFormEnd, setShiftFormEnd)}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, boxSizing: "border-box" }} />
              </div>
            </div>


            {/* SHIFT_BREAKS_v1 (3.1) -- Pausenzeit (optional); SHIFT_ONCE_v1: Einzeltag kann (noch) keine Pause speichern */}
            {!(shiftFormOnce && ds) && <div style={{ padding: 10, background: "var(--cardHover)", borderRadius: 8, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textLight, textTransform: "uppercase", marginBottom: 6 }}>Pause (optional)</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, alignItems: "end" }}>
                <div>
                  <label style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 3, display: "block" }}>Pausen-Start</label>
                  <input type="time" value={shiftFormBreakStart} onChange={e => setShiftFormBreakStart(e.target.value)} onKeyDown={e => timeArrowKey(e, shiftFormBreakStart, setShiftFormBreakStart)}
                    style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13, boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 3, display: "block" }}>Pausen-Ende</label>
                  <input type="time" value={shiftFormBreakEnd} onChange={e => setShiftFormBreakEnd(e.target.value)} onKeyDown={e => timeArrowKey(e, shiftFormBreakEnd, setShiftFormBreakEnd)}
                    style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13, boxSizing: "border-box" }} />
                </div>
                {(shiftFormBreakStart || shiftFormBreakEnd) && (
                  <button onClick={() => { setShiftFormBreakStart(""); setShiftFormBreakEnd(""); }}
                    style={{ background: "transparent", border: "none", cursor: "pointer", color: COLORS.textLight, fontSize: 11, padding: 6 }} title="Pause entfernen">×</button>
                )}
              </div>
            </div>}
            </>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            {existing && !(shiftFormOnce && ds) && ( /* SHIFT_ONCE_v1: Loeschen betrifft die Wochenregel -> nur ohne "Nur am" */
              <Btn variant="danger" size="sm" onClick={handleShiftDelete} disabled={shiftFormSaving}>
                <Trash2 size={13} /> Löschen
              </Btn>
            )}
            <div style={{ flex: 1 }} />
            <Btn variant="secondary" size="sm" onClick={() => setShiftModal(null)}>Abbrechen</Btn>
            <Btn size="sm" onClick={handleShiftSave} disabled={shiftFormSaving}>
              {shiftFormSaving ? "..." : (existing ? "Speichern" : "Anlegen")}
            </Btn>
          </div>
        </div>
      </div>
    );
  };

  // ── Vacation Modal ── VACATION_MODAL_STATE_FIX_v1
  // State (vacEmpId, vacType, vacFromDate, vacToDate, vacSaving) ist auf ShiftsPage-Level
  // damit Auto-Refresh (CALENDAR_AUTOREFRESH_v1) den State nicht zurücksetzt.
  const handleVacSave = async (ownerPin) => {
    if (!vacEmpId) return;
    const _pin = typeof ownerPin === "string" ? ownerPin : null; // OWNER_PIN_v1 -- nie das Klick-Ereignis mitschicken
    // DATE_YEAR_PLAUSIBILITY_v1 (04.09.2026) -- Jahres-Tippfehler (z. B. 0027 statt 2027) sofort abfangen;
    // der Server prüft dieselbe Regel (Vorjahr bis heute+3) nochmal und antwortet sonst mit 400.
    const _thisYear = new Date().getFullYear();
    for (const [lbl, val] of [["Von-Datum", vacFromDate], ["Bis-Datum", vacToDate]]) {
      const y = parseInt(String(val || "").slice(0, 4), 10);
      if (val && !isNaN(y) && (y < _thisYear - 1 || y > _thisYear + 3)) {
        alert(`${lbl} ${val}: Jahr ${y} ist unplausibel (erlaubt ${_thisYear - 1} bis ${_thisYear + 3}). Bitte das Jahr prüfen.`);
        return;
      }
    }
    setVacSaving(true);
    try {
      // VACATION_EDIT_v1 -- vacEditId gesetzt = bestehenden Urlaub ändern statt neu anlegen
      const payload = { employee_id: vacEmpId, type: vacType, from_date: vacFromDate, to_date: vacToDate };
      if (_pin) payload.owner_pin = _pin;
      const res = vacEditId ? await api.updateVacation(vacEditId, payload) : await api.createVacation(payload);
      // OWNER_PIN_v1 -- mehr Urlaub als der Anspruch bei vergebener PIN: PIN abfragen statt Fehlermeldung
      const _code = res && res.data && res.data.code;
      if (!(res && res.ok) && _code === "pin_required") { setVacPinErr(""); setVacPinOpen(true); return; }
      if (!(res && res.ok) && _code && String(_code).startsWith("pin_")) { setVacPinErr(ownerPinErrorText(res)); setVacPinOpen(true); return; }
      // DATE_YEAR_PLAUSIBILITY_v1: auch beim ANLEGEN Fehler zeigen (vorher schloss das Fenster still)
      if (!(res && res.ok)) {
        alert((vacEditId ? "Ändern" : "Anlegen") + " fehlgeschlagen: " + ((res && res.data && res.data.error) || "Server-Fehler"));
        return;
      }
      setVacPinOpen(false);
      await loadData();
      setVacModal(false);
      setVacEditId(null);
    } finally {
      setVacSaving(false);
    }
  };
  const VacationModal = () => (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={e => { if (e.target === e.currentTarget) { setVacModal(false); setVacEditId(null); } }}>
      <div style={{ background: "var(--card)", borderRadius: 16, padding: 24, width: "100%", maxWidth: 420, boxShadow: "0 25px 50px rgba(0,0,0,0.25)" }}
        onKeyDown={e => { if (e.key === "Enter" && e.target.tagName !== "BUTTON" && !vacSaving && vacEmpId) { e.preventDefault(); handleVacSave(); } }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{vacEditId ? "Urlaub bearbeiten" : "Urlaub / Abwesenheit eintragen"}</h2>
          <button onClick={() => { setVacModal(false); setVacEditId(null); }} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={18} /></button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4, display: "block" }}>Mitarbeiter</label>
            <select value={vacEmpId} onChange={e => setVacEmpId(Number(e.target.value))}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, boxSizing: "border-box" }}>
              {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4, display: "block" }}>Art</label>
            <select value={vacType} onChange={e => setVacType(e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, boxSizing: "border-box" }}>
              {["Urlaub", "Krank", "Fortbildung", "Frei", "Sonstiges"].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4, display: "block" }}>Von</label>
              <input type="date" value={vacFromDate} onChange={e => setVacFromDate(e.target.value)}
                style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4, display: "block" }}>Bis</label>
              <input type="date" value={vacToDate} onChange={e => setVacToDate(e.target.value)}
                style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, boxSizing: "border-box" }} />
            </div>
          </div>
        </div>

        {/* VACATION_ENTITLEMENT_v1 -- Vorschau: gleiche Rechnung wie beim Speichern (Server) */}
        {vacPreview && vacPreview.counts && (
          <div data-feature="VACATION_ENTITLEMENT_v1" style={{ marginTop: 14, padding: "10px 12px", borderRadius: 10, fontSize: 12.5, lineHeight: 1.55,
            background: vacPreview.over ? `${COLORS.danger}12` : "var(--cardHover)", border: `1px solid ${vacPreview.over ? COLORS.danger + "55" : COLORS.border}` }}>
            <div>Zählt <strong>{vacPreview.days_total}</strong> Urlaubstag{vacPreview.days_total === 1 ? "" : "e"} (Arbeitstage laut Schichtplan, ohne Feiertage).</div>
            {(vacPreview.years || []).map(y => (
              <div key={y.year}>{y.year}: danach <strong style={{ color: y.remaining_after < 0 ? COLORS.danger : COLORS.success }}>{fmtDays(y.remaining_after)}</strong> von {fmtDays(y.entitlement)} Tagen übrig</div>
            ))}
            {vacPreview.over && (
              <div style={{ marginTop: 6, color: COLORS.danger, fontWeight: 600 }}>
                {vacPreview.pin_set ? "Das ist mehr als der Anspruch – eintragen geht nur mit der Inhaber-PIN."
                  : "Das ist mehr als der Anspruch. Tipp: Mit einer Inhaber-PIN (Einstellungen) kann nur noch der Inhaber solche Ausnahmen freigeben."}
              </div>
            )}
          </div>
        )}
        {vacPreview && !vacPreview.counts && vacPreview.reason === "no_plan" && (
          <div style={{ marginTop: 12, fontSize: 12, color: COLORS.textLight }}>Kein Schichtplan hinterlegt – Urlaubstage werden nicht gezählt.</div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
          <Btn variant="secondary" onClick={() => { setVacModal(false); setVacEditId(null); }}>Abbrechen</Btn>
          <Btn onClick={() => handleVacSave()} disabled={vacSaving || !vacEmpId}>
            {vacSaving ? "Speichere..." : (vacPreview && vacPreview.over && vacPreview.pin_set) ? "Mit Inhaber-PIN eintragen" : vacEditId ? "Speichern" : "Eintragen"}
          </Btn>
        </div>
      </div>
    </div>
  );

  // ── Month View Helpers ──
  const getMonthDates = (offset) => {
    const d = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    const year = d.getFullYear(), month = d.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1));
  };
  const monthDates = getMonthDates(monthOffset);
  const monthLabel = monthDates[0].toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  const dayKeyFromDate = (d) => ["so", "mo", "di", "mi", "do", "fr", "sa"][d.getDay()];

  // ── Month View Component ──
  const MonthView = () => {
    const cellW = Math.max(28, Math.floor((window.innerWidth - 300) / monthDates.length));
    return (
      <Card style={{ padding: 0, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: monthDates.length * 30 + 150 }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
              <th style={{ textAlign: "left", padding: "8px 10px", position: "sticky", left: 0, background: "var(--card)", zIndex: 2, minWidth: 120, color: COLORS.textLight, fontSize: 11 }}>Mitarbeiter</th>
              {monthDates.map((d, i) => {
                const ds = formatDate(d);
                const hol = holidays.find(h => h.date === ds);
                const isToday = ds === formatDate(today);
                const isSun = d.getDay() === 0;
                const isSat = d.getDay() === 6;
                return (
                  <th key={i} style={{
                    textAlign: "center", padding: "6px 2px", minWidth: cellW, fontSize: 10,
                    color: isSun ? COLORS.danger : isSat ? COLORS.warning : isToday ? COLORS.primary : COLORS.textLight,
                    background: isToday ? `${COLORS.primary}08` : hol ? `${COLORS.danger}06` : "transparent",
                    borderLeft: d.getDay() === 1 ? `2px solid ${COLORS.border}` : "none"
                  }}>
                    <div style={{ fontWeight: 700, fontSize: 9 }}>{["So","Mo","Di","Mi","Do","Fr","Sa"][d.getDay()]}</div>
                    <div style={{ fontWeight: 600 }}>{d.getDate()}</div>
                    {hol && hol.name && <div style={{ fontSize: 7, color: COLORS.danger, lineHeight: 1.1 }}>{String(hol.name).substring(0, 6)}</div>}
                  </th>
                );
              })}
              <th style={{ textAlign: "center", padding: "6px 4px", color: COLORS.textLight, fontSize: 10, minWidth: 40 }}>Std</th>
            </tr>
          </thead>
          <tbody>
            {employees.map(emp => {
              let monthMins = 0;
              return (
                <tr key={emp.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <td style={{ padding: "8px 10px", position: "sticky", left: 0, background: "var(--card)", zIndex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: emp.color, flexShrink: 0 }} />
                      <span style={{ fontWeight: 600, fontSize: 11, whiteSpace: "nowrap" }}>{emp.name}</span>
                    </div>
                  </td>
                  {monthDates.map((d, i) => {
                    const ds = formatDate(d);
                    const dk = dayKeyFromDate(d);
                    const vac = vacations.find(v => v.employee_id === emp.id && ds >= v.from_date && ds <= v.to_date);
                    const hol = holidays.find(h => h.date === ds);
                    const shift = getShift(emp.id, dk, ds); // BIWEEKLY_SHIFT_v1
                    const hasShift = shift && !shift.is_off && shift.start_time;
                    if (hasShift && !vac && !hol && shift.end_time) {
                      // NULLSAFE_AUDIT_v1 -- end_time-Guard fehlte, Schicht ohne Ende → crash
                      const [sh, sm] = String(shift.start_time).split(":").map(Number);
                      const [eh, em] = String(shift.end_time).split(":").map(Number);
                      monthMins += (eh * 60 + em) - (sh * 60 + sm);
                    }
                    const isSun = d.getDay() === 0;
                    return (
                      <td key={i}
                        onClick={() => { if (hol) return; if (vac) { openVacEdit(vac); return; } setShiftModal({ empId: emp.id, dayKey: dk, ds }); }}
                        style={{
                          textAlign: "center", padding: "4px 1px", cursor: hol ? "default" : "pointer",
                          background: vac ? `${COLORS.info}08` : hol ? `${COLORS.danger}05` : "transparent",
                          borderLeft: d.getDay() === 1 ? `2px solid ${COLORS.border}` : "none",
                          transition: "background 0.1s"
                        }}
                        onMouseEnter={e => { if (!hol) e.currentTarget.style.background = `${COLORS.primary}10`; }}
                        onMouseLeave={e => { e.currentTarget.style.background = vac ? `${COLORS.info}08` : hol ? `${COLORS.danger}05` : "transparent"; }}
                        title={vac ? vac.type + " · Klicken zum Bearbeiten" : hol ? hol.name : hasShift ? `${shift.start_time}-${shift.end_time}` : shift?.is_off ? "Frei" : "Klicken zum Anlegen"}>
                        {vac ? (
                          <div style={{ width: 16, height: 16, borderRadius: 4, background: COLORS.info, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <span style={{ color: "white", fontSize: 8, fontWeight: 700 }}>{vac.type.charAt(0)}</span>
                          </div>
                        ) : hol ? (
                          <div style={{ width: 16, height: 16, borderRadius: 4, background: `${COLORS.danger}30`, margin: "0 auto" }} />
                        ) : hasShift ? (
                          <div style={{ width: 16, height: 16, borderRadius: 4, background: `${emp.color}CC`, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <span style={{ color: "white", fontSize: 7, fontWeight: 700 }}>{shift.start_time.split(":")[0]}</span>
                          </div>
                        ) : shift?.is_off ? (
                          <span style={{ color: "#D1D5DB", fontSize: 9 }}>—</span>
                        ) : isSun ? (
                          <span style={{ color: "#E5E7EB", fontSize: 9 }}>—</span>
                        ) : (
                          <span style={{ color: "#E5E7EB", fontSize: 12 }}>+</span>
                        )}
                      </td>
                    );
                  })}
                  <td style={{ textAlign: "center", padding: "4px 4px", fontWeight: 700, fontSize: 11, color: Math.round(monthMins / 60) > 170 ? COLORS.danger : COLORS.text }}>
                    {Math.round(monthMins / 60)}h
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    );
  };

  // ── Month Legend ──
  const MonthLegend = () => (
    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 12, fontSize: 11, color: COLORS.textLight }}>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <div style={{ width: 14, height: 14, borderRadius: 3, background: `${COLORS.primary}CC` }} />
        <span>Schicht (Startstunde)</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <div style={{ width: 14, height: 14, borderRadius: 3, background: COLORS.info, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ color: "white", fontSize: 8, fontWeight: 700 }}>U</span>
        </div>
        <span>Urlaub/Abwesenheit</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <div style={{ width: 14, height: 14, borderRadius: 3, background: `${COLORS.danger}30` }} />
        <span>Feiertag / Betriebsurlaub</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span style={{ color: "#D1D5DB", fontSize: 12 }}>—</span>
        <span>Frei</span>
      </div>
    </div>
  );

  if (loading) return <div style={{ textAlign: "center", padding: 40 }}>Lade Schichtplan...</div>;

  return (
    <div>
      <Header title="Schichtplan" subtitle="Arbeitszeiten, Urlaub & Feiertage" />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {/* View Toggle */}
          <div style={{ display: "flex", background: COLORS.background, borderRadius: 10, padding: 3 }}>
            {[{k:"week",l:"Woche"},{k:"month",l:"Monat"}].map(v => (
              <button key={v.k} onClick={() => setViewMode(v.k)}
                style={{ padding: "6px 14px", borderRadius: 8, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.2s",
                  background: viewMode === v.k ? "var(--card)" : "transparent", color: viewMode === v.k ? COLORS.primary : COLORS.textLight,
                  boxShadow: viewMode === v.k ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}>
                {v.l}
              </button>
            ))}
          </div>
          {/* Navigation */}
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Btn variant="ghost" size="sm" onClick={() => viewMode === "week" ? setWeekOffset(w => w - 1) : setMonthOffset(m => m - 1)}><ChevronLeft size={16} /></Btn>
            {/* SHIFT_DATE_TO_MONTH_v1 (Toby 22.09.2026) -- Tipp auf das Datum der Woche zeigt den ganzen Monat dieser Woche */}
            <span onClick={viewMode === "week" ? () => { const d0 = weekDates[0]; setMonthOffset((d0.getFullYear() - today.getFullYear()) * 12 + (d0.getMonth() - today.getMonth())); setViewMode("month"); } : undefined}
              title={viewMode === "week" ? "Ganzen Monat anzeigen" : undefined}
              style={{ fontSize: window.innerWidth < 768 ? 12 : 15, fontWeight: 700, textAlign: "center", minWidth: 180, cursor: viewMode === "week" ? "pointer" : "default", padding: "4px 8px", borderRadius: 8 }}>
              {viewMode === "week" ? (
                `KW ${getISOWeek(weekDates[0])} — ${weekDates[0].toLocaleDateString("de-DE", { day: "numeric", month: "short" })} bis ${weekDates[6].toLocaleDateString("de-DE", { day: "numeric", month: "short", year: "numeric" })}`
              ) : monthLabel}
            </span>
            <Btn variant="ghost" size="sm" onClick={() => viewMode === "week" ? setWeekOffset(w => w + 1) : setMonthOffset(m => m + 1)}><ChevronRight size={16} /></Btn>
          </div>
          <Btn variant="secondary" size="sm" onClick={() => viewMode === "week" ? setWeekOffset(0) : setMonthOffset(0)}>
            {viewMode === "week" ? "Diese Woche" : "Dieser Monat"}
          </Btn>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {/* SHIFT_TEMPLATE_v1 (3.2) */}
          <Btn size="sm" variant="secondary" onClick={() => setTplModal(true)}><Plus size={14} /> Vorlage</Btn>
          {/* SHIFT_OVERRIDE_UI_v1 (3.3) */}
          {/* SHIFT_OVERRIDE_RENAME_v1 -- "Krank/Verspätung" deckte nicht mehr alles ab seit Extra-Schicht dazukam */}
          <Btn size="sm" variant="secondary" onClick={() => setOvModal(true)} style={{ background: "#fde68a", color: "#92400e" }}>Schichtänderung</Btn>
          <Btn size="sm" onClick={() => {
            // VACATION_MODAL_STATE_FIX_v1 -- jedes Mal beim Oeffnen frische Defaults
            setVacEmpId(employees[0]?.id || 0);
            setVacType("Urlaub");
            setVacFromDate(formatDate(today));
            setVacToDate(formatDate(today));
            setVacModal(true);
          }}><Plus size={14} /> Urlaub</Btn>
          {/* SALON_CLOSURE_v1 */}
          <Btn size="sm" variant="secondary" onClick={() => { setClMsg(null); setClosureModal(true); }}>🏖 Betriebsurlaub</Btn>
        </div>
      </div>

      {/* SHIFT_MODAL_REMOUNT_FIX_v1 -- ShiftModal()/VacationModal() als Funktionsaufruf, NICHT als <JSX/>:
          Beide sind inline in ShiftsPage definiert und bekämen bei jedem Render eine neue Komponenten-Identität
          → React unmountet+remountet das Modal bei JEDEM Tastendruck. Zerstörtes fokussiertes time/date-Input
          crasht dabei den Safari/WebKit-Prozess (Tab-Tod, Schichtplan-Bug H-Galerie 13.07.2026).
          Als Funktionsaufruf gibt es keine Komponenten-Grenze, React reconciled normal, Inputs bleiben stabil.
          NICHT zurück zu <ShiftModal /> ändern! */}
      {shiftModal && ShiftModal()}
      {vacModal && VacationModal()}
      {/* OWNER_PIN_v1 -- eigene Komponente NEBEN dem Urlaubs-Fenster (liegt darueber, zIndex 10050) */}
      <OwnerPinPrompt open={vacPinOpen} title="Inhaber-PIN"
        text="Dieser Urlaub geht über den Anspruch hinaus. Freigeben kann das nur der Inhaber."
        error={vacPinErr} busy={vacSaving} onSubmit={p => handleVacSave(p)} onCancel={() => setVacPinOpen(false)} />
      {entEdit && <EntitlementEditor row={entEdit} onClose={() => setEntEdit(null)} onSaved={() => { setEntEdit(null); loadData(); }} />}
      {closureModal && ClosureModal()}
      {tplModal && <ShiftTemplateModal employees={employees} onClose={() => setTplModal(false)} onSaved={loadData} />}
      {ovModal && <ShiftOverrideModal employees={employees} onClose={() => setOvModal(false)} onSaved={loadData} />}

      {/* ── Week View ── */}
      {viewMode === "week" && (
        <>
          <Card style={{ padding: 0, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                  <th style={{ textAlign: "left", padding: "10px 14px", width: 140, color: COLORS.textLight }}>Mitarbeiter</th>
                  {weekDates.map((d, i) => {
                    const ds = formatDate(d);
                    const hol = holidays.find(h => h.date === ds);
                    const isToday = ds === formatDate(today);
                    const isSun = i === 6;
                    return (
                      <th key={i} style={{ textAlign: "center", padding: "10px 8px", color: isSun ? COLORS.danger : isToday ? COLORS.primary : COLORS.textLight, background: isToday ? `${COLORS.primary}08` : hol ? `${COLORS.danger}08` : "transparent" }}>
                        <div style={{ fontWeight: 700 }}>{dayNames[i]} {d.getDate()}.{d.getMonth() + 1}</div>
                        {hol && <div style={{ fontSize: 9, color: COLORS.danger }}>{hol.name}</div>}
                      </th>
                    );
                  })}
                  <th style={{ textAlign: "center", padding: "10px 8px", color: COLORS.textLight, width: 60 }}>Std/W</th>
                </tr>
              </thead>
              <tbody>
                {employees.map(emp => {
                  const weekHrs = calcWeekHours(emp.id);
                  return (
                    <tr key={emp.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 8, height: 8, borderRadius: "50%", background: emp.color }} />
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 12 }}>{emp.name}</div>
                            <div style={{ fontSize: 10, color: COLORS.textLight }}>{emp.role}</div>
                          </div>
                        </div>
                      </td>
                      {dayKeys.map((dayKey, i) => {
                        const ds = formatDate(weekDates[i]);
                        const vac = vacations.find(v => v.employee_id === emp.id && ds >= v.from_date && ds <= v.to_date);
                        const hol = holidays.find(h => h.date === ds);
                        const shift = getShift(emp.id, dayKey, ds); // BIWEEKLY_SHIFT_v1
                        const hasShift = shift && !shift.is_off && shift.start_time;
                        // SHIFT_OVERRIDE_RENDER_v1 -- Override (late/early_leave/sick/custom) anzeigen
                        const ov = (overrides || []).find(o => Number(o.employee_id) === Number(emp.id) && String(o.date) === ds && o.override_type !== "vacation");
                        const ovIsSick = ov && ov.override_type === "sick";
                        // EXTRA_SHIFT_RENDER_v1 -- "extra" = MA kommt zusätzlich obwohl keine reguläre Schicht.
                        // Muss orange-Bubble bekommen wie andere Overrides, sonst wirkt es als wäre nichts gespeichert.
                        const ovIsExtra = ov && ov.override_type === "extra";
                        // Effektive Start-/Endzeit unter Berücksichtigung Override (extra: aus override-Felder)
                        const effStart = ov && (ov.override_type === "late" || ov.override_type === "custom" || ov.override_type === "extra")
                          ? (ov.start_time || "").slice(0,5)
                          : (hasShift ? shift.start_time : null);
                        const effEnd = ov && (ov.override_type === "early_leave" || ov.override_type === "custom" || ov.override_type === "extra")
                          ? (ov.end_time || "").slice(0,5)
                          : (hasShift ? shift.end_time : null);
                        // SHIFT_OVERRIDE_DELETE_FIX_v1 -- Click auf bestehende Override-Bubble → Schichtänderung entfernen (mit Bestätigung).
                        // Click auf leere Zelle / reguläre Schicht → Schicht-Formular wie bisher.
                        const _ovLabel = ov ? (ovIsSick ? "Krank" : ov.override_type === "late" ? "Verspätung" : ov.override_type === "early_leave" ? "Früher Schluss" : ovIsExtra ? "Extra-Schicht" : "Custom") : "";
                        const handleCellClick = async () => {
                          if (hol) return;
                          if (vac) { openVacEdit(vac); return; } // VAC_CELL_EDIT_v1
                          if (ov) {
                            const dayLabel = weekDates[i].toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });
                            if (!window.confirm(`"${_ovLabel}" für ${emp.name} am ${dayLabel} entfernen?`)) return;
                            const res = await api.request("/shift-overrides/" + ov.id, { method: "DELETE" });
                            if (res && res.ok) {
                              await loadData();
                            } else {
                              alert("Löschen fehlgeschlagen: " + ((res && res.data && res.data.error) || "Server-Fehler"));
                            }
                            return;
                          }
                          setShiftModal({ empId: emp.id, dayKey, ds }); // BIWEEKLY_SHIFT_v1: Datum für Paritäts-Zuordnung
                        };
                        return (
                          <td key={i} onClick={handleCellClick}
                            style={{ textAlign: "center", padding: "8px 6px", cursor: hol ? "default" : "pointer",
                              background: vac ? `${COLORS.info}08` : hol ? `${COLORS.danger}05` : ov ? `${COLORS.warning}08` : "transparent",
                              transition: "background 0.15s" }}
                            onMouseEnter={e => { if (!vac && !hol) e.currentTarget.style.background = `${COLORS.primary}08`; }}
                            onMouseLeave={e => { if (!vac && !hol) e.currentTarget.style.background = vac ? `${COLORS.info}08` : hol ? `${COLORS.danger}05` : ov ? `${COLORS.warning}08` : "transparent"; }}
                            title={ov ? (ovIsSick ? "Krank" : ov.override_type === "late" ? `Verspätung: Start ${effStart}` : ov.override_type === "early_leave" ? `Früher Schluss: ${effEnd}` : ovIsExtra ? `Extra-Schicht: ${effStart}-${effEnd}` : `Custom: ${effStart}-${effEnd}`) + (ov.notes ? " · " + ov.notes : "") + " · Klick zum Entfernen" : undefined}>
                            {vac ? (
                              <div style={{ position: "relative" }}>
                                <span style={{ padding: "3px 8px", borderRadius: 12, background: `${COLORS.info}15`, color: COLORS.info, fontSize: 10, fontWeight: 600 }}>{vac.type}</span>
                              </div>
                            ) : hol ? (
                              <span style={{ padding: "3px 8px", borderRadius: 12, background: `${COLORS.danger}15`, color: COLORS.danger, fontSize: 10, fontWeight: 600 }}>{hol.closure ? "Betriebsurlaub" : "Feiertag"}</span>
                            ) : ovIsSick ? (
                              <span style={{ padding: "3px 8px", borderRadius: 12, background: `${COLORS.danger}15`, color: COLORS.danger, fontSize: 10, fontWeight: 700 }}>🤒 Krank</span>
                            ) : ovIsExtra ? (
                              /* EXTRA_SHIFT_RENDER_v1 -- grün, weil "MA kommt zusätzlich rein" eine positive Info ist */
                              <span style={{ padding: "3px 8px", borderRadius: 12, background: `${COLORS.success}18`, color: COLORS.success, fontSize: 10, fontWeight: 700, border: `1px dashed ${COLORS.success}` }} title="Extra-Schicht">
                                ➕ {effStart}-{effEnd}
                              </span>
                            ) : ov && hasShift ? (
                              <span style={{ padding: "3px 8px", borderRadius: 12, background: `${COLORS.warning}18`, color: COLORS.warning, fontSize: 10, fontWeight: 700, border: `1px dashed ${COLORS.warning}` }} title={ov.override_type === "late" ? "Verspätung" : ov.override_type === "early_leave" ? "Früher Schluss" : "Custom"}>
                                {effStart}-{effEnd}
                              </span>
                            ) : hasShift ? (
                              <span style={{ padding: "3px 8px", borderRadius: 12, background: `${emp.color}15`, color: emp.color, fontSize: 11, fontWeight: 600 }}
                                title={shift.week_parity != null ? "Alle 2 Wochen (14-Tage-Rhythmus)" : undefined}>
                                {shift.start_time}-{shift.end_time}{shift.week_parity != null ? " 🔁" : ""}
                              </span>
                            ) : shift && shift.is_off ? (
                              <span style={{ color: COLORS.textLight, fontSize: 11 }}>frei</span>
                            ) : (
                              <span style={{ color: "#D1D5DB", fontSize: 16, lineHeight: 1 }}>+</span>
                            )}
                          </td>
                        );
                      })}
                      <td style={{ textAlign: "center", padding: "8px 6px", fontWeight: 700, fontSize: 12, color: weekHrs > 40 ? COLORS.danger : COLORS.text }}>
                        {weekHrs}h
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {/* ── Month View ── */}
      {viewMode === "month" && (
        <>
          <MonthView />
          <MonthLegend />
        </>
      )}

      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: 16, marginTop: 16 }}>
        <Card>
          <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Kommende Feiertage & Betriebsurlaub</h3>
          {/* SALON_CLOSURE_v1 -- Betriebsurlaub als EIN Eintrag mit Zeitraum, nicht je Tag */}
          {(closures || []).filter(c => c.to_date >= formatDate(today)).map(c => (
            <div key={"cl" + c.id} onClick={() => { setClMsg(null); setClosureModal(true); }} style={{ padding: "6px 0", borderBottom: `1px solid ${COLORS.border}`, fontSize: 12, display: "flex", justifyContent: "space-between", cursor: "pointer" }}>
              <span style={{ fontWeight: 600 }}>🏖 {c.label}</span>
              <span style={{ color: COLORS.textLight }}>{fmtDe(c.from_date)}{c.to_date !== c.from_date ? ` – ${fmtDe(c.to_date)}` : ""}</span>
            </div>
          ))}
          {holidays.filter(h => !h.closure && h.date >= formatDate(today)).map((h, i) => (
            <div key={i} style={{ padding: "6px 0", borderBottom: `1px solid ${COLORS.border}`, fontSize: 12, display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 600 }}>{h.name}</span>
              <span style={{ color: COLORS.textLight }}>{new Date(h.date).toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "short" })}</span>
            </div>
          ))}
        </Card>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Geplanter Urlaub</h3>
          </div>
          {vacations.length === 0 && <p style={{ fontSize: 13, color: COLORS.textLight }}>Kein Urlaub eingetragen.</p>}
          {vacations.map((v, i) => {
            const emp = employees.find(e => e.id === v.employee_id);
            return (
              <div key={v.id || i} style={{ padding: "8px 0", borderBottom: `1px solid ${COLORS.border}`, fontSize: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span><strong>{v.employee_name || emp?.name}</strong> — <Badge text={v.type} color={COLORS.info} /></span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: COLORS.textLight }}>
                    {new Date(v.from_date).toLocaleDateString("de-DE", { day: "numeric", month: "short" })} – {new Date(v.to_date).toLocaleDateString("de-DE", { day: "numeric", month: "short" })}
                  </span>
                  {/* VACATION_EDIT_v1 -- Bearbeiten öffnet das Urlaubs-Fenster vorbefüllt */}
                  <button onClick={() => { setVacEditId(v.id); setVacEmpId(v.employee_id); setVacType(v.type || "Urlaub"); setVacFromDate(String(v.from_date).slice(0, 10)); setVacToDate(String(v.to_date).slice(0, 10)); setVacModal(true); }}
                    style={{ background: `${COLORS.primary}10`, border: "none", borderRadius: 6, padding: "4px 6px", cursor: "pointer", color: COLORS.primary }} title="Bearbeiten">
                    <Edit3 size={12} />
                  </button>
                  <button onClick={async () => { if (confirm(`Urlaub von ${v.employee_name || emp?.name} löschen?`)) { await api.deleteVacation(v.id); await loadData(); } }}
                    style={{ background: `${COLORS.danger}10`, border: "none", borderRadius: 6, padding: "4px 6px", cursor: "pointer", color: COLORS.danger }} title="Löschen">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </Card>
        {/* VACATION_ENTITLEMENT_v1 + MY_VACATION_v1 -- Inhaber/Admin: Urlaubskonto aller (Name antippen = Daten);
            Mitarbeiter-Login: „Mein Urlaub" (Server liefert nur die eigene Zeile, Daten immer offen) */}
        {vacBal && (vacBal.employees || []).length > 0 && (
          <Card>
            <h3 data-feature="VACATION_ENTITLEMENT_v1" style={{ margin: "0 0 10px", fontSize: 15, fontWeight: 700 }}>
              {vacBal.own ? `Mein Urlaub ${vacBal.year}` : `Urlaubskonto ${vacBal.year}`}
            </h3>
            {(vacBal.employees || []).map(r => {
              const open = !!vacBal.own || vacOpenEmp === r.employee_id;
              return (
                <div key={r.employee_id} style={{ padding: "7px 0", borderBottom: `1px solid ${COLORS.border}` }}>
                  {!vacBal.own && (
                    <button onClick={() => setVacOpenEmp(open ? null : r.employee_id)}
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", background: "none", border: "none", padding: "0 0 2px", cursor: "pointer", color: COLORS.text, textAlign: "left" }}>
                      <span style={{ fontWeight: 600, fontSize: 12.5 }}>{r.name}</span>
                      <span style={{ fontSize: 11, color: COLORS.primary, fontWeight: 600 }}>{open ? "▾ Daten ausblenden" : "▸ Daten anzeigen"}</span>
                    </button>
                  )}
                  <VacationAccountLine row={r} onEdit={vacOwnerish ? () => setEntEdit(r) : null} />
                  {open && <VacationEntries entries={r.entries} />}
                </div>
              );
            })}
            <p style={{ margin: "10px 0 0", fontSize: 11.5, color: COLORS.textLight, lineHeight: 1.5 }}>
              {vacBal.own
                ? "Gezählt werden Tage der Art „Urlaub“ an deinen Arbeitstagen laut Schichtplan, ohne Feiertage. Fragen zum Anspruch klärst du mit der Salonleitung."
                : "Gezählt werden Tage der Art „Urlaub“ an Arbeitstagen laut Schichtplan, ohne Feiertage."}
              {!vacBal.own && !vacBal.pin_set && " Mit einer Inhaber-PIN (Einstellungen) kann nur noch der Inhaber Urlaub über den Anspruch hinaus freigeben."}
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};

// ============================================================
// TIME_TRACKING_v1 -- Zeiterfassung Mitarbeiter
// Top-level Component (KEIN inline-Pattern — siehe SHIFT_MODAL_STATE_FIX_v1 Lektion).
// Employee-View: großer Komme/Gehe-Knopf für sich selbst.
// Owner/Admin-View: Tagesübersicht aller MAs + Stempel-Möglichkeit für andere (mit Notiz-Pflicht).
// ============================================================
// TIME_CORRECTION_v1 -- Modal für Stempel-Korrektur (Admin/Owner only)
// Append-only: alter Eintrag bleibt, neuer mit corrected_from_id-Verweis wird erzeugt.
export const TimeCorrectionModal = ({ employee, events, onClose, onSaved }) => {
  const [editing, setEditing] = useState(null); // event object
  const [newType, setNewType] = useState("in");
  const [newTime, setNewTime] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const startEdit = (ev) => {
    setEditing(ev);
    setNewType(ev.entry_type);
    // ev.occurred_at ISO -> nur Zeit-Anteil
    try {
      const d = new Date(ev.occurred_at);
      setNewTime(d.toISOString().slice(0, 16));
    } catch { setNewTime(""); }
    setReason("");
    setError("");
  };
  const cancelEdit = () => { setEditing(null); setReason(""); setError(""); };

  const save = async (mode) => {
    if (!editing) return;
    if (!reason.trim() || reason.trim().length < 5) { setError("Begründung Pflicht (mind. 5 Zeichen)"); return; }
    setSaving(true);
    const payload = { entry_id: editing.id, reason: reason.trim() };
    if (mode === "void") {
      payload.new_entry_type = "void";
    } else {
      payload.new_entry_type = newType;
      payload.new_occurred_at = new Date(newTime).toISOString();
    }
    const res = await api.timeCorrect(payload);
    setSaving(false);
    if (res && res.ok) {
      setEditing(null);
      onSaved && onSaved();
    } else {
      setError((res && res.data && res.data.error) || "Fehler beim Speichern");
    }
  };

  const typeLabel = t => ({
    in: "👋 Komme", out: "🏁 Gehe",
    break_start: "☕ Pause Start", break_end: "▶️ Pause Ende",
    correction_void: "✗ storniert"
  })[t] || t;
  const fmtTime = iso => { try { return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 100000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "var(--card)", borderRadius: 14, width: "min(560px, 95vw)",
        maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
      }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid " + COLORS.border, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
            Stempel-Korrektur — {employee && employee.employee_name}
          </h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "var(--textLight)" /* DARK_BTN_TEXT_v1 */ }}>×</button>
        </div>

        {!editing ? (
          <div style={{ overflowY: "auto", padding: "12px 20px", flex: 1 }}>
            <div style={{ padding: 10, background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 8, fontSize: 12, marginBottom: 12, lineHeight: 1.5 }}>
              ℹ️ Korrekturen werden audit-fest protokolliert: der alte Wert bleibt, ein neuer Eintrag mit Verweis wird hinzugefügt. Begründung ist Pflicht.
            </div>
            {(events || []).length === 0 ? (
              <div style={{ padding: 20, textAlign: "center", color: COLORS.textLight, fontSize: 13 }}>Keine Stempel heute.</div>
            ) : (
              (events || []).map(ev => (
                <div key={ev.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid " + COLORS.border }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{typeLabel(ev.entry_type)}</div>
                    <div style={{ fontSize: 12, color: COLORS.textLight }}>{fmtTime(ev.occurred_at)}{ev.source !== "button" ? " · " + ev.source : ""}</div>
                  </div>
                  <button onClick={() => startEdit(ev)} style={{
                    padding: "6px 12px", borderRadius: 6, border: "1px solid " + COLORS.border,
                    background: "transparent", color: COLORS.text, fontSize: 12, cursor: "pointer", fontWeight: 600,
                  }}>Korrigieren</button>
                </div>
              ))
            )}
          </div>
        ) : (
          <div style={{ overflowY: "auto", padding: "16px 20px", flex: 1 }}>
            <div style={{ padding: 10, background: "var(--cardHover)", borderRadius: 8, marginBottom: 14, fontSize: 12.5 }}>
              <strong>Original:</strong> {typeLabel(editing.entry_type)} um {fmtTime(editing.occurred_at)}
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Neue Aktion</label>
            <select value={newType} onChange={e => setNewType(e.target.value)} style={{
              width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid " + COLORS.border,
              background: "var(--card)", color: COLORS.text, fontSize: 13, marginBottom: 12,
            }}>
              <option value="in">👋 Komme</option>
              <option value="out">🏁 Gehe</option>
              <option value="break_start">☕ Pause Start</option>
              <option value="break_end">▶️ Pause Ende</option>
            </select>
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Neuer Zeitpunkt</label>
            <input type="datetime-local" value={newTime} onChange={e => setNewTime(e.target.value)} style={{
              width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid " + COLORS.border,
              background: "var(--card)", color: COLORS.text, fontSize: 13, marginBottom: 12, boxSizing: "border-box",
            }} />
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>
              Begründung * (mind. 5 Zeichen)
            </label>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
              placeholder="z.B. Mariella hat vergessen zu stempeln, sie war ab 09:00 da."
              style={{
                width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid " + COLORS.border,
                background: "var(--card)", color: COLORS.text, fontSize: 13, fontFamily: "inherit",
                resize: "vertical", boxSizing: "border-box", marginBottom: 12,
              }} />
            {error && <div style={{ padding: 8, background: "rgba(239,68,68,0.10)", color: "#EF4444", borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{error}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "space-between", flexWrap: "wrap" }}>
              <button onClick={() => save("void")} disabled={saving}
                style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #EF4444", background: "transparent", color: "#EF4444", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                Eintrag stornieren
              </button>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={cancelEdit} disabled={saving}
                  style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid " + COLORS.border, background: "transparent", color: COLORS.text, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  Abbrechen
                </button>
                <button onClick={() => save("update")} disabled={saving}
                  style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: COLORS.primary || "#7C3AED", color: "white", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  {saving ? "Speichere..." : "Korrektur speichern"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const TimeTrackingPage = ({ currentUser, employees = [] }) => {
  const [overview, setOverview] = useState(null);
  const [myStatus, setMyStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [punching, setPunching] = useState(false);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);
  // TIME_OVERTIME_v1 -- Monatsuebersicht Soll/Ist pro MA
  const [monthYM, setMonthYM] = useState(() => new Date().toISOString().slice(0, 7));
  const [monthData, setMonthData] = useState({});  // { empId: month-response }
  const [monthLoading, setMonthLoading] = useState(false);
  // TIME_CORRECTION_v1 -- Korrektur-Modal
  const [correctionFor, setCorrectionFor] = useState(null); // { employee, events }

  const isEmployee = currentUser && currentUser.role === "employee";
  const isOwnerOrAdmin = currentUser && (currentUser.role === "owner" || currentUser.role === "admin");
  const myEmployeeId = currentUser && currentUser.employee_id;

  // TIME_TRACKING_TOGGLE_v1 -- Zeiterfassung pro Salon an/aus (nur Owner/Admin schaltet)
  const salonId = currentUser && currentUser.salon_id;
  const [ttEnabled, setTtEnabled] = useState(null);   // null=lädt, true/false=Salon-Setting
  const [ttSaving, setTtSaving] = useState(false);
  const [salonName, setSalonName] = useState("");     // TIMESHEET_PRINT_v1 -- Kopfzeile des Stundenzettels
  const trackingActive = ttEnabled !== false;         // null/true => aktiv (Default AN)
  useEffect(() => {
    if (salonId == null) return;
    let cancelled = false;
    api.request("/salons/" + salonId).then(r => {
      if (!cancelled && r && r.ok) {
        setTtEnabled(r.data.time_tracking_enabled !== false);
        setSalonName(r.data.name || "");
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [salonId]);

  // TIMESHEET_PRINT_v1 (08.09.2026, Toby: "Zeiterfassung ... Müssen auch zum ausdrucken für Steuer sein") --
  // Druckbarer Stundenzettel je Mitarbeiter und Monat aus den Monatsdaten (/time/employee/<id>/month):
  // Tag, Beginn (erstes Kommen), Ende (letztes Gehen), Pausen, Netto (gestempelt), Soll, Differenz,
  // Korrekturen mit Begründung, Summen, Unterschriftszeilen. Öffnet ein neues Fenster mit reinem HTML
  // (popup-sicher: Daten liegen schon im State, window.open läuft synchron im Klick) und ruft print().
  const _tsEsc = (v) => String(v == null ? "" : v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const _tsHM = (min) => {
    const m = Math.max(0, Math.round(min || 0));
    return Math.floor(m / 60) + ":" + String(m % 60).padStart(2, "0");
  };
  const _tsDayRow = (d) => {
    const evs = d.events || [];
    const ins = evs.filter(e => e.entry_type === "in");    // Stempel-Typen: in | out | break_start | break_end
    const outs = evs.filter(e => e.entry_type === "out");
    let pause = 0, bs = null;
    evs.forEach(e => {
      if (e.entry_type === "break_start") bs = new Date(e.occurred_at);
      else if (e.entry_type === "break_end" && bs) { pause += Math.max(0, (new Date(e.occurred_at) - bs) / 60000); bs = null; }
    });
    const corr = evs.filter(e => e.source === "correction");
    const notes = corr.map(e => e.notes).filter(Boolean).join("; ");
    const dt = new Date(d.date + "T12:00:00");
    const wd = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][dt.getDay()];
    const missing = d.final_status === "missing";
    const open = !missing && ins.length && outs.length < ins.length;
    return { wd, date: dt.toLocaleDateString("de-DE"), begin: ins.length ? ins[0].time : "", end: outs.length ? outs[outs.length - 1].time : (open ? "offen" : ""),
      pause, worked: d.worked_minutes || 0, soll: d.soll_minutes || 0, diff: d.diff_minutes || 0, missing, corr: corr.length > 0, notes };
  };
  const _tsSheetHtml = (md, empName) => {
    const [y, m] = String(md.ym || "").split("-");
    const monthLabel = new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
    // Laufender Monat: Tage nach heute weglassen (der Server listet Soll-Tage des ganzen Monats als
    // "fehlend"); Summen deshalb aus den gedruckten Zeilen rechnen, nicht aus den Monats-Totalen.
    const todayKey = formatDate(new Date());  // BLOCKER_SERIES_TZ_FIX_v1 -- lokal statt UTC
    const rows = (md.days || []).filter(d => d.date <= todayKey).map(_tsDayRow);
    const pauseSum = rows.reduce((a, r) => a + r.pause, 0);
    const workedSum = rows.reduce((a, r) => a + r.worked, 0);
    const sollSum = rows.reduce((a, r) => a + r.soll, 0);
    const diffSum = workedSum - sollSum;
    const partial = (md.ym || "") === todayKey.slice(0, 7);
    const corrAny = rows.some(r => r.corr);
    const tr = rows.map(r => `<tr class="${r.missing ? "miss" : ""}">
      <td>${r.wd}, ${r.date}</td><td class="c">${r.missing ? "—" : _tsEsc(r.begin)}</td><td class="c">${r.missing ? "—" : _tsEsc(r.end)}</td>
      <td class="r">${r.missing ? "—" : _tsHM(r.pause)}</td><td class="r b">${r.missing ? "0:00" : _tsHM(r.worked)}</td>
      <td class="r">${_tsHM(r.soll)}</td><td class="r ${r.diff < 0 ? "neg" : r.diff > 0 ? "pos" : ""}">${r.diff > 0 ? "+" : r.diff < 0 ? "−" : ""}${_tsHM(Math.abs(r.diff))}</td>
      <td class="n">${r.missing ? "kein Stempel" : (r.corr ? "Korrektur" + (r.notes ? ": " + _tsEsc(r.notes) : "") : "")}</td></tr>`).join("");
    const printed = new Date().toLocaleDateString("de-DE") + " " + new Date().toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    return `<section class="sheet">
      <div class="head"><div><div class="t">Stundenzettel</div><div class="s">${_tsEsc(salonName || "")}</div></div>
        <div class="meta"><div><b>Mitarbeiter/in:</b> ${_tsEsc(empName)}</div><div><b>Monat:</b> ${_tsEsc(monthLabel)}</div></div></div>
      <table><thead><tr><th>Tag</th><th class="c">Beginn</th><th class="c">Ende</th><th class="r">Pausen</th><th class="r">Arbeitszeit</th><th class="r">Soll</th><th class="r">Differenz</th><th>Bemerkung</th></tr></thead>
      <tbody>${tr || '<tr><td colspan="8" class="n">Keine Einträge in diesem Monat.</td></tr>'}</tbody>
      <tfoot><tr><td><b>Summe${partial ? " (bis heute)" : ""}</b></td><td></td><td></td><td class="r">${_tsHM(pauseSum)}</td><td class="r b">${_tsHM(workedSum)}</td><td class="r">${_tsHM(sollSum)}</td>
      <td class="r ${diffSum < 0 ? "neg" : diffSum > 0 ? "pos" : ""}">${diffSum > 0 ? "+" : diffSum < 0 ? "−" : ""}${_tsHM(Math.abs(diffSum))}</td><td></td></tr></tfoot></table>
      <div class="legend">Arbeitszeit = gestempelte Zeit ohne Pausen. Soll = laut Schichtplan. „kein Stempel" = Soll-Tag ohne Buchung (Krankheit, Urlaub oder vergessen — bitte prüfen).${corrAny ? " Korrekturen sind mit Begründung vermerkt." : ""}${partial ? " Laufender Monat: Stand " + new Date().toLocaleDateString("de-DE") + "." : ""}</div>
      <div class="sig"><div><div class="line"></div>Datum, Unterschrift Mitarbeiter/in</div><div><div class="line"></div>Datum, Unterschrift Arbeitgeber/in</div></div>
      <div class="foot">Arbeitszeitaufzeichnung gemäß § 16 Abs. 2 ArbZG und § 17 MiLoG · Aufbewahrung mindestens 2 Jahre · erstellt am ${printed} mit Shearly (shearly.pro)</div>
    </section>`;
  };
  const printTimesheets = (items) => {
    // items: [{ md, name }]
    const list = (items || []).filter(i => i && i.md);
    if (!list.length) return;
    const w = window.open("", "_blank");
    if (!w) { setError("Pop-up blockiert — bitte Pop-ups für shearly.pro erlauben."); return; }
    const html = `<!DOCTYPE html><html lang="de"><head><meta charset="utf-8"><title>Stundenzettel ${_tsEsc(list[0].md.ym || "")}</title>
      <style>
        @page { size: A4; margin: 14mm; }
        :root { color-scheme: light; }
        html, body { background: #fff; }
        body { font-family: -apple-system, "Segoe UI", Helvetica, Arial, sans-serif; color: #111; font-size: 12px; margin: 0; padding: 12px; }
        .sheet { page-break-after: always; padding: 8px 0; }
        .sheet:last-child { page-break-after: auto; }
        .head { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #111; padding-bottom: 8px; margin-bottom: 12px; }
        .t { font-size: 20px; font-weight: 700; } .s { font-size: 13px; color: #444; margin-top: 2px; }
        .meta { text-align: right; font-size: 12px; line-height: 1.6; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #999; padding: 4px 6px; vertical-align: top; }
        th { background: #eee; font-weight: 600; text-align: left; font-size: 11px; }
        td.c, th.c { text-align: center; } td.r, th.r { text-align: right; font-variant-numeric: tabular-nums; }
        td.b { font-weight: 700; } td.n { font-size: 10.5px; color: #333; }
        tr.miss td { color: #888; background: #fafafa; }
        td.neg { color: #b91c1c; } td.pos { color: #047857; }
        tfoot td { background: #f3f3f3; font-weight: 600; }
        .legend { font-size: 10px; color: #555; margin-top: 8px; line-height: 1.5; }
        .sig { display: flex; gap: 40px; margin-top: 40px; font-size: 11px; color: #333; }
        .sig > div { flex: 1; } .line { border-top: 1px solid #111; margin-bottom: 4px; height: 28px; }
        .foot { font-size: 9.5px; color: #777; margin-top: 18px; border-top: 1px solid #ddd; padding-top: 6px; }
        .toolbar { position: fixed; bottom: 12px; right: 12px; } .toolbar button { font: inherit; padding: 8px 14px; border-radius: 8px; border: 1px solid #7c3aed; background: #7c3aed; color: #fff; cursor: pointer; }
        @media print { .toolbar { display: none; } }
      </style></head><body>
      <div class="toolbar"><button onclick="window.print()">Drucken / als PDF sichern</button></div>
      ${list.map(i => _tsSheetHtml(i.md, i.name)).join("")}
      <script>setTimeout(function(){ try { window.print(); } catch (e) {} }, 300);<\/script>
      </body></html>`;
    w.document.open(); w.document.write(html); w.document.close();
  };
  const saveTracking = async (next) => {
    if (salonId == null) return;
    setTtSaving(true); setError("");
    try {
      const r = await api.request("/salons/" + salonId, { method: "PUT", body: JSON.stringify({ time_tracking_enabled: !!next }) });
      if (r && r.ok) setTtEnabled(!!next);
      else setError((r && r.data && r.data.error) || "Speichern fehlgeschlagen");
    } catch (e) {
      setError("Fehler beim Speichern: " + (e.message || "Unbekannt"));
    } finally { setTtSaving(false); }
  };

  // Daten laden + 30s Auto-Refresh
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const ovRes = isOwnerOrAdmin ? await api.getTimeOverview() : null;
        const stRes = myEmployeeId ? await api.getTimeStatus(myEmployeeId) : null;
        if (cancelled) return;
        if (ovRes && ovRes.ok) setOverview(ovRes.data);
        if (stRes && stRes.ok) setMyStatus(stRes.data);
        setLoading(false);
      } catch (e) {
        if (!cancelled) { setError("Laden fehlgeschlagen: " + (e.message || "Unbekannter Fehler")); setLoading(false); }
      }
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [tick]);

  // Sekunden-Ticker für „läuft seit XX:XX" Anzeige
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 60000); // 1 min für Status-Refresh
    return () => clearInterval(t);
  }, []);

  // TIME_OVERTIME_v1 -- Monatsdaten pro MA laden wenn Owner/Admin
  useEffect(() => {
    if (!isOwnerOrAdmin) return;
    let cancelled = false;
    const loadMonth = async () => {
      setMonthLoading(true);
      const collected = {};
      const empList = (overview && overview.employees) || [];
      for (const e of empList) {
        try {
          const r = await api.getTimeMonth(e.employee_id, monthYM);
          if (r && r.ok) collected[e.employee_id] = r.data;
        } catch {}
        if (cancelled) return;
      }
      if (!cancelled) { setMonthData(collected); setMonthLoading(false); }
    };
    loadMonth();
    return () => { cancelled = true; };
  }, [monthYM, isOwnerOrAdmin, overview && overview.employees && overview.employees.length]);

  const handlePunch = async (entryType, employeeId, requireNote) => {
    setPunching(true);
    setError("");
    try {
      let notes = "";
      if (requireNote) {
        notes = window.prompt("Begründung für Stempel bei einem anderen Mitarbeiter:");
        if (!notes || !notes.trim()) {
          setPunching(false);
          return;
        }
      }
      const payload = { entry_type: entryType };
      if (employeeId) payload.employee_id = employeeId;
      if (notes) payload.notes = notes;
      const res = await api.timePunch(payload);
      if (!res.ok) {
        const errMsg = (res.data && res.data.error) || "Stempeln fehlgeschlagen";
        setError(errMsg);
        setPunching(false);
        return;
      }
      // Neu laden
      setTick(t => t + 1);
    } catch (e) {
      setError("Fehler: " + (e.message || "Unbekannt"));
    } finally {
      setPunching(false);
    }
  };

  const formatMin = (min) => {
    if (!min || min <= 0) return "0:00";
    const h = Math.floor(min / 60);
    const m = min % 60;
    return h + ":" + String(m).padStart(2, "0");
  };

  const statusBadge = (status) => {
    const conf = {
      out:   { label: "ausgestempelt", color: "#6B7280", bg: "rgba(107,114,128,0.12)" },
      in:    { label: "arbeitet", color: "#10B981", bg: "rgba(16,185,129,0.15)" },
      break: { label: "in Pause", color: "#F59E0B", bg: "rgba(245,158,11,0.15)" },
    }[status] || { label: status, color: "#6B7280", bg: "rgba(107,114,128,0.12)" };
    return (
      <span style={{
        display: "inline-block", padding: "3px 10px", borderRadius: 12,
        background: conf.bg, color: conf.color, fontSize: 12, fontWeight: 600
      }}>{conf.label}</span>
    );
  };

  const renderMyPunchBox = () => {
    if (!myStatus) return null;
    const s = myStatus.status;
    const buttons = [];
    if (s === "out") {
      buttons.push({ type: "in", label: "👋 Komme", color: "#10B981" });
    } else if (s === "in") {
      buttons.push({ type: "break_start", label: "☕ Pause Start", color: "#F59E0B" });
      buttons.push({ type: "out", label: "🏁 Gehe", color: "#EF4444" });
    } else if (s === "break") {
      buttons.push({ type: "break_end", label: "▶️ Pause Ende", color: "#10B981" });
      buttons.push({ type: "out", label: "🏁 Gehe", color: "#EF4444" });
    }
    return (
      <Card style={{ marginBottom: 20, padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 13, color: COLORS.textLight, marginBottom: 6 }}>Mein Status</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.text, marginBottom: 6 }}>
              {myStatus.employee_name || "Mitarbeiter"}
            </div>
            <div style={{ marginBottom: 8 }}>{statusBadge(s)}</div>
            <div style={{ fontSize: 13, color: COLORS.textLight }}>
              Heute gearbeitet: <strong style={{ color: COLORS.text, fontSize: 15 }}>{formatMin(myStatus.worked_minutes_today)} h</strong>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 200 }}>
            {buttons.map(b => (
              <button key={b.type}
                onClick={() => handlePunch(b.type, null, false)}
                disabled={punching}
                style={{
                  padding: "14px 20px", borderRadius: 10, border: "none", cursor: punching ? "wait" : "pointer",
                  background: b.color, color: "white", fontSize: 16, fontWeight: 700,
                  opacity: punching ? 0.6 : 1, boxShadow: "0 2px 8px " + b.color + "40",
                }}>{b.label}</button>
            ))}
          </div>
        </div>
      </Card>
    );
  };

  const renderOverviewTable = () => {
    if (!overview || !overview.employees) return null;
    return (
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid " + COLORS.border, fontSize: 14, fontWeight: 700, color: COLORS.text }}>
          Heute — alle Mitarbeiter
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--cardHover)" }}>
                <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 12, fontWeight: 600, color: COLORS.textLight, borderBottom: "1px solid " + COLORS.border }}>Mitarbeiter</th>
                <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 12, fontWeight: 600, color: COLORS.textLight, borderBottom: "1px solid " + COLORS.border }}>Status</th>
                <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 12, fontWeight: 600, color: COLORS.textLight, borderBottom: "1px solid " + COLORS.border }}>Heute gearbeitet</th>
                <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 12, fontWeight: 600, color: COLORS.textLight, borderBottom: "1px solid " + COLORS.border }}>Letzter Stempel</th>
                {isOwnerOrAdmin && <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 12, fontWeight: 600, color: COLORS.textLight, borderBottom: "1px solid " + COLORS.border }}>Aktion</th>}
              </tr>
            </thead>
            <tbody>
              {overview.employees.map(e => {
                const last = e.last_in_at || e.last_out_at;
                const lastTime = last ? new Date(last).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : "—";
                let actionButtons = null;
                /* TIME_CORRECTION_v1 -- Korrektur-Modal öffnen für diesen MA */
                const openCorrection = async () => {
                  const r = await api.getTimeStatus(e.employee_id);
                  if (r && r.ok) {
                    setCorrectionFor({ employee: r.data, events: r.data.events_today || [] });
                  }
                };
                if (isOwnerOrAdmin) {
                  const isMe = e.employee_id === myEmployeeId;
                  if (e.status === "out") {
                    actionButtons = (
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        <button onClick={openCorrection} title="Stempel korrigieren" style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid " + COLORS.border, background: "transparent", color: COLORS.textLight, fontSize: 12, cursor: "pointer" }}>✏️</button>
                        <button onClick={() => handlePunch("in", e.employee_id, !isMe)} disabled={punching}
                          style={{ padding: "6px 12px", borderRadius: 8, border: "none", background: "#10B981", color: "white", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                          Einstempeln
                        </button>
                      </div>
                    );
                  } else {
                    actionButtons = (
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        <button onClick={openCorrection} title="Stempel korrigieren" style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid " + COLORS.border, background: "transparent", color: COLORS.textLight, fontSize: 12, cursor: "pointer" }}>✏️</button>
                        <button onClick={() => handlePunch("out", e.employee_id, !isMe)} disabled={punching}
                          style={{ padding: "6px 12px", borderRadius: 8, border: "none", background: "#EF4444", color: "white", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                          Ausstempeln
                        </button>
                      </div>
                    );
                  }
                }
                return (
                  <tr key={e.employee_id} style={{ borderBottom: "1px solid " + COLORS.border }}>
                    <td style={{ padding: "12px 14px", fontSize: 14, color: COLORS.text, fontWeight: 500 }}>
                      <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: e.color || "#7C3AED", marginRight: 8, verticalAlign: "middle" }} />
                      {e.employee_name}
                    </td>
                    <td style={{ padding: "12px 14px" }}>{statusBadge(e.status)}</td>
                    <td style={{ padding: "12px 14px", fontSize: 14, color: COLORS.text, fontVariantNumeric: "tabular-nums" }}>{formatMin(e.worked_minutes)} h</td>
                    <td style={{ padding: "12px 14px", fontSize: 13, color: COLORS.textLight, fontVariantNumeric: "tabular-nums" }}>{lastTime}</td>
                    {isOwnerOrAdmin && <td style={{ padding: "12px 14px", textAlign: "right" }}>{actionButtons}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    );
  };

  return (
    <div>
      <Header title="Zeiterfassung" subtitle="Komme/Gehe-Stempeluhr · Pflicht für Angestellte (BAG 13.09.2022 + MiLoG)" />

      {error && (
        <div style={{ padding: 12, background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8, color: "#EF4444", fontSize: 13, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <span>⚠️ {error}</span>
          <button onClick={() => setError("")} style={{ background: "none", border: "none", cursor: "pointer", color: "#EF4444", fontSize: 18, padding: 0, lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* TIME_TRACKING_TOGGLE_v1 -- An/Aus direkt im Fenster (nur Owner/Admin schaltet) */}
      {isOwnerOrAdmin && (
        <Card style={{ marginBottom: 16 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14 }}>
            <input type="checkbox" checked={trackingActive}
              disabled={ttEnabled === null || ttSaving}
              onChange={e => saveTracking(e.target.checked)} />
            <strong>Zeiterfassung für diesen Salon aktiviert</strong>
            {ttSaving && <span style={{ fontSize: 12, color: COLORS.textLight }}>speichert…</span>}
          </label>
          <p style={{ fontSize: 12, color: COLORS.textLight, margin: "8px 0 0 26px", lineHeight: 1.6 }}>
            Wenn deaktiviert, wird die Stempeluhr für alle ausgeblendet — viele Salons erfassen die Arbeitszeit über ihr Kassensystem.
          </p>
        </Card>
      )}

      {/* Info-Text wenn Zeiterfassung deaktiviert */}
      {!loading && !trackingActive && (
        <Card>
          <div style={{ padding: 24, textAlign: "center", color: COLORS.textLight, fontSize: 13.5, lineHeight: 1.7 }}>
            <div style={{ fontSize: 30, marginBottom: 8 }}>🕔</div>
            <div style={{ fontWeight: 700, color: COLORS.text, fontSize: 15, marginBottom: 6 }}>Zeiterfassung ist deaktiviert</div>
            {isOwnerOrAdmin
              ? "Dieser Salon erfasst die Arbeitszeiten aktuell nicht über Shearly — viele Salons nutzen dafür ihr Kassensystem. Du kannst die Stempeluhr oben jederzeit wieder aktivieren."
              : "Dieser Salon erfasst die Arbeitszeiten nicht über Shearly. Bei Fragen wende dich an die Salonleitung."}
          </div>
        </Card>
      )}

      {loading && <div style={{ padding: 30, textAlign: "center", color: COLORS.textLight, fontSize: 13 }}>Lade...</div>}

      {!loading && trackingActive && myEmployeeId && renderMyPunchBox()}

      {!loading && trackingActive && isOwnerOrAdmin && renderOverviewTable()}

      {/* TIME_OVERTIME_v1 -- Monatsuebersicht Soll/Ist + Ueberstunden pro MA */}
      {!loading && trackingActive && isOwnerOrAdmin && (
        <Card style={{ marginTop: 20, padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "14px 20px", borderBottom: "1px solid " + COLORS.border, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>
              Monatsübersicht — Soll / Ist / Über-/Minus-Stunden
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input type="month" value={monthYM} onChange={e => setMonthYM(e.target.value)}
                style={{ padding: "6px 10px", border: "1px solid " + COLORS.border, borderRadius: 6, fontSize: 13, background: "var(--card)", color: COLORS.text }} />
              {/* TIMESHEET_PRINT_v1 -- alle Stundenzettel des Monats in einem Druckauftrag (eine Seite je MA) */}
              <Btn size="sm" variant="secondary" disabled={monthLoading || !Object.keys(monthData).length}
                onClick={() => printTimesheets(((overview && overview.employees) || []).map(e => ({ md: monthData[e.employee_id], name: e.employee_name })))}>
                <Download size={12} /> Alle Stundenzettel drucken
              </Btn>
            </div>
          </div>
          {monthLoading && <div style={{ padding: 20, textAlign: "center", color: COLORS.textLight, fontSize: 13 }}>Lade...</div>}
          {!monthLoading && (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--cardHover)" }}>
                    <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 12, fontWeight: 600, color: COLORS.textLight }}>Mitarbeiter</th>
                    <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 12, fontWeight: 600, color: COLORS.textLight }}>Soll</th>
                    <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 12, fontWeight: 600, color: COLORS.textLight }}>Ist</th>
                    <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 12, fontWeight: 600, color: COLORS.textLight }}>Differenz</th>
                    <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 12, fontWeight: 600, color: COLORS.textLight }}>Status</th>
                    <th style={{ padding: "10px 14px", textAlign: "right", fontSize: 12, fontWeight: 600, color: COLORS.textLight }}>Stundenzettel</th>
                  </tr>
                </thead>
                <tbody>
                  {((overview && overview.employees) || []).map(e => {
                    const md = monthData[e.employee_id];
                    if (!md) return (
                      <tr key={e.employee_id} style={{ borderBottom: "1px solid " + COLORS.border }}>
                        <td style={{ padding: "12px 14px", fontSize: 14, fontWeight: 500 }}>{e.employee_name}</td>
                        <td colSpan={5} style={{ padding: "12px 14px", fontSize: 12, color: COLORS.textLight }}>—</td>
                      </tr>
                    );
                    const diff = md.diff_hours;
                    const diffColor = diff > 0.5 ? "#10B981" : diff < -0.5 ? "#EF4444" : COLORS.textLight;
                    const statusLabel = diff > 0.5 ? `🟢 +${diff.toFixed(1)}h Überstunden` : diff < -0.5 ? `🔴 ${diff.toFixed(1)}h Minus-Stunden` : "⚪ ausgeglichen";
                    return (
                      <tr key={e.employee_id} style={{ borderBottom: "1px solid " + COLORS.border }}>
                        <td style={{ padding: "12px 14px", fontSize: 14, fontWeight: 500 }}>
                          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: e.color || "#7C3AED", marginRight: 6, verticalAlign: "middle" }} />
                          {e.employee_name}
                        </td>
                        <td style={{ padding: "12px 14px", fontSize: 14, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{md.total_soll_hours.toFixed(1)} h</td>
                        <td style={{ padding: "12px 14px", fontSize: 14, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{md.total_hours.toFixed(1)} h</td>
                        <td style={{ padding: "12px 14px", fontSize: 14, textAlign: "right", fontVariantNumeric: "tabular-nums", color: diffColor, fontWeight: 700 }}>
                          {diff > 0 ? "+" : ""}{diff.toFixed(1)} h
                        </td>
                        <td style={{ padding: "12px 14px", fontSize: 12, color: diffColor }}>{statusLabel}</td>
                        <td style={{ padding: "8px 14px", textAlign: "right" }}>
                          {/* TIMESHEET_PRINT_v1 -- Druck je Mitarbeiter/in */}
                          <Btn size="sm" variant="ghost" onClick={() => printTimesheets([{ md, name: e.employee_name }])} title="Stundenzettel drucken oder als PDF sichern">
                            <Download size={12} /> Drucken
                          </Btn>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ padding: "8px 16px", borderTop: "1px solid " + COLORS.border, fontSize: 11, color: COLORS.textLight }}>
            <strong>Soll</strong> = aus Schichtplan (Wochenrhythmus). <strong>Ist</strong> = gestempelte Arbeitszeit. <strong>Differenz</strong> = Über-/Minus-Stunden. Bei fehlenden Stempel-Tagen wird Soll-Zeit voll als Minus gezählt.
          </div>
        </Card>
      )}

      {!loading && trackingActive && !myEmployeeId && !isOwnerOrAdmin && (
        <Card>
          <div style={{ padding: 20, textAlign: "center", color: COLORS.textLight }}>
            Kein Mitarbeiter-Konto verknüpft. Bitte Admin kontaktieren.
          </div>
        </Card>
      )}

      {trackingActive && (
        <div style={{ marginTop: 24, padding: 14, background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.25)", borderRadius: 8, fontSize: 12.5, lineHeight: 1.6, color: COLORS.text }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>ℹ️ Rechtlicher Hinweis</div>
          Seit BAG-Urteil 13.09.2022 ist die Erfassung der täglichen Arbeitszeit für alle Angestellten gesetzlich verpflichtend (§ 3 ArbSchG). Für Friseurhandwerk zusätzlich MiLoG § 17. Daten werden 2 Jahre aufbewahrt. Korrekturen werden audit-fest protokolliert.
        </div>
      )}

      {/* TIME_CORRECTION_v1 -- Modal */}
      {correctionFor && (
        <TimeCorrectionModal
          employee={correctionFor.employee}
          events={correctionFor.events}
          onClose={() => setCorrectionFor(null)}
          onSaved={() => { setCorrectionFor(null); setTick(t => t + 1); }}
        />
      )}
    </div>
  );
};